import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { UI_MESSAGES } from "@/constants/ui";
import { useForm, useUI } from "@/shared/state/store";
import { useAlert } from "@/app/providers/AlertProvider";
import Screen from "@/engine/renderer/Screen";
import HelpDialog from "@/components/HelpDialog";
import {
  BudgetService,
  OnboardingService,
  ProfileService,
  DependentsService,
  DebtsService,
  DebtCompaniesService,
} from "@/shared/api/services";
import { normalizeBudgetFrequency, toMonthlyBudgetAmount } from "@/shared/lib/budgetFrequency";
import {
  BudgetFormRenderer,
  OnboardingDialog,
  MobileLayout,
  DesktopLayout,
  // Helpers
  normalizeString,
  isAffirmative,
  isPlainObject,
  findLenderByName,
  buildAccountTypeOptions,
  // Constants
  incomeOnboardingSchemas,
  partnerEmploymentSchema,
  PROFILE_DIALOG_STEP_MAP,
  PROFILE_DIALOG_TITLES,
  PROFILE_DIALOG_SUCCESS_MESSAGES,
  INCOME_ONBOARDING_STEP_OFFSET,
  MANUAL_COMPANY_OPTION_VALUE,
  MANUAL_COMPANY_LABEL,
  HIDDEN_COMPANY_NAMES,
  BLOCKED_REFERENCE_COMPANY_KEYS,
  innerRoutesConfig,
  outerRoutes,
  type ProfileDialogKey,
} from "./UpdateBudgetPageCom";
import type { DependentDobRecord } from "@/shared/api/services/dependentsService";
import type {
  AccountTypeRecord,
  LenderParentRecord,
  LenderApiResponse,
} from "@/shared/api/services/lenderService";
import type {
  BroadbandSupplierRecord,
  EnergyCompanyRecord,
  MobileSupplierRecord,
  LandlineSupplierRecord,
} from "@/shared/api/types";
import {
  BUDGET_FIELD_MAPPINGS,
  checkFieldTrigger,
  EMPTY_DEBTS_SCHEMA_STATE,
  getGuidanceNotesForField,
  mapDebtApiToSchema,
  mapExpenseDataToSchema,
  mapIncomeDataToSchema,
  mapSchemaDataToExpense,
  mapSchemaDataToExpenseNotes,
  mapSchemaDataToIncome,
  mapSchemaDataToIncomeNotes,
  mapArrearsDebtsToExpenseSchema,
} from "@/shared/lib/budgetFieldMapping";
import DebtCategory from "@/components/DebtCategory";
import {
  Checkbox,
  FormControlLabel,
  Stack,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import QuickEditDialog from "@/components/warnings/QuickEditDialog";
import { evaluateWarnings } from "@/shared/warnings/warningEngine";
import type {
  ResolvedWarning,
  VariableSpec,
  WarningToken,
  OpenActionSpec,
} from "@/shared/warnings/types";
import promptTemplatesJson from "@/shared/warnings/promptTemplates.json";
import variableRegistryJson from "@/shared/warnings/variableRegistry.json";
import fieldWarningRulesAggregated from "@/shared/warnings/fieldRules";
import { TriggersProvider } from "@/shared/warnings/TriggersContext";
import ProfileFormDialog from "@/components/profile/ProfileFormDialog";
import type {
  ButtonActionLogRequest,
  CustomerDebtRecord,
  DebtFormEntry,
  ExpenseDataRecord,
  ExpensesNoteData,
  ExpenseUpdateRequest,
  IncomeDataRecord,
  IncomeNoteData,
  IncomeNoteUpdateRequest,
  IncomeUpdateRequest,
  DebtsSchemaState,
} from "@/shared/api/types";
import { calculateAge } from "@/shared/lib/utils/others";
import { httpGet } from "@/shared/api/http";
import { customerEndpoints } from "@/shared/api/endpoints";
import { femaCapture } from "@/shared/fema/capture";
import type { ApiResponse, CalculatedTotals } from "@/shared/api/types";
import { WarningsRefreshContext } from "@/shared/warnings/WarningsRefreshContext";
import { CustomerMetadataContext } from "@/shared/warnings/CustomerMetadataContext";
import { UU_SHOW_HIDE_INFO } from "@/shared/uuShowHide";
import { Paper } from "@ui";
import {
  computeConfirmBeforeClearPlan,
  type BudgetSnapshots,
  type ProfileBudgetContext,
} from "@/shared/budget/confirmBeforeClear";
import {
  detectCleanupNeeded,
  deleteCleanupRecords,
  buildCleanupConfirmMessage,
} from "@/shared/budget/uuEvidenceCleanup";
import { openConfirmDialog } from "@/components/ConfirmDialog";
import { evaluateVisibilityRule } from "@/engine/runtime/visibility/dsl";
import { useNorthernIrelandDetection } from "@/shared/hooks/useNorthernIrelandDetection";
import { applyNISchemaTransformations } from "@/shared/lib/niSchemaTransformer";

// Import Schema type from Screen component
type ScreenSchema = Parameters<typeof Screen>[0]["schema"];

interface ProfileDialogState {
  key: ProfileDialogKey;
  stepIndex: number;
  schema: ScreenSchema;
  title: string;
  initialData: Record<string, unknown>;
}

// Create sets for fast lookup
const hiddenCompanyNameSet = new Set(
  HIDDEN_COMPANY_NAMES.map((name) => normalizeString(name))
);
const blockedReferenceCompanySet = new Set(
  BLOCKED_REFERENCE_COMPANY_KEYS.map((name) => normalizeString(name))
);

// Main Budget Screen Component
const UpdateBudgetPageInner: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Menu state
  const [innerNavMenuAnchor, setInnerNavMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [isBudgetDataLoading, setIsBudgetDataLoading] = useState<boolean>(true);
  const [isNavigating, setIsNavigating] = useState<boolean>(false);
  const [budgetFormRehydrateNonce, setBudgetFormRehydrateNonce] =
    useState<number>(0);

  // Onboarding Dialog State
  const onboardingPending = searchParams.get("onboardingPending") === "true";
  const [isOnboardingDialogOpen, setIsOnboardingDialogOpen] =
    useState<boolean>(onboardingPending);
  const [currentOnboardingStep, setCurrentOnboardingStep] = useState<number>(0);
  const [formInstanceKeys, setFormInstanceKeys] = useState<
    Record<number, number>
  >({});

  // Field stage persistence refs (similar to newOnBoardingScreen.tsx)
  const fieldStageInitializedRef = useRef<boolean>(false);
  const fieldStageRecordIdRef = useRef<number | null>(null);
  const fieldStageCustomerIdRef = useRef<number | null>(null);
  const lastPersistedStageRef = useRef<string | null>(null);

  // Track if step was in original URL (not added by us)
  const originalStepParamRef = useRef<string | null>(null);
  const hasInitializedStepRef = useRef<boolean>(false);

  // Help Dialog State
  const [isHelpDialogOpen, setIsHelpDialogOpen] = useState<boolean>(false);

  // Northern Ireland detection for label customizations
  const {
    isNorthernIreland,
    loading: niLoading,
  } = useNorthernIrelandDetection();

  // Form state management for onboarding
  const { setFormData, formData: allFormData } = useForm();
  const { setLoading, loading } = useUI();
  const { showAlert } = useAlert();

  // When the Update Budget page is (re)opened, always start from fresh API data
  // instead of any stale form cache that may have been captured before the
  // user edited values on the Budget Breakdown page.
  //
  // This fixes the bug where:
  // - User opens UpdateBudgetPage (forms are cached into Zustand)
  // - Navigates to Budget Breakdown and changes values (saving to API)
  // - Comes back to UpdateBudgetPage and still sees the old values until a full reload.
  //
  // By clearing both the HTTP caches and the per‑section form store on mount,
  // the initial form values are always re‑hydrated from the latest API state.
  useEffect(() => {
    try {
      BudgetService.clearIncomeDataCache();
      BudgetService.clearExpenseDataCache();
    } catch {
      // Best-effort cache clear; ignore failures
    }

    try {
      Object.entries(innerRoutesConfig).forEach(([outerKey, innerRoutes]) => {
        innerRoutes.forEach((route) => {
          const formId = `${outerKey}-${route.key}-form`;
          setFormData(formId, {});
        });
      });
    } catch {
      // Best-effort reset; if anything goes wrong we fall back to existing data
    }
  }, [setFormData]);

  // Budget data state
  const [incomeData, setIncomeData] = useState<IncomeDataRecord[]>([]);
  const [expenseData, setExpenseData] = useState<ExpenseDataRecord[]>([]);
  const [incomeNotes, setIncomeNotes] = useState<IncomeNoteData[]>([]);
  const [expenseNotes, setExpenseNotes] = useState<ExpensesNoteData[]>([]);
  const [guidanceNotes, setGuidanceNotes] = useState<any[]>([]);
  const [incomeTriggers, setIncomeTriggers] = useState<any[]>([]);
  const [debtData, setDebtData] = useState<CustomerDebtRecord[]>([]);
  const [debtAccountTypes, setDebtAccountTypes] = useState<AccountTypeRecord[]>(
    []
  );
  const [debtsLoading, setDebtsLoading] = useState<boolean>(true);
  const [accountTypesLoading, setAccountTypesLoading] = useState<boolean>(true);

  /**
   * Latest API baselines (refs) used to avoid stale-closure issues on fast flows:
   * QuickEdit (redwarning) does PUT+GET and then a user can navigate immediately.
   * Navigation triggers auto-save which calls the previous render's save callback.
   * If that callback compares against stale state (e.g. basicIncome=1000), it will
   * incorrectly fire a redundant PUT even though API is already updated to 0.
   */
  const latestIncomeRecordRef = useRef<IncomeDataRecord | null>(null);
  const latestExpenseRecordRef = useRef<ExpenseDataRecord | null>(null);
  const latestIncomeNoteRef = useRef<IncomeNoteData | null>(null);
  const latestExpenseNoteRef = useRef<ExpensesNoteData | null>(null);

  useEffect(() => {
    latestIncomeRecordRef.current = incomeData?.[0] ?? null;
  }, [incomeData]);
  useEffect(() => {
    latestExpenseRecordRef.current = expenseData?.[0] ?? null;
  }, [expenseData]);
  useEffect(() => {
    latestIncomeNoteRef.current = incomeNotes?.[0] ?? null;
  }, [incomeNotes]);
  useEffect(() => {
    latestExpenseNoteRef.current = expenseNotes?.[0] ?? null;
  }, [expenseNotes]);

  // Lender data state for dynamic options
  const [lenders, setLenders] = useState<LenderParentRecord[]>([]);
  const [lendersLoaded, setLendersLoaded] = useState(false);

  // Energy companies data state for dynamic options
  const [energyCompanies, setEnergyCompanies] = useState<EnergyCompanyRecord[]>(
    []
  );
  const [energyCompaniesLoaded, setEnergyCompaniesLoaded] = useState(false);

  // Broadband suppliers data state for dynamic options
  const [broadbandSuppliers, setBroadbandSuppliers] = useState<
    BroadbandSupplierRecord[]
  >([]);
  const [broadbandSuppliersLoaded, setBroadbandSuppliersLoaded] =
    useState(false);

  // Mobile suppliers data state for dynamic options
  const [mobileSuppliers, setMobileSuppliers] = useState<
    MobileSupplierRecord[]
  >([]);
  const [mobileSuppliersLoaded, setMobileSuppliersLoaded] = useState(false);

  // Landline suppliers data state for dynamic options
  const [landlineSuppliers, setLandlineSuppliers] = useState<
    LandlineSupplierRecord[]
  >([]);
  const [landlineSuppliersLoaded, setLandlineSuppliersLoaded] = useState(false);

  // Dependents DOB data state
  const [dependentsDobData, setDependentsDobData] = useState<
    DependentDobRecord[]
  >([]);

  // Calculated totals for dynamic savings help text
  const [calculatedTotals, setCalculatedTotals] =
    useState<CalculatedTotals | null>(null);
  const hasFetchedTotalsRef = useRef<boolean>(false);

  // Customer metadata for visibility conditions
  const [customerMetadata, setCustomerMetadata] = useState<{
    jointAccount?: string;
    customerEmploymentStatus?: string;
    partnerEmploymentStatus?: string;
    haveChildrenUnder18?: string;
    accommodationType?: string;
    customerAge?: number;
    partnerAge?: number;
    dependentChildrenUnder16?: number;
    dependentChildrenAged16Plus?: number;
  }>({});

  // Confirmation dialog: used when a profile change will hide valued budget fields
  const CONFIRM_REMOVE_SECTION_MESSAGE =
    "Updating your profile status will change your recorded budget. You should review your monthly summary before sharing your budget. Do you wish to continue?";
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmPendingAction, setConfirmPendingAction] = useState<
    null | (() => Promise<void>)
  >(null);
  const [confirmMessage, setConfirmMessage] = useState<string>(
    CONFIRM_REMOVE_SECTION_MESSAGE
  );

  // Helper: store an async callback in state without it being treated as a state-updater
  const setPendingConfirmAction = useCallback((action: () => Promise<void>): void => {
    setConfirmPendingAction(() => action);
  }, []);

  // Clear hidden budget fields based on a confirm-before-clear plan.
  // Shared by onboarding flow + profile warning quick-edit dialogs.
  const clearBudgetForPlan = useCallback(
    async (
      plan: ReturnType<typeof computeConfirmBeforeClearPlan>
    ): Promise<void> => {
      try {
        if (plan.clearPayloads.incomeData) {
          await BudgetService.updateIncomeData(plan.clearPayloads.incomeData);
        }
        if (plan.clearPayloads.incomeNotes) {
          await BudgetService.updateIncomeNotes(plan.clearPayloads.incomeNotes);
        }
        if (plan.clearPayloads.expenseData) {
          await BudgetService.updateExpenseData(plan.clearPayloads.expenseData);
        }
        if (plan.clearPayloads.expenseNotes) {
          await BudgetService.updateExpenseNotes(plan.clearPayloads.expenseNotes);
        }
      } catch {
        // Non-blocking: if clearing fails, proceed with profile update
      }
    },
    []
  );

  // Section-level warnings and quick-edit dialog state
  const [sectionWarnings, setSectionWarnings] = useState<ResolvedWarning[]>([]);
  const [isVarDialogOpen, setIsVarDialogOpen] = useState(false);
  const [varSpec, setVarSpec] = useState<VariableSpec | null>(null);
  const [profileDialogState, setProfileDialogState] =
    useState<ProfileDialogState | null>(null);
  const [isProfileDialogSaving, setIsProfileDialogSaving] = useState(false);
  const [warningsRefreshKey, setWarningsRefreshKey] = useState(0);

  const debtsAllAddedFlag = useMemo(() => {
    const expenseRecord = expenseData?.[0] as
      | Partial<ExpenseDataRecord>
      | undefined;
    const rawFlag =
      expenseRecord?.field4TxtFld ??
      (expenseRecord as Record<string, unknown> | undefined)?.field4Txtfld;
    if (typeof rawFlag === "string") {
      return rawFlag.trim().toUpperCase() === "Y";
    }
    if (typeof rawFlag === "boolean") {
      return rawFlag;
    }
    return false;
  }, [expenseData]);

  const mappedDebts = useMemo<DebtsSchemaState>(() => {
    if (!Array.isArray(debtData) || debtData.length === 0) {
      return { ...EMPTY_DEBTS_SCHEMA_STATE, allDebtsAdded: debtsAllAddedFlag };
    }
    // Build helper maps to improve category resolution when API omits accTypeCode
    const idToCode: Record<number, string> = {};
    const labelToCode: Record<string, string> = {};
    for (const rec of debtAccountTypes) {
      if (typeof rec?.id === "number" && rec.code) {
        idToCode[rec.id] = rec.code;
      }
      if (rec?.label && rec.code) {
        labelToCode[rec.label.trim().toLowerCase()] = rec.code;
      }
    }

    return mapDebtApiToSchema(debtData, {
      includeRaw: true,
      allDebtsAdded: debtsAllAddedFlag,
      accountTypeIdToCodeMap: idToCode,
      accountTypeLabelToCodeMap: labelToCode,
    });
  }, [debtData, debtsAllAddedFlag]);

  // Live form values for the current onboarding step
  const liveStepValuesRef = useRef<Record<string, unknown>>({});

  // Live form values for the current budget category form
  const liveBudgetFormValuesRef = useRef<Record<string, unknown>>({});

  const lastDebtsSelectionRef = useRef<{ outer: string; inner: string } | null>(
    null
  );

  // Ref to hold the latest saveBudgetData callback (avoids stale closure issues
  // and allows navigation callbacks to be defined before saveBudgetData)
  const saveBudgetDataRef = useRef<
    ((opts?: { source?: "auto" | "manual" }) => Promise<void>) | null
  >(null);

  // Helper functions for reading stored customer/user data
  const readStoredCustomerData = useCallback((): Record<string, any> | null => {
    if (typeof window === "undefined") return null;
    try {
      const storedCustomerRaw = localStorage.getItem("customerData");
      return storedCustomerRaw ? JSON.parse(storedCustomerRaw) : null;
    } catch {
      return null;
    }
  }, []);

  const resolveCustomerId = useCallback(() => {
    let resolvedCustomerId: string | number | null = null;
    const qpCustomerId = searchParams.get("customerId");
    if (qpCustomerId) resolvedCustomerId = qpCustomerId;

    if (!resolvedCustomerId) {
      const storedCustomer = readStoredCustomerData();
      const localCustomerId = storedCustomer?.customerId || storedCustomer?.id;
      if (localCustomerId) resolvedCustomerId = localCustomerId;
    }

    return resolvedCustomerId;
  }, [searchParams, readStoredCustomerData]);

  const resolveUserId = useCallback(() => {
    const storedUserRaw =
      typeof window !== "undefined" ? localStorage.getItem("userData") : null;
    const storedUser = storedUserRaw ? JSON.parse(storedUserRaw) : null;
    const userId = storedUser?.userId || null;
    return userId;
  }, []);

  const refreshDebtsData = useCallback(
    async (options?: { withSpinner?: boolean }) => {
      const customerId = resolveCustomerId();
      if (!customerId) {
        return;
      }

      const shouldShowSpinner = options?.withSpinner ?? true;

      if (shouldShowSpinner) {
        setDebtsLoading(true);
      }

      try {
        const response = await DebtsService.getDebts(customerId);
        if (response.success && Array.isArray(response.data)) {
          setDebtData(response.data);
        }
      } catch (error) {
        console.error("Failed to refresh debts data", error);
      } finally {
        if (shouldShowSpinner) {
          setDebtsLoading(false);
        }
      }
    },
    [resolveCustomerId, setDebtData, setDebtsLoading]
  );

  const fetchCalculatedTotals = useCallback(
    async (opts?: { customerId?: string | number; withSpinner?: boolean }) => {
      const resolvedId = opts?.customerId ?? resolveCustomerId();
      if (!resolvedId) {
        return;
      }

      const withSpinner = opts?.withSpinner ?? false;

      try {
        if (withSpinner) {
          setLoading("calculated-totals-load", true);
        }

        const response = await httpGet<ApiResponse>(
          customerEndpoints.getCalculatedTotals(Number(resolvedId))
        );
        // API may return either { data: [...] } or [...]
        const raw =
          response.data?.data ?? (response.data as unknown as unknown[]);
        const summary = Array.isArray(raw) ? raw[0] : raw;
        if (summary) {
          setCalculatedTotals(summary as unknown as CalculatedTotals);
        }
      } catch (error) {
        femaCapture("fetching_calculated_totals_data_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
      } finally {
        if (withSpinner) {
          setLoading("calculated-totals-load", false);
        }
      }
    },
    [resolveCustomerId, setLoading]
  );

  // After confirm-before-clear executes, refresh local state so:
  // - hidden fields are actually removed from the live records
  // - calculated totals (balanceAmount / "what's left") reflect the backend updates immediately
  const refreshBudgetStateAfterClear = useCallback(
    async (
      customerId: number
    ): Promise<{
      income: IncomeDataRecord | null;
      incomeNotes: IncomeNoteData | null;
      expenses: ExpenseDataRecord | null;
      expenseNotes: ExpensesNoteData | null;
    } | null> => {
      if (!Number.isFinite(customerId)) return null;

      try {
        BudgetService.clearIncomeDataCache();
        BudgetService.clearExpenseDataCache();
      } catch {
        // ignore
      }

      let out: {
        income: IncomeDataRecord | null;
        incomeNotes: IncomeNoteData | null;
        expenses: ExpenseDataRecord | null;
        expenseNotes: ExpensesNoteData | null;
      } = { income: null, incomeNotes: null, expenses: null, expenseNotes: null };

      try {
        const [incomeResp, incomeNotesResp, expenseResp, expenseNotesResp] =
          await Promise.all([
            BudgetService.getIncomeData(customerId, { skipCache: true }),
            BudgetService.getIncomeNotes(customerId, { skipCache: true }),
            BudgetService.getExpenseData(customerId, { skipCache: true }),
            BudgetService.getExpenseNotes(customerId, { skipCache: true }),
          ]);

        if (incomeResp?.success && Array.isArray(incomeResp.data)) {
          setIncomeData(incomeResp.data);
          out.income = incomeResp.data[0] ?? null;
        }
        if (incomeNotesResp?.success && Array.isArray(incomeNotesResp.data)) {
          setIncomeNotes(incomeNotesResp.data);
          out.incomeNotes = incomeNotesResp.data[0] ?? null;
        }
        if (expenseResp?.success && Array.isArray(expenseResp.data)) {
          setExpenseData(expenseResp.data);
          out.expenses = expenseResp.data[0] ?? null;
        }
        if (expenseNotesResp?.success && Array.isArray(expenseNotesResp.data)) {
          setExpenseNotes(expenseNotesResp.data);
          out.expenseNotes = expenseNotesResp.data[0] ?? null;
        }
      } catch {
        // non-blocking: totals refresh below still helps UI consistency
      }

      await fetchCalculatedTotals({ customerId, withSpinner: false });
      return out;
    },
    [fetchCalculatedTotals]
  );

  // Fetch calculated totals once to populate dynamic values in Savings help text
  useEffect(() => {
    if (hasFetchedTotalsRef.current) return;
    hasFetchedTotalsRef.current = true;
    void fetchCalculatedTotals({ withSpinner: true });
  }, [fetchCalculatedTotals]);

  // Helper to extract only schema-defined field names (excludes metadata fields)
  const getSchemaFieldNames = useCallback((schema: any): Set<string> => {
    if (!schema || !schema.sections) return new Set();

    const fieldNames = new Set<string>();
    const visitField = (field: any, prefix?: string) => {
      if (!field || typeof field !== "object") return;
      const name = typeof field.fieldName === "string" ? field.fieldName : "";
      if (!name) return;

      const path = prefix ? `${prefix}.${name}` : name;

      // Only include fields that should be added to payload
      if (field.addToPayload !== false) {
        fieldNames.add(path);
      }

      // Recurse into children (supports nested question-groups like:
      // hobbiesLeisureSports.gamblingFromHobbiesLeisure.amount)
      if (Array.isArray(field.children)) {
        field.children.forEach((child: any) => visitField(child, path));
      }
    };

    schema.sections.forEach((section: any) => {
      if (!Array.isArray(section?.fields)) return;
      section.fields.forEach((field: any) => visitField(field));
    });

    return fieldNames;
  }, []);

  const [selectedOuter, setSelectedOuter] = useState(outerRoutes[0]?.key || "");
  const [selectedInner, setSelectedInner] = useState(() => {
    const routes =
      innerRoutesConfig[selectedOuter as keyof typeof innerRoutesConfig];
    return routes && routes.length > 0 ? routes[0]?.key || "" : "";
  });

  const rehydrateLocalBudgetFormsAfterClear = useCallback(
    (
      plan: ReturnType<typeof computeConfirmBeforeClearPlan>,
      snaps:
        | {
            income: IncomeDataRecord | null;
            incomeNotes: IncomeNoteData | null;
            expenses: ExpenseDataRecord | null;
            expenseNotes: ExpensesNoteData | null;
          }
        | null
    ): void => {
      if (!snaps) return;

      const toClear = Array.isArray((plan as any)?.fields)
        ? ((plan as any).fields as Array<{
            id: string;
            domain: "income" | "expense";
            beforeVisible: boolean;
            afterVisible: boolean;
            hasData: boolean;
          }>).filter((f) => f.beforeVisible && !f.afterVisible && f.hasData)
        : [];

      const incomePrefixes = new Set(
        toClear
          .filter((f) => f.domain === "income")
          .map((f) => String(f.id || "").split(".")[0])
          .filter(Boolean)
      );

      const expenseIds = new Set(
        toClear
          .filter((f) => f.domain === "expense")
          .map((f) => String(f.id || ""))
          .filter(Boolean)
      );

      const formsRehydrated = new Set<string>();

      if (snaps.income) {
        const notes = (snaps.incomeNotes ?? {}) as any;

        if (incomePrefixes.has("benefits")) {
          setFormData(
            "income-benefits-form",
            mapIncomeDataToSchema(snaps.income as any, notes, "benefits") as any
          );
          formsRehydrated.add("income-benefits-form");
        }

        if (incomePrefixes.has("salary")) {
          setFormData(
            "income-salary-form",
            mapIncomeDataToSchema(snaps.income as any, notes, "salary") as any
          );
          formsRehydrated.add("income-salary-form");
        }

        if (incomePrefixes.has("pension")) {
          setFormData(
            "income-pension-form",
            mapIncomeDataToSchema(snaps.income as any, notes, "pension") as any
          );
          formsRehydrated.add("income-pension-form");
        }

        if (incomePrefixes.has("otherSources")) {
          setFormData(
            "income-other-sources-form",
            mapIncomeDataToSchema(snaps.income as any, notes, "otherSources") as any
          );
          formsRehydrated.add("income-other-sources-form");
        }
      }

      if (snaps.expenses) {
        const notes = (snaps.expenseNotes ?? {}) as any;

        const needsSchool =
          expenseIds.has("expenses.schoolUniform") ||
          expenseIds.has("expenses.clubsAndSchoolTrips") ||
          expenseIds.has("expenses.otherSchoolCosts");
        if (needsSchool) {
          setFormData(
            "fixed-expenses-school-form",
            mapExpenseDataToSchema(snaps.expenses as any, notes, "school") as any
          );
          formsRehydrated.add("fixed-expenses-school-form");
        }

        const needsPocketMoney = expenseIds.has("expenses.pocketMoney");
        if (needsPocketMoney) {
          setFormData(
            "flexible-expenses-leisure-form",
            mapExpenseDataToSchema(snaps.expenses as any, notes, "leisure") as any
          );
          formsRehydrated.add("flexible-expenses-leisure-form");
        }
      }

      const activeFormId = `${selectedOuter}-${selectedInner}-form`;
      if (formsRehydrated.has(activeFormId)) {
        setBudgetFormRehydrateNonce((prev) => prev + 1);
      }
    },
    [selectedOuter, selectedInner, setFormData]
  );

  // Get current inner routes based on outer selection
  const currentInnerRoutes = useMemo(() => {
    let routes =
      innerRoutesConfig[selectedOuter as keyof typeof innerRoutesConfig] || [];

    // Filter out pension tab if customer age is less than 40 years
    //if (selectedOuter === 'income' && customerMetadata.customerAge !== undefined && customerMetadata.customerAge < 40) {
    //  routes = routes.filter(route => route.key !== 'pension') as typeof routes;
    //}

    return routes;
  }, [innerRoutesConfig, selectedOuter, customerMetadata.customerAge]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const styleId = "iehub-warning-highlight-style";
    if (document.getElementById(styleId)) return;

    const styleEl = document.createElement("style");
    styleEl.id = styleId;
    styleEl.textContent = `
      .warning-highlight {
        outline: 3px solid #155F54;
        outline-offset: 2px;
        box-shadow: 0 0 0 3px rgba(21, 95, 84, 0.18);
        transition: box-shadow 0.18s ease-in-out, outline 0.18s ease-in-out;
      }
    `;
    document.head.appendChild(styleEl);
  }, []);

  const filteredLenders = useMemo(() => {
    if (!Array.isArray(lenders)) {
      return [] as LenderParentRecord[];
    }
    return lenders.filter((record) => {
      const parentKey = normalizeString(record?.parentCompany);
      if (parentKey && hiddenCompanyNameSet.has(parentKey)) {
        return false;
      }
      const rawParentKey = normalizeString(
        (record.raw as { parentCompany?: string } | undefined)?.parentCompany
      );
      if (rawParentKey && hiddenCompanyNameSet.has(rawParentKey)) {
        return false;
      }
      const descriptionKey = normalizeString(record?.description);
      if (descriptionKey && hiddenCompanyNameSet.has(descriptionKey)) {
        return false;
      }
      const lenderKey = normalizeString((record as { lender?: string }).lender);
      if (lenderKey && hiddenCompanyNameSet.has(lenderKey)) {
        return false;
      }
      const childCompany = Array.isArray(record.childCompany)
        ? record.childCompany
        : [];
      for (const child of childCompany) {
        if (hiddenCompanyNameSet.has(normalizeString(child))) {
          return false;
        }
      }
      return true;
    });
  }, [lenders]);

  // Build lenders map for quick lookup
  const lendersMap = useMemo(() => {
    const map: Record<string, string[]> = {};
    filteredLenders.forEach((lender) => {
      const key = lender.parentCompany?.trim();
      if (!key) {
        return;
      }
      map[key] = Array.isArray(lender.childCompany) ? lender.childCompany : [];
    });
    return map;
  }, [filteredLenders]);

  const lenderRecordLookup = useMemo(() => {
    const map = new Map<string, LenderParentRecord>();
    filteredLenders.forEach((record) => {
      const parentKey = normalizeString(record?.parentCompany);
      if (parentKey) {
        map.set(parentKey, record);
      }
      const descriptionKey = normalizeString(record?.description);
      if (descriptionKey) {
        map.set(descriptionKey, record);
      }
      const parentRaw = record.raw as
        | { parentCompany?: string; lender?: string }
        | undefined;
      const parentRootKey = normalizeString(parentRaw?.parentCompany);
      if (parentRootKey) {
        map.set(parentRootKey, record);
      }
      const lenderKey = normalizeString((record as { lender?: string }).lender);
      if (lenderKey) {
        map.set(lenderKey, record);
      }
      const childCompanies = Array.isArray(record.childCompany)
        ? record.childCompany
        : [];
      childCompanies.forEach((child) => {
        const childKey = normalizeString(child);
        if (childKey) {
          map.set(childKey, record);
        }
      });
      const childrenRaw = Array.isArray(record.childrenRaw)
        ? record.childrenRaw
        : [];
      childrenRaw.forEach((child) => {
        const childKey = normalizeString(
          child?.lender ?? child?.description ?? ""
        );
        if (childKey) {
          map.set(childKey, record);
        }
      });
    });
    return map;
  }, [filteredLenders]);

  const accountTypesByCompany = useMemo(() => {
    const map = new Map<string, AccountTypeRecord[]>();
    debtAccountTypes.forEach((record) => {
      const key = normalizeString(record?.companyName);
      if (!key) {
        return;
      }
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key)!.push(record);
    });
    return map;
  }, [debtAccountTypes]);

  const arrearsCompanyOptions = useMemo(() => {
    const unique = new Map<string, any>();
    filteredLenders.forEach((record) => {
      const parentLabel = record.parentCompany?.trim();
      if (!parentLabel) {
        return;
      }
      // Hide specific companies from selectable options
      const normalizedParent = normalizeString(parentLabel);
      if (hiddenCompanyNameSet.has(normalizedParent)) {
        return;
      }

      const childSet = new Set(
        (record.childCompany ?? [])
          .map((child: any) => child.trim())
          .filter(Boolean)
      );
      const refFormat =
        record.referenceNumberFormat ?? record.raw?.referenceNumberFormat;
      const normalizedCompany = normalizeString(parentLabel);

      // Get account types for this company
      const companyAccountTypes =
        accountTypesByCompany.get(normalizedCompany) ?? [];
      const accountTypes = companyAccountTypes.map((type: any) => ({
        code: type.accountType,
        label: type.accountTypeLabel || type.accountType,
      }));

      unique.set(parentLabel, {
        value: parentLabel,
        label: parentLabel,
        hasChildCompanies: childSet.size > 0,
        ...(refFormat !== undefined && { referenceNumberFormat: refFormat }),
        ...(accountTypes.length > 0 && { accountTypes }),
      });
    });

    const sorted = Array.from(unique.values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
    return [
      { value: MANUAL_COMPANY_OPTION_VALUE, label: MANUAL_COMPANY_LABEL },
      ...sorted,
    ];
  }, [filteredLenders, accountTypesByCompany]);

  const allAccountTypeOptions = useMemo(
    () => buildAccountTypeOptions(debtAccountTypes),
    [debtAccountTypes]
  );

  // Get current schema based on selection with dynamic lender options and energy companies
  const currentSchema = useMemo(() => {
    const innerRoute = currentInnerRoutes.find(
      (route) => route.key === selectedInner
    );
    const baseSchema = innerRoute?.schema || null;

    if (!baseSchema) {
      return baseSchema;
    }

    let schemaClone: any = null;
    const ensureSchemaClone = () => {
      if (!schemaClone) {
        schemaClone = JSON.parse(JSON.stringify(baseSchema));
      }
      return schemaClone;
    };

    // Keep School tab visible but show a message (no questions) when there are no children under 18
    if (
      selectedOuter === "fixed-expenses" &&
      selectedInner === "school" &&
      customerMetadata.haveChildrenUnder18 === "N"
    ) {
      const schema = ensureSchemaClone();
      if (Array.isArray(schema.sections)) {
        schema.sections = schema.sections.map((section: any) => ({
          ...section,
          sectionHelperText:
            "Based on the personal circumstances that you have provided, there are no questions in this section for you to answer. Please click on the continue button to proceed.",
          sectionHelperTextHtml: true,
          fields: [],
        }));
      }
      return schemaClone ?? baseSchema;
    }

    // Keep Pension tab (income) visible but show a message (no questions) when BOTH
    // customer AND partner (if exists) are below pensionable age AND neither is retired.
    // Pensionable age threshold — matches income/3-pension.json (Yes/No when age > 40).
    if (
      selectedOuter === "income" &&
      selectedInner === "pension"
    ) {
      const customerAge = customerMetadata.customerAge;
      const partnerAge = customerMetadata.partnerAge;
      const customerEmploymentStatus = customerMetadata.customerEmploymentStatus;
      const partnerEmploymentStatus = customerMetadata.partnerEmploymentStatus;
      const hasPartner = customerMetadata.jointAccount === "Y";

      const customerQualifies =
        (typeof customerAge === "number" && customerAge > 45) ||
        customerEmploymentStatus === "Retired";

      const partnerQualifies =
        hasPartner &&
        ((typeof partnerAge === "number" && partnerAge > 45) ||
          partnerEmploymentStatus === "Retired");

      // Show "no questions" message ONLY if neither customer nor partner qualifies
      const shouldHidePensionQuestions = !customerQualifies && !partnerQualifies;

      if (shouldHidePensionQuestions) {
        const schema = ensureSchemaClone();
        if (Array.isArray(schema.sections)) {
          schema.sections = schema.sections.map((section: any) => ({
            ...section,
            sectionHelperText:
              "Based on the personal circumstances that you have provided (you are below pensionable age), there are no questions in this section for you to answer. Please click on the continue button to proceed.",
            sectionHelperTextHtml: true,
            fields: [],
          }));
        }
        return schemaClone ?? baseSchema;
      }
    }

    // Keep Salary tab visible but show a message (no questions) when **all** salary fields
    // (basic_income, debtor2_basic_income, other_salary_wages_1) are hidden according
    // to their individual hide rules.
    //
    // Field‑level hide rules (from legacy / JSON):
    // - basic_income           → hide WHEN employment_status = 'Unemployed'
    // - debtor2_basic_income   → hide WHEN joint_account = 'N' OR partner_employment_status = 'Unemployed'
    // - other_salary_wages_1   → hide WHEN
    //      customer_employment_status IN
    //        ('Unemployed','Retired','Carer','Student','Other','Not working due to illness/disability')
    //      AND (joint_account = 'N' OR partner_employment_status in same list)
    //
    // If you combine those conditions, **all three fields are hidden** exactly when:
    //   customerEmploymentStatus === 'Unemployed'
    //   AND (jointAccount === 'N' OR partnerEmploymentStatus === 'Unemployed')
    if (
      selectedOuter === "income" &&
      selectedInner === "salary"
    ) {
      const customerStatus = 
        customerMetadata.customerEmploymentStatus ?? 
        readStoredCustomerData()?.customerEmploymentStatus;
      
      const partnerStatus = 
        customerMetadata.partnerEmploymentStatus ?? 
        readStoredCustomerData()?.partnerEmploymentStatus;
      
      const jointAccount = 
        customerMetadata.jointAccount ?? 
        readStoredCustomerData()?.jointAccount;

      // Fix: Only check for "Unemployed" status, not all non-working statuses.
      // The schema's visibleWhen rule for partnerTakeHomeSalary hides it only when
      // partnerEmploymentStatus === "Unemployed", so partner salary should still
      // be visible when partner is "Student", "Retired", "Carer", or "Other".
      const isPartnerUnemployed = partnerStatus === "Unemployed";
      
      const hasNoPartner = jointAccount === "N" || jointAccount === "n";
      
      // All salary fields hidden (basic income, partner basic income, other salary)
      // when customer is Unemployed AND (no partner OR partner is Unemployed)
      const allFieldsHidden = 
        typeof customerStatus === "string" &&
        customerStatus === "Unemployed" &&
        (hasNoPartner || isPartnerUnemployed);
      
      if (allFieldsHidden) {
        const schema = ensureSchemaClone();
        if (Array.isArray(schema.sections)) {
          schema.sections = schema.sections.map((section: any) => ({
            ...section,
            sectionHelperText:
              "Based on the personal information that you have provided, there are no questions in this section for you to answer. Please click on the Continue button to proceed.",
            sectionHelperTextHtml: true,
            fields: [],
          }));
        }
        return schemaClone ?? baseSchema;
      }
    }

    const applyDebtEnhancementsForField = (
      schema: any,
      listFieldName: string
    ) => {
      if (!schema?.sections?.length || !lendersLoaded || lenders.length === 0) {
        return;
      }

      const formId = `${selectedOuter}-${selectedInner}-form`;
      const currentFormData = allFormData[formId] as
        | Record<string, any>
        | undefined;
      const tempFieldPrefix = `__listform_temp_${listFieldName}__`;

      const selectedCompanyFromTemp = (() => {
        const directKey = currentFormData?.[`${tempFieldPrefix}.companyName`];
        if (typeof directKey === "string" && directKey.trim().length > 0) {
          return directKey;
        }
        const nestedKey = currentFormData?.[tempFieldPrefix]?.companyName;
        if (typeof nestedKey === "string" && nestedKey.trim().length > 0) {
          return nestedKey;
        }
        return undefined;
      })();

      schema.sections.forEach((section: any) => {
        if (!Array.isArray(section?.fields)) {
          return;
        }

        section.fields.forEach((field: any) => {
          if (!Array.isArray(field?.children)) {
            return;
          }

          field.children = field.children.map((child: any) => {
            if (
              child?.fieldName !== listFieldName ||
              child?.dataType !== "list-form" ||
              !Array.isArray(child?.children)
            ) {
              return child;
            }

            const existingEntries =
              currentFormData?.[field.fieldName]?.[listFieldName];
            let fallbackCompany: string | undefined;
            if (
              !selectedCompanyFromTemp &&
              Array.isArray(existingEntries) &&
              existingEntries.length > 0
            ) {
              const firstEntry = existingEntries[0];
              const candidate =
                firstEntry?.companyName ??
                firstEntry?.company ??
                firstEntry?.creditor;
              if (
                typeof candidate === "string" &&
                candidate.trim().length > 0
              ) {
                fallbackCompany = candidate;
              }
            }

            const selectedEntry =
              Array.isArray(existingEntries) && existingEntries.length > 0
                ? existingEntries[0]
                : undefined;
            const storedCompanyValue =
              typeof selectedEntry?.companyName === "string"
                ? selectedEntry.companyName.trim()
                : "";
            const manualCompanyName =
              typeof selectedEntry?.companyNameManual === "string"
                ? selectedEntry.companyNameManual.trim()
                : "";
            const productValueRaw =
              typeof selectedEntry?.productName === "string"
                ? selectedEntry.productName
                : typeof selectedEntry?.product === "string"
                  ? selectedEntry.product
                  : "";
            const productValue = productValueRaw ? productValueRaw.trim() : "";
            const accountTypeValue =
              typeof selectedEntry?.accountType === "string"
                ? selectedEntry.accountType.trim()
                : "";

            const companySelectionRaw =
              selectedCompanyFromTemp ??
              storedCompanyValue ??
              fallbackCompany ??
              "";
            const companySelection =
              typeof companySelectionRaw === "string"
                ? companySelectionRaw.trim()
                : "";
            const manualSelection =
              companySelection === MANUAL_COMPANY_OPTION_VALUE;
            const lookupCompanyName = manualSelection
              ? fallbackCompany
              : companySelection;
            const lenderRecord = manualSelection
              ? undefined
              : findLenderByName(
                  lenderRecordLookup,
                  filteredLenders,
                  lookupCompanyName
                );

            const resolvedCompanyLabel = manualSelection
              ? manualCompanyName
              : lenderRecord?.parentCompany?.trim() || lookupCompanyName || "";
            const normalizedResolvedCompany =
              normalizeString(resolvedCompanyLabel);

            const childCompanies = Array.isArray(lenderRecord?.childCompany)
              ? lenderRecord.childCompany
              : [];
            const childRecords = Array.isArray(lenderRecord?.childrenRaw)
              ? (lenderRecord.childrenRaw as LenderApiResponse[])
              : [];
            const normalizedProductValue = normalizeString(productValue);
            const selectedChildRaw = childRecords.find(
              (child) =>
                normalizeString(child?.lender ?? child?.description ?? "") ===
                normalizedProductValue
            );
            const parentRaw = lenderRecord?.raw as
              | Record<string, unknown>
              | undefined;

            const parentReferenceFormat =
              lenderRecord?.referenceNumberFormat ??
              (typeof parentRaw?.referenceNumberFormat === "string"
                ? parentRaw.referenceNumberFormat
                : undefined);
            const parentAddToDebtList = lenderRecord
              ? isAffirmative(lenderRecord.addToDebtList) ||
                isAffirmative(parentRaw?.addToDebtList)
              : false;
            const parentShowCompanyList = lenderRecord
              ? isAffirmative(lenderRecord.showCompanyList) ||
                isAffirmative(parentRaw?.showCompanyList)
              : false;
            const childReferenceFormat =
              selectedChildRaw?.referenceNumberFormat;
            const childAddToDebtList = selectedChildRaw
              ? isAffirmative(selectedChildRaw.addToDebtList)
              : undefined;
            const childShowCompanyList = selectedChildRaw
              ? isAffirmative(selectedChildRaw.showCompanyList)
              : undefined;

            const effectiveAddToDebtList =
              childAddToDebtList ?? parentAddToDebtList;
            const effectiveShowCompanyList =
              childShowCompanyList ?? parentShowCompanyList;

            const uniqueProductOptions = Array.from(
              new Set(
                childCompanies
                  .map((child) =>
                    typeof child === "string" ? child.trim() : ""
                  )
                  .filter(Boolean)
              )
            );
            let productOptions = uniqueProductOptions.map((child) => ({
              value: child,
              label: child,
            }));
            if (
              productValue &&
              !productOptions.some(
                (option) =>
                  normalizeString(option.value) === normalizedProductValue
              )
            ) {
              productOptions = [
                ...productOptions,
                { value: productValue, label: productValue },
              ];
            }
            const productVisible =
              !manualSelection &&
              productOptions.length > 0 &&
              effectiveShowCompanyList;
            const productRequired = productVisible;
            const productReady = !productVisible || productValue.length > 0;

            let accountTypeRecordsForCompany: AccountTypeRecord[];
            if (manualSelection) {
              accountTypeRecordsForCompany = debtAccountTypes;
            } else if (normalizedResolvedCompany) {
              accountTypeRecordsForCompany =
                accountTypesByCompany.get(normalizedResolvedCompany) ?? [];
            } else {
              accountTypeRecordsForCompany = [];
            }
            let accountTypeOptionsForField = buildAccountTypeOptions(
              accountTypeRecordsForCompany
            );
            if (
              !accountTypeOptionsForField.length &&
              !manualSelection &&
              effectiveAddToDebtList
            ) {
              accountTypeOptionsForField = allAccountTypeOptions;
            }
            const accountTypeVisible =
              (manualSelection || (effectiveAddToDebtList && productReady)) &&
              accountTypeOptionsForField.length > 0;
            const accountTypeRequired = accountTypeVisible;

            const candidateBlockedKeys = new Set<string>([
              normalizeString(companySelection),
              normalizedResolvedCompany,
              normalizeString(manualCompanyName),
              normalizedProductValue,
              normalizeString(lenderRecord?.description),
              normalizeString(parentRaw?.parentCompany as string | undefined),
              normalizeString(parentRaw?.lender as string | undefined),
            ]);
            const isBlockedCompany = Array.from(candidateBlockedKeys).some(
              (key) => key && blockedReferenceCompanySet.has(key)
            );

            const showReference = manualSelection
              ? Boolean(manualCompanyName)
              : productReady &&
                effectiveAddToDebtList &&
                effectiveShowCompanyList &&
                !isBlockedCompany;
            const requiredWhenReference = showReference
              ? { op: "ne", args: [{ var: "noReferenceNumber" }, true] }
              : null;

            child.children = child.children.map((listChild: any) => {
              if (listChild.fieldName === "companyName") {
                const allowManualCompany =
                  listChild?.ui?.allowManualCompany !== false;
                let companyOptions = arrearsCompanyOptions.filter(
                  (option) =>
                    allowManualCompany ||
                    option.value !== MANUAL_COMPANY_OPTION_VALUE
                );
                if (
                  allowManualCompany &&
                  !companyOptions.some(
                    (option) => option.value === MANUAL_COMPANY_OPTION_VALUE
                  )
                ) {
                  companyOptions = [
                    {
                      value: MANUAL_COMPANY_OPTION_VALUE,
                      label: MANUAL_COMPANY_LABEL,
                    },
                    ...companyOptions,
                  ];
                }
                if (companySelection && !manualSelection) {
                  const normalizedSelection = normalizeString(companySelection);
                  const exists = companyOptions.some(
                    (option) =>
                      normalizeString(option.value) === normalizedSelection
                  );
                  if (!exists) {
                    const withoutSelection = companyOptions.filter(
                      (option) =>
                        normalizeString(option.value) !== normalizedSelection
                    );
                    if (allowManualCompany) {
                      const manualOption = {
                        value: MANUAL_COMPANY_OPTION_VALUE,
                        label: MANUAL_COMPANY_LABEL,
                      };
                      const rest = withoutSelection.filter(
                        (option) => option.value !== MANUAL_COMPANY_OPTION_VALUE
                      );
                      companyOptions = [
                        manualOption,
                        { value: companySelection, label: companySelection },
                        ...rest,
                      ];
                    } else {
                      companyOptions = [
                        { value: companySelection, label: companySelection },
                        ...withoutSelection,
                      ];
                    }
                  }
                }

                const existingEvents = listChild.events ?? {};
                const existingOnChange = Array.isArray(existingEvents.onChange)
                  ? existingEvents.onChange
                  : [];
                const resetOnCompanyChange = {
                  when: { op: "ne", args: [{ var: "$value" }, ""] },
                  actions: [
                    {
                      type: "setFieldValue",
                      name: "companyNameManual",
                      value: "",
                    },
                    { type: "setFieldValue", name: "productName", value: "" },
                    { type: "setFieldValue", name: "accountType", value: "" },
                    {
                      type: "setFieldValue",
                      name: "referenceNumber",
                      value: "",
                    },
                    {
                      type: "setFieldValue",
                      name: "noReferenceNumber",
                      value: false,
                    },
                  ],
                };
                const hasCompanyReset = existingOnChange.some(
                  (
                    event:
                      | { actions?: Array<{ name?: unknown }> }
                      | null
                      | undefined
                  ) =>
                    Array.isArray(event?.actions) &&
                    event.actions.some(
                      (action: { name?: unknown }) =>
                        action?.name === "accountType"
                    )
                );
                const mergedEvents = {
                  ...existingEvents,
                  onChange: hasCompanyReset
                    ? existingOnChange
                    : [...existingOnChange, resetOnCompanyChange],
                };

                // Update to use select dataType with onboarding-style UI
                return {
                  ...listChild,
                  dataType: "select",
                  options: companyOptions,
                  events: mergedEvents,
                  ui: {
                    ...(listChild.ui ?? {}),
                    placeholder: "Select a Company",
                    helperTop:
                      "Select your creditor or choose 'Can't find my creditor' if not listed",
                    showErrorBelow: false,
                    dialogLabel: "Select a Creditor",
                    searchable: true,
                  },
                };
              }

              if (listChild.fieldName === "companyNameManual") {
                return {
                  ...listChild,
                  dataType: "text",
                  isRequired: manualSelection,
                  visibleWhen: {
                    op: "eq",
                    args: [{ var: "companyName" }, MANUAL_COMPANY_OPTION_VALUE],
                  },
                  requiredWhen: {
                    op: "eq",
                    args: [{ var: "companyName" }, MANUAL_COMPANY_OPTION_VALUE],
                  },
                  maxLength: 250,
                  validationRules: [
                    {
                      type: "minLength",
                      value: 2,
                      message: "Company name must be at least 2 characters",
                    },
                    {
                      type: "maxLength",
                      value: 250,
                      message: "Company name must be less than 250 characters",
                    },
                  ],
                  ui: {
                    ...(listChild.ui ?? {}),
                    placeholder: "Enter the company name",
                    helperTop:
                      "Please enter the name of the company that you owe money to",
                    showErrorBelow: true,
                  },
                };
              }

              if (listChild.fieldName === "productName") {
                const existingEvents = listChild.events ?? {};
                const existingOnChange = Array.isArray(existingEvents.onChange)
                  ? existingEvents.onChange
                  : [];
                const resetOnProductChange = {
                  when: { op: "ne", args: [{ var: "$value" }, ""] },
                  actions: [
                    { type: "setFieldValue", name: "accountType", value: "" },
                    {
                      type: "setFieldValue",
                      name: "referenceNumber",
                      value: "",
                    },
                    {
                      type: "setFieldValue",
                      name: "noReferenceNumber",
                      value: false,
                    },
                  ],
                };
                const hasProductReset = existingOnChange.some(
                  (
                    event:
                      | { actions?: Array<{ name?: unknown }> }
                      | null
                      | undefined
                  ) =>
                    Array.isArray(event?.actions) &&
                    event.actions.some(
                      (action: { name?: unknown }) =>
                        action?.name === "accountType"
                    )
                );
                const mergedEvents = {
                  ...existingEvents,
                  onChange: hasProductReset
                    ? existingOnChange
                    : [...existingOnChange, resetOnProductChange],
                };

                return {
                  ...listChild,
                  dataType: "select",
                  options: productOptions,
                  isHidden: !productVisible,
                  isRequired: productRequired,
                  events: mergedEvents,
                  ui: {
                    ...(listChild.ui ?? {}),
                    placeholder: "Select a product",
                    showErrorBelow: true,
                    dialogLabel: `Select a Product`,
                  },
                };
              }

              if (listChild.fieldName === "accountType") {
                return {
                  ...listChild,
                  dataType: "select",
                  options: accountTypeOptionsForField,
                  isHidden: !accountTypeVisible,
                  isRequired: accountTypeRequired,
                  isEditable: accountTypeVisible,
                  isReadOnly: !accountTypeVisible,
                  defaultValue:
                    accountTypeValue ||
                    (accountTypeOptionsForField.length === 1
                      ? (accountTypeOptionsForField[0]?.value ?? "")
                      : ""),
                  ui: {
                    ...(listChild.ui ?? {}),
                    placeholder: "select an account type",
                    showErrorBelow: true,
                    searchable: false,
                    dialogLabel: "Select an Account Type",
                  },
                };
              }

              if (listChild.fieldName === "referenceNumber") {
                const referencePlaceholder =
                  childReferenceFormat ??
                  parentReferenceFormat ??
                  listChild?.ui?.placeholder ??
                  "Enter reference (if applicable)";
                const validationRules =
                  Array.isArray(listChild.validationRules) &&
                  listChild.validationRules.length > 0
                    ? listChild.validationRules
                    : [
                        {
                          type: "pattern",
                          value: "^[^']*$",
                          message: UI_MESSAGES.APOSTROPHE_NOT_ALLOWED,
                        },
                        {
                          type: "maxLength",
                          value: 50,
                          message: UI_MESSAGES.CHARACTER_LIMIT_REACHED,
                        },
                      ];

                // Update to use special-text dataType with onboarding-style UI
                return {
                  ...listChild,
                  dataType: "special-text",
                  isHidden: !showReference,
                  // Mirror onboarding: always required with conditional requiredWhen
                  isRequired: true,
                  requiredWhen: requiredWhenReference,
                  validationRules,
                  ui: {
                    ...(listChild.ui ?? {}),
                    placeholder: referencePlaceholder,
                    helperTop: null,
                    showErrorBelow: true,
                    allowNoValue: true,
                    noValueFieldName: "noReferenceNumber",
                  referenceHelp: {
                    companyFields: [
                      "productName",
                      "companyName",
                      "company",
                      "creditor",
                      "freepostName",
                      "companyNameManual",
                      "manualCompanyName",
                      "companyOtherName",
                    ],
                    backendFields: ["lenderName", "backendName", "creditor", "company"],
                    fallbackLinkLabel: "How to find my Reference ID",
                  },
                  },
                };
              }

              return listChild;
            });

            return child;
          });
        });
      });
    };

    const applyArrearsEnhancements = (schema: any) =>
      applyDebtEnhancementsForField(schema, "arrears");
    const applyCreditCardDebtEnhancements = (schema: any) =>
      applyDebtEnhancementsForField(schema, "creditCards");
    const applyLoanDebtEnhancements = (schema: any) =>
      applyDebtEnhancementsForField(schema, "loans");
    const applyOverdraftDebtEnhancements = (schema: any) =>
      applyDebtEnhancementsForField(schema, "overdrafts");
    const applyOtherDebtEnhancements = (schema: any) =>
      applyDebtEnhancementsForField(schema, "otherDebts");

    if (
      selectedOuter === "fixed-expenses" &&
      selectedInner === "utilities" &&
      energyCompaniesLoaded
    ) {
      const schema = ensureSchemaClone();
      const section = schema.sections?.[0];
      if (section) {
        const formId = `${selectedOuter}-${selectedInner}-form`;
        const currentFormData =
          (allFormData[formId] as Record<string, any>) || {};
        const gasData = (currentFormData.gas as Record<string, any>) || {};
        const electricityData =
          (currentFormData.electricity as Record<string, any>) || {};
        const supplierAmountWarning =
          "You have selected the company but missed to enter the amount.";
        const shouldShowSupplierAmountWarning = (
          amountValue: unknown,
          supplierValue: unknown
        ) => {
          const supplierChosen =
            typeof supplierValue === "string" &&
            supplierValue.trim().length > 0;
          const amountMissing = (() => {
            if (amountValue === null || amountValue === undefined) return true;
            const normalized =
              typeof amountValue === "number"
                ? amountValue.toString()
                : `${amountValue}`;
            return normalized.trim().length === 0;
          })();
          return supplierChosen && amountMissing;
        };

        const gasField = section.fields?.find(
          (f: any) => f.fieldName === "gas"
        );
        if (gasField?.children) {
          gasField.children = gasField.children.map((childField: any) => {
            if (childField.fieldName === "gasSupplier") {
              // Build supplier options with a top manual option that opens the "Enter Supplier" field
              // Keep the underlying value as "Other" to match schema visibleWhen rules
              const manualTopOptionMain = {
                value: "Other",
                label: "I can't find my company",
              };
              // Map API energy companies and de-duplicate by label
              const seen = new Set<string>();
              const mapped = energyCompanies
                .map((company) => ({
                  value: company.companyName,
                  label: company.companyName,
                }))
                // Remove any API-provided "Other" entry to avoid duplicate with our top manual option
                .filter((opt) => {
                  const val = String(opt.value || "").trim().toLowerCase();
                  const lbl = String(opt.label || "").trim().toLowerCase();
                  return val !== "other" && lbl !== "other";
                })
                .filter((opt) => {
                  const key = (opt.label || "").trim().toLowerCase();
                  if (!key || seen.has(key)) return false;
                  seen.add(key);
                  return true;
                })
                .sort((a, b) => a.label.localeCompare(b.label));
              const companyOptions = [manualTopOptionMain, ...mapped];

              // When the user switches away from "I can't find my company" / "Other"
              // back to a concrete supplier, clear the manual supplier text field so
              // that `gasSupplierOther` becomes empty and its visibleWhen rule hides
              // the "Enter Supplier Name" input.
              const existingEvents = childField.events ?? {};
              const existingOnChange = Array.isArray(existingEvents.onChange)
                ? existingEvents.onChange
                : [];
              const resetManualSupplierOnChange = {
                when: {
                  op: "and",
                  args: [
                    { op: "ne", args: [{ var: "$value" }, "Other"] },
                    {
                      op: "ne",
                      args: [
                        { var: "$value" },
                        "I can't find my company",
                      ],
                    },
                  ],
                },
                actions: [
                  {
                    type: "setFieldValue",
                    name: "gas.gasSupplierOther",
                    value: "",
                  },
                ],
              };
              const hasManualReset = existingOnChange.some(
                (
                  event:
                    | { actions?: Array<{ name?: unknown }> }
                    | null
                    | undefined
                ) =>
                  Array.isArray(event?.actions) &&
                  event.actions.some(
                    (action: { name?: unknown }) =>
                      action?.name === "gas.gasSupplierOther"
                  )
              );
              const mergedEvents = {
                ...existingEvents,
                onChange: hasManualReset
                  ? existingOnChange
                  : [...existingOnChange, resetManualSupplierOnChange],
              };
              const showWarning = shouldShowSupplierAmountWarning(
                gasData.amount,
                gasData.gasSupplier
              );
              const updatedUi: Record<string, any> = {
                ...(childField.ui ?? {}),
              };
              if (showWarning) {
                updatedUi.helperBottom = supplierAmountWarning;
                updatedUi.helperBottomTone = "error";
                updatedUi.showErrorBelow = true;
              } else if (
                updatedUi.helperBottom === supplierAmountWarning ||
                updatedUi.helperBottomTone === "error"
              ) {
                delete updatedUi.helperBottom;
                delete updatedUi.helperBottomTone;
              }

              return {
                ...childField,
                options: companyOptions,
                events: mergedEvents,
                ui: updatedUi,
              };
            }
            // Mirror the same supplier list and sensible defaults inside the Gas arrears dialog
            if (childField.fieldName === "arrears" && Array.isArray(childField.children)) {
              const formId = `${selectedOuter}-${selectedInner}-form`;
              const currentFormData =
                (allFormData[formId] as Record<string, any>) || {};
              const gasData = (currentFormData.gas as Record<string, any>) || {};
              const gasAmount = gasData.amount ?? "";
              const gasFrequency = gasData.frequency ?? "";
              const gasSupplier = gasData.gasSupplier ?? "";
              const gasSupplierOther = gasData.gasSupplierOther ?? "";
              const manualTopOptionDialog = {
                value: "__manual__",
                label: "I can't find my company",
              };
              const seen = new Set<string>();
              const energyOptions = [
                manualTopOptionDialog,
                ...energyCompanies
                  .map((company) => ({
                    value: company.companyName,
                    label: company.companyName,
                  }))
                  .filter((opt) => {
                    const key = (opt.label || "").trim().toLowerCase();
                    if (!key || seen.has(key)) return false;
                    seen.add(key);
                    return true;
                  })
                  .sort((a, b) => a.label.localeCompare(b.label)),
              ];
              childField.children = childField.children.map((lfChild: any) => {
                if (lfChild.fieldName === "companyName") {
                  // Determine default company based on main page selection
                  const companyDefault =
                    typeof gasSupplier === "string" && gasSupplier.trim().length > 0
                      ? (gasSupplier === "Other" ? "__manual__" : gasSupplier)
                      : undefined;
                  return {
                    ...lfChild,
                    dataType: "select",
                    options: energyOptions,
                    ...(companyDefault ? { defaultValue: companyDefault } : {}),
                    ui: {
                      ...(lfChild.ui ?? {}),
                      searchable: true,
                      placeholder: "Select a Company",
                      dialogLabel: "Select a Company",
                    },
                  };
                }
                if (lfChild.fieldName === "companyNameManual") {
                  const manualDefault =
                    typeof gasSupplier === "string" &&
                    gasSupplier === "Other" &&
                    typeof gasSupplierOther === "string" &&
                    gasSupplierOther.trim().length > 0
                      ? gasSupplierOther
                      : undefined;
                  return {
                    ...lfChild,
                    ...(manualDefault ? { defaultValue: manualDefault } : {}),
                  };
                }
                if (lfChild.fieldName === "amountCurrentlyOwned") {
                  return {
                    ...lfChild,
                    label: "Total outstanding balance",
                    defaultValue: gasAmount,
                  };
                }
                if (lfChild.fieldName === "frequency") {
                  return {
                    ...lfChild,
                    defaultValue: gasFrequency,
                  };
                }
                if (lfChild.fieldName === "accountType") {
                  const autoDefault =
                    Array.isArray(lfChild.options) && lfChild.options.length === 1
                      ? (lfChild.options[0] as any)?.value
                      : undefined;
                  return {
                    ...lfChild,
                    label: "Arrears Type",
                    isRequired: true,
                    ...(autoDefault ? { defaultValue: autoDefault } : {}),
                    ui: {
                      ...(lfChild.ui ?? {}),
                      dialogLabel: "Select arrears type",
                    },
                  };
                }
                return lfChild;
              });
              return childField;
            }
            return childField;
          });
        }

        const electricityField = section.fields?.find(
          (f: any) => f.fieldName === "electricity"
        );
        if (electricityField?.children) {
          electricityField.children = electricityField.children.map(
            (childField: any) => {
              if (childField.fieldName === "electricitySupplier") {
                // Build supplier options with a top manual option, consistent with Gas
                const manualTopOptionMain = {
                  value: "Other",
                  label: "I can't find my company",
                };
                const seen = new Set<string>();
                const mapped = energyCompanies
                  .map((company) => ({
                    value: company.companyName,
                    label: company.companyName,
                  }))
                  // Remove any API-provided "Other" entry to avoid duplicate with our top manual option
                  .filter((opt) => {
                    const val = String(opt.value || "").trim().toLowerCase();
                    const lbl = String(opt.label || "").trim().toLowerCase();
                    return val !== "other" && lbl !== "other";
                  })
                  .filter((opt) => {
                    const key = (opt.label || "").trim().toLowerCase();
                    if (!key || seen.has(key)) return false;
                    seen.add(key);
                    return true;
                  })
                  .sort((a, b) => a.label.localeCompare(b.label));
                const companyOptions = [manualTopOptionMain, ...mapped];

                // When the user switches away from "I can't find my company" / "Other"
                // back to a concrete supplier, clear the manual supplier text field so
                // that `electricitySupplierOther` becomes empty and its visibleWhen rule
                // hides the "Enter Supplier" input.
                const existingEvents = childField.events ?? {};
                const existingOnChange = Array.isArray(existingEvents.onChange)
                  ? existingEvents.onChange
                  : [];
                const resetManualSupplierOnChange = {
                  when: {
                    op: "and",
                    args: [
                      { op: "ne", args: [{ var: "$value" }, "Other"] },
                      {
                        op: "ne",
                        args: [
                          { var: "$value" },
                          "I can't find my company",
                        ],
                      },
                    ],
                  },
                  actions: [
                    {
                      type: "setFieldValue",
                      name: "electricity.electricitySupplierOther",
                      value: "",
                    },
                  ],
                };
                const hasManualReset = existingOnChange.some(
                  (
                    event:
                      | { actions?: Array<{ name?: unknown }> }
                      | null
                      | undefined
                  ) =>
                    Array.isArray(event?.actions) &&
                    event.actions.some(
                    (action: { name?: unknown }) =>
                      action?.name === "electricity.electricitySupplierOther"
                    )
                );
                const mergedEvents = {
                  ...existingEvents,
                  onChange: hasManualReset
                    ? existingOnChange
                    : [...existingOnChange, resetManualSupplierOnChange],
                };
                const showWarning = shouldShowSupplierAmountWarning(
                  electricityData.amount,
                  electricityData.electricitySupplier
                );
                const updatedUi: Record<string, any> = {
                  ...(childField.ui ?? {}),
                };
                if (showWarning) {
                  updatedUi.helperBottom = supplierAmountWarning;
                  updatedUi.helperBottomTone = "error";
                  updatedUi.showErrorBelow = true;
                } else if (
                  updatedUi.helperBottom === supplierAmountWarning ||
                  updatedUi.helperBottomTone === "error"
                ) {
                  delete updatedUi.helperBottom;
                  delete updatedUi.helperBottomTone;
                }

                return {
                  ...childField,
                  options: companyOptions,
                  events: mergedEvents,
                  ui: updatedUi,
                };
              }
              // Mirror same list and defaults in Electricity arrears dialog
              if (childField.fieldName === "arrears" && Array.isArray(childField.children)) {
                const formId = `${selectedOuter}-${selectedInner}-form`;
                const currentFormData =
                  (allFormData[formId] as Record<string, any>) || {};
                const elecData =
                  (currentFormData.electricity as Record<string, any>) || {};
                const elecAmount = elecData.amount ?? "";
                const elecFrequency = elecData.frequency ?? "";
                const elecSupplier = elecData.electricitySupplier ?? "";
                const elecSupplierOther = elecData.electricitySupplierOther ?? "";
                const manualTopOptionDialog = {
                  value: "__manual__",
                  label: "I can't find my company",
                };
                const seen = new Set<string>();
                const energyOptions = [
                  manualTopOptionDialog,
                  ...energyCompanies
                    .map((company) => ({
                      value: company.companyName,
                      label: company.companyName,
                    }))
                    .filter((opt) => {
                      const key = (opt.label || "").trim().toLowerCase();
                      if (!key || seen.has(key)) return false;
                      seen.add(key);
                      return true;
                    })
                    .sort((a, b) => a.label.localeCompare(b.label)),
                ];
                childField.children = childField.children.map((lfChild: any) => {
                  if (lfChild.fieldName === "companyName") {
                    const companyDefault =
                      typeof elecSupplier === "string" && elecSupplier.trim().length > 0
                        ? (elecSupplier === "Other" ? "__manual__" : elecSupplier)
                        : undefined;
                    return {
                      ...lfChild,
                      dataType: "select",
                      options: energyOptions,
                       ...(companyDefault ? { defaultValue: companyDefault } : {}),
                      ui: {
                        ...(lfChild.ui ?? {}),
                        searchable: true,
                        placeholder: "Select a Company",
                        dialogLabel: "Select a Company",
                      },
                    };
                  }
                  if (lfChild.fieldName === "companyNameManual") {
                    const manualDefault =
                      typeof elecSupplier === "string" &&
                      elecSupplier === "Other" &&
                      typeof elecSupplierOther === "string" &&
                      elecSupplierOther.trim().length > 0
                        ? elecSupplierOther
                        : undefined;
                    return {
                      ...lfChild,
                      ...(manualDefault ? { defaultValue: manualDefault } : {}),
                    };
                  }
                  if (lfChild.fieldName === "amountCurrentlyOwned") {
                    return {
                      ...lfChild,
                      label: "Total outstanding balance",
                      defaultValue: elecAmount,
                    };
                  }
                  if (lfChild.fieldName === "frequency") {
                    return {
                      ...lfChild,
                      defaultValue: elecFrequency,
                    };
                  }
                  if (lfChild.fieldName === "accountType") {
                    const autoDefault =
                      Array.isArray(lfChild.options) && lfChild.options.length === 1
                        ? (lfChild.options[0] as any)?.value
                        : undefined;
                    return {
                      ...lfChild,
                      label: "Arrears Type",
                      isRequired: true,
                      ...(autoDefault ? { defaultValue: autoDefault } : {}),
                      ui: {
                        ...(lfChild.ui ?? {}),
                        dialogLabel: "Select arrears type",
                      },
                    };
                  }
                  return lfChild;
                });
                return childField;
              }
              return childField;
            }
          );
        }
      }
    }

    if (
      selectedOuter === "flexible-expenses" &&
      selectedInner === "leisure" &&
      broadbandSuppliersLoaded &&
      landlineSuppliersLoaded &&
      mobileSuppliersLoaded
    ) {
      const schema = ensureSchemaClone();
      const section = schema.sections?.[0];
      if (section) {
        const formId = `${selectedOuter}-${selectedInner}-form`;
        const currentFormData =
          (allFormData[formId] as Record<string, any>) || {};
        const landlineData =
          (currentFormData.landlineInternetTV as Record<string, any>) || {};
        const mobileData =
          (currentFormData.mobilePhones as Record<string, any>) || {};
        const supplierAmountWarning =
          "You have selected the company but missed to enter the amount.";
        const shouldShowSupplierAmountWarning = (
          amountValue: unknown,
          supplierValue: unknown
        ) => {
          const supplierChosen =
            typeof supplierValue === "string" &&
            supplierValue.trim().length > 0;
          const amountMissing = (() => {
            if (amountValue === null || amountValue === undefined) return true;
            const normalized =
              typeof amountValue === "number"
                ? amountValue.toString()
                : `${amountValue}`;
            return normalized.trim().length === 0;
          })();
          return supplierChosen && amountMissing;
        };

        const landlineInternetTVField = section.fields?.find(
          (f: any) => f.fieldName === "landlineInternetTV"
        );
        if (landlineInternetTVField?.children) {
          landlineInternetTVField.children =
            landlineInternetTVField.children.map((childField: any) => {
              if (childField.fieldName === "landlineSupplier") {
                const manualTopOption = {
                  value: "Other",
                  label: "Other",
                };
                const seen = new Set<string>();
                const mapped = landlineSuppliers
                  .map((supplier) => ({
                    value: supplier.supplierName,
                    label: supplier.supplierName,
                  }))
                  .filter((opt) => {
                    const val = String(opt.value || "").trim().toLowerCase();
                    const lbl = String(opt.label || "").trim().toLowerCase();
                    return val !== "other" && lbl !== "other";
                  })
                  .filter((opt) => {
                    const key = (opt.label || "").trim().toLowerCase();
                    if (!key || seen.has(key)) return false;
                    seen.add(key);
                    return true;
                  })
                  .sort((a, b) => a.label.localeCompare(b.label));
                const supplierOptions = [manualTopOption, ...mapped];
                const showWarning = shouldShowSupplierAmountWarning(
                  landlineData.amount,
                  landlineData.landlineSupplier
                );
                const updatedUi: Record<string, any> = {
                  ...(childField.ui ?? {}),
                };
                if (showWarning) {
                  updatedUi.helperBottom = supplierAmountWarning;
                  updatedUi.helperBottomTone = "error";
                  updatedUi.showErrorBelow = true;
                } else if (
                  updatedUi.helperBottom === supplierAmountWarning ||
                  updatedUi.helperBottomTone === "error"
                ) {
                  delete updatedUi.helperBottom;
                  delete updatedUi.helperBottomTone;
                }
                return { ...childField, options: supplierOptions, ui: updatedUi };
              }

              if (childField.fieldName === "broadbandSupplier") {
                const manualTopOption = {
                  value: "Other",
                  label: "Other",
                };
                const seen = new Set<string>();
                const mapped = broadbandSuppliers
                  .map((supplier) => ({
                    value: supplier.supplierName,
                    label: supplier.supplierName,
                  }))
                  .filter((opt) => {
                    const val = String(opt.value || "").trim().toLowerCase();
                    const lbl = String(opt.label || "").trim().toLowerCase();
                    return val !== "other" && lbl !== "other";
                  })
                  .filter((opt) => {
                    const key = (opt.label || "").trim().toLowerCase();
                    if (!key || seen.has(key)) return false;
                    seen.add(key);
                    return true;
                  })
                  .sort((a, b) => a.label.localeCompare(b.label));
                const supplierOptions = [manualTopOption, ...mapped];
                const showWarning = shouldShowSupplierAmountWarning(
                  landlineData.amount,
                  landlineData.broadbandSupplier
                );
                const updatedUi: Record<string, any> = {
                  ...(childField.ui ?? {}),
                };
                if (showWarning) {
                  updatedUi.helperBottom = supplierAmountWarning;
                  updatedUi.helperBottomTone = "error";
                  updatedUi.showErrorBelow = true;
                } else if (
                  updatedUi.helperBottom === supplierAmountWarning ||
                  updatedUi.helperBottomTone === "error"
                ) {
                  delete updatedUi.helperBottom;
                  delete updatedUi.helperBottomTone;
                }
                return { ...childField, options: supplierOptions, ui: updatedUi };
              }

              return childField;
            });
        }

        const mobilePhonesField = section.fields?.find(
          (f: any) => f.fieldName === "mobilePhones"
        );
        if (mobilePhonesField?.children) {
          mobilePhonesField.children = mobilePhonesField.children.map(
            (childField: any) => {
              if (childField.fieldName === "mobileSupplier") {
                const manualTopOption = {
                  value: "Other",
                  label: "Other",
                };
                const seen = new Set<string>();
                const mapped = mobileSuppliers
                  .map((supplier) => ({
                    value: supplier.supplierName,
                    label: supplier.supplierName,
                  }))
                  .filter((opt) => {
                    const val = String(opt.value || "").trim().toLowerCase();
                    const lbl = String(opt.label || "").trim().toLowerCase();
                    return val !== "other" && lbl !== "other";
                  })
                  .filter((opt) => {
                    const key = (opt.label || "").trim().toLowerCase();
                    if (!key || seen.has(key)) return false;
                    seen.add(key);
                    return true;
                  })
                  .sort((a, b) => a.label.localeCompare(b.label));
                const supplierOptions = [manualTopOption, ...mapped];

                const showWarning = shouldShowSupplierAmountWarning(
                  mobileData.amount,
                  mobileData.mobileSupplier
                );
                const updatedUi: Record<string, any> = {
                  ...(childField.ui ?? {}),
                };
                if (showWarning) {
                  updatedUi.helperBottom = supplierAmountWarning;
                  updatedUi.helperBottomTone = "error";
                  updatedUi.showErrorBelow = true;
                } else if (
                  updatedUi.helperBottom === supplierAmountWarning ||
                  updatedUi.helperBottomTone === "error"
                ) {
                  delete updatedUi.helperBottom;
                  delete updatedUi.helperBottomTone;
                }

                return {
                  ...childField,
                  options: supplierOptions,
                  ui: updatedUi,
                };
              }
              return childField;
            }
          );
        }
      }
    }

    if (lendersLoaded && lenders.length > 0) {
      const schema = ensureSchemaClone();
      applyCreditCardDebtEnhancements(schema);
      applyLoanDebtEnhancements(schema);
      applyOverdraftDebtEnhancements(schema);
      applyOtherDebtEnhancements(schema);
      applyArrearsEnhancements(schema);
    }

    if (selectedOuter === "debts" && selectedInner === "debts") {
      const schema = ensureSchemaClone();
      schema.sections?.forEach((section: any) => {
        if (!Array.isArray(section?.fields)) {
          return;
        }

        section.fields = section.fields.map((field: any) => {
          if (field?.dataType === "debt-category") {
            const existingContext = field.ui?.debtContext ?? {};
            const categoryType = field.ui?.categoryType ?? field.fieldName;
            field.ui = {
              ...field.ui,
              categoryType,
              debtContext: {
                ...existingContext,
                lenders,
                accountTypes: debtAccountTypes,
                loading: debtsLoading || accountTypesLoading || !lendersLoaded,
                description: field.helpText,
                readOnly: field.isEditable === false,
                hideAccountType: true,
              },
            };
          }

          if (
            field?.fieldName === "haveDebts" &&
            mappedDebts.haveDebts !== undefined
          ) {
            field.defaultValue = mappedDebts.haveDebts;
          }

          if (
            field?.fieldName === "allDebtsAdded" &&
            mappedDebts.allDebtsAdded !== undefined
          ) {
            field.defaultValue = mappedDebts.allDebtsAdded;
          }

          return field;
        });
      });
    }

    // Apply Northern Ireland label transformations (council rates, water hiding, savings)
    const finalSchema = schemaClone ?? baseSchema;
    if (finalSchema && isNorthernIreland) {
      // Pass expense data so water fields can be hidden for NI users without existing data
      const expenseRecord = expenseData?.[0];
      applyNISchemaTransformations(finalSchema, isNorthernIreland, {
        expenseData: expenseRecord
          ? {
              waterBill: expenseRecord.waterBill,
              waterWaste: expenseRecord.waterWaste,
            }
          : undefined,
      });
    }

    return finalSchema;
  }, [
    currentInnerRoutes,
    selectedInner,
    selectedOuter,
    lendersLoaded,
    filteredLenders,
    lendersMap,
    lenderRecordLookup,
    arrearsCompanyOptions,
    accountTypesByCompany,
    allAccountTypeOptions,
    allFormData,
    energyCompaniesLoaded,
    energyCompanies,
    broadbandSuppliersLoaded,
    broadbandSuppliers,
    landlineSuppliersLoaded,
    landlineSuppliers,
    mobileSuppliersLoaded,
    mobileSuppliers,
    debtAccountTypes,
    debtsLoading,
    accountTypesLoading,
    mappedDebts,
    isNorthernIreland,
    expenseData,
  ]);

  // Build a lightweight profile snapshot (best-effort)
  const profileSnapshot = useMemo(() => {
    const snapshot: Record<string, any> = {};
    try {
      const storedCustomerRaw =
        typeof window !== "undefined"
          ? localStorage.getItem("customerData")
          : null;
      const storedCustomer = storedCustomerRaw
        ? JSON.parse(storedCustomerRaw)
        : null;
      if (storedCustomer) {
        snapshot.housingTenure =
          storedCustomer.housingTenure ??
          storedCustomer.accomodationType ??
          snapshot.housingTenure;
        snapshot.customerEmploymentStatus =
          storedCustomer.customerEmploymentStatus ??
          snapshot.customerEmploymentStatus;
        snapshot.haveChildrenUnder18 =
          storedCustomer.haveChildrenUnder18 ?? snapshot.haveChildrenUnder18;
        snapshot.dependentChildrenUnder16 =
          storedCustomer.dependentChildrenUnder16 ??
          snapshot.dependentChildrenUnder16;
        snapshot.dependentChildrenAged16Plus =
          storedCustomer.dependentChildrenAged16Plus ??
          snapshot.dependentChildrenAged16Plus;
        snapshot.noOfVehiclesInHouseholds =
          storedCustomer.noOfVehiclesInHouseholds ??
          snapshot.noOfVehiclesInHouseholds;
        snapshot.debtor1DateOfBirthStr =
          storedCustomer.debtor1DateOfBirthStr ??
          snapshot.debtor1DateOfBirthStr;
        snapshot.partnerEmploymentStatus =
          storedCustomer.partnerEmploymentStatus ??
          snapshot.partnerEmploymentStatus;
      }
    } catch {}

    // Overlay the live `customerMetadata` state so profile edits (dialogs) take effect
    // immediately across warnings/triggers without requiring a full refresh.
    snapshot.housingTenure =
      customerMetadata.accommodationType ?? snapshot.housingTenure;
    snapshot.customerEmploymentStatus =
      customerMetadata.customerEmploymentStatus ?? snapshot.customerEmploymentStatus;
    snapshot.partnerEmploymentStatus =
      customerMetadata.partnerEmploymentStatus ?? snapshot.partnerEmploymentStatus;
    snapshot.haveChildrenUnder18 =
      customerMetadata.haveChildrenUnder18 ?? snapshot.haveChildrenUnder18;
    snapshot.dependentChildrenUnder16 =
      customerMetadata.dependentChildrenUnder16 ?? snapshot.dependentChildrenUnder16;
    snapshot.dependentChildrenAged16Plus =
      customerMetadata.dependentChildrenAged16Plus ??
      snapshot.dependentChildrenAged16Plus;

    const inc = incomeData?.[0] as any;
    if (inc) {
      // Derive UC receipt if present
      const ucVal = inc.otherIncludeUniversalCredit ?? inc.universalCredit;
      snapshot.universalCredit =
        typeof ucVal === "number" ? ucVal > 0 : Boolean(ucVal);
    }

    // Derive pension-age flag for section-level pension rules
    const PENSION_AGE_YEARS = 65;
    const dobStr = snapshot.debtor1DateOfBirthStr as string | undefined;
    const ageYears = calculateAge(dobStr);
    if (typeof ageYears === "number") {
      snapshot.isPensionAge = ageYears >= PENSION_AGE_YEARS;
    }

    return snapshot;
  }, [customerMetadata, incomeData, warningsRefreshKey]);

  const getInitialDataForProfileDialog = useCallback(
    (stepIndex: number): Record<string, unknown> => {
      const formId = `income-onboarding-step-${stepIndex}`;
      const existing = allFormData[formId];
      if (existing && Object.keys(existing).length > 0) {
        return { ...existing };
      }

      const storedCustomer = readStoredCustomerData() || {};
      const initial: Record<string, unknown> = {};

      switch (stepIndex) {
        case 0: {
          const accommodation = (customerMetadata.accommodationType ??
            storedCustomer.housingTenure ??
            storedCustomer.accomodationType) as string | undefined;
          if (accommodation) {
            initial.accomodationType = accommodation;
          }
          break;
        }
        case 1: {
          const employment = (customerMetadata.customerEmploymentStatus ??
            storedCustomer.customerEmploymentStatus) as string | undefined;
          if (employment) {
            initial.customerEmploymentStatus = employment;
          }
          break;
        }
        case 2: {
          const jointAccountRaw =
            customerMetadata.jointAccount ?? storedCustomer.jointAccount;
          if (jointAccountRaw !== undefined) {
            const withPartner =
              jointAccountRaw === "Y" ||
              jointAccountRaw === "y" ||
              jointAccountRaw === true;
            initial.budgetWithPartner = withPartner;
            if (withPartner) {
              const partnerName = storedCustomer.debtor2Name as
                | string
                | undefined;
              if (partnerName) initial.name = partnerName;

              const dobRaw = (storedCustomer.debtor2DateOfBirthStr ??
                storedCustomer.debtor2DateOfBirth) as string | undefined;
              if (dobRaw) {
                const dobDate = new Date(dobRaw);
                if (!Number.isNaN(dobDate.getTime())) {
                  initial.dob = dobDate;
                }
              }

              const partnerEmployment =
                (customerMetadata.partnerEmploymentStatus ??
                  storedCustomer.partnerEmploymentStatus) as string | undefined;
              if (partnerEmployment) {
                initial.employmentStatus = partnerEmployment;
              }
            }
          }
          break;
        }
        case 3: {
          const jointAccountRaw =
            customerMetadata.jointAccount ?? storedCustomer.jointAccount;
          if (jointAccountRaw !== undefined) {
            initial.budgetWithPartner =
              jointAccountRaw === "Y" ||
              jointAccountRaw === "y" ||
              jointAccountRaw === true;
          }

          const haveDependentsRaw =
            customerMetadata.haveChildrenUnder18 ??
            storedCustomer.haveChildrenUnder18;
          // Only pre-fill if the user has explicitly answered this question (Y or N)
          // Don't pre-fill if undefined/null (new user who hasn't answered yet)
          if (haveDependentsRaw !== undefined && haveDependentsRaw !== null) {
            // Also consider other adults when determining haveDependents
            // adultsOver18IncludingMyself is actually other adults excluding customer and partner
            const adultsOver18 = storedCustomer.adultsOver18IncludingMyself;
            const otherAdultsCount = typeof adultsOver18 === "number" ? adultsOver18 : 0;
            const hasOtherAdults = otherAdultsCount > 0;
            
            const hasDependents =
              haveDependentsRaw === "Y" ||
              haveDependentsRaw === "y" ||
              haveDependentsRaw === true ||
              hasOtherAdults;
            initial.haveDependents = hasDependents;
            if (hasDependents) {
              const under16 = storedCustomer.dependentChildrenUnder16;
              if (under16 !== undefined) initial.under16Dependents = under16;
              const between16To18 = storedCustomer.dependentChildrenAged16Plus;
              if (between16To18 !== undefined)
                initial.between16to18Dependents = between16To18;
              // adultsOver18IncludingMyself is actually other adults excluding customer and partner
              const adultsOver18 = storedCustomer.adultsOver18IncludingMyself;
              if (adultsOver18 !== undefined)
                initial.otherAdultsOver18 = Number(adultsOver18);

              const dependentsSource =
                Array.isArray(dependentsDobData) && dependentsDobData.length > 0
                  ? dependentsDobData
                  : [];
              dependentsSource
                .filter((dep) => dep.dependantCategory === "age-under16")
                .forEach((dep) => {
                  const dobStr =
                    dep.childDependantDobStr ?? dep.childDependantDob;
                  if (!dobStr) return;
                  const dobDate = new Date(dobStr);
                  if (!Number.isNaN(dobDate.getTime())) {
                    initial[`under16DependentsDOB_${dep.childDependantNo}`] =
                      dobDate;
                  }
                });

              dependentsSource
                .filter((dep) => dep.dependantCategory === "age-16to18")
                .forEach((dep) => {
                  const dobStr =
                    dep.childDependantDobStr ?? dep.childDependantDob;
                  if (!dobStr) return;
                  const dobDate = new Date(dobStr);
                  if (!Number.isNaN(dobDate.getTime())) {
                    initial[
                      `between16to18DependentsDOB_${dep.childDependantNo}`
                    ] = dobDate;
                  }
                });
            }
          }
          break;
        }
        case 4: {
          const haveVehicleRaw =
            storedCustomer.haveVehicle ?? storedCustomer.hasVehicle;
          if (haveVehicleRaw !== undefined) {
            const hasVehicle =
              haveVehicleRaw === "Y" ||
              haveVehicleRaw === "y" ||
              haveVehicleRaw === true;
            initial.haveVehicle = hasVehicle;
            const vehicleCountRaw =
              storedCustomer.noOfVehiclesInHouseholds ??
              storedCustomer.vehicles;
            if (hasVehicle && vehicleCountRaw !== undefined) {
              const parsed =
                typeof vehicleCountRaw === "string"
                  ? parseInt(vehicleCountRaw, 10)
                  : Number(vehicleCountRaw);
              if (!Number.isNaN(parsed)) {
                initial.noOfVehiclesInHouseholds = parsed;
              }
            }
          }
          break;
        }
        case 5: {
          const partnerEmployment = (customerMetadata.partnerEmploymentStatus ??
            storedCustomer.partnerEmploymentStatus) as string | undefined;
          if (partnerEmployment) {
            initial.partnerEmploymentStatus = partnerEmployment;
          }
          break;
        }
        default:
          break;
      }

      return initial;
    },
    [allFormData, customerMetadata, readStoredCustomerData, dependentsDobData]
  );

  const openProfileWarningDialog = useCallback(
    (key: ProfileDialogKey): boolean => {
      const stepIndex = PROFILE_DIALOG_STEP_MAP[key];
      if (typeof stepIndex !== "number") return false;
      
      // Step 5 (partner employment) uses separate schema since it's not in onboarding array
      const schema = stepIndex === 5 
        ? partnerEmploymentSchema 
        : incomeOnboardingSchemas[stepIndex];
      if (!schema) return false;

      const initialData = getInitialDataForProfileDialog(stepIndex);
      setProfileDialogState({
        key,
        stepIndex,
        schema: schema as ScreenSchema,
        title: PROFILE_DIALOG_TITLES[key] ?? "",
        initialData,
      });

      return true;
    },
    [getInitialDataForProfileDialog]
  );

  const handleProfileDialogSubmit = useCallback(
    async (formData: Record<string, unknown>) => {
      if (!profileDialogState) return;

      const { stepIndex, key } = profileDialogState;
      const formStoreId = `income-onboarding-step-${stepIndex}`;

      setFormData(formStoreId, formData);
      setIsProfileDialogSaving(true);

      const payload: Record<string, any> = {};
      const localUpdates: Record<string, unknown> = {};
      const metadataUpdates: Record<string, unknown> = {};
      let shouldRefreshDependents = false;
      let persistDependentsDobs: null | (() => Promise<void>) = null;

      try {
        const storedCustomer = readStoredCustomerData() || {};
        const customerIdRaw = resolveCustomerId();
        const customerId =
          typeof customerIdRaw === "number"
            ? customerIdRaw
            : typeof customerIdRaw === "string"
              ? parseInt(customerIdRaw, 10)
              : null;
        const userIdRaw = resolveUserId();
        const userId =
          typeof userIdRaw === "number"
            ? userIdRaw
            : typeof userIdRaw === "string"
              ? parseInt(userIdRaw, 10)
              : null;

        switch (stepIndex) {
          case 0: {
            const accommodation =
              typeof formData.accomodationType === "string"
                ? formData.accomodationType
                : "";
            if (accommodation) {
              payload.housingTenure = accommodation;
              localUpdates.housingTenure = accommodation;
              localUpdates.accomodationType = accommodation;
              metadataUpdates.accommodationType = accommodation;
            }
            break;
          }
          case 1: {
            const employment =
              typeof formData.customerEmploymentStatus === "string"
                ? formData.customerEmploymentStatus
                : "";
            if (employment) {
              payload.customerEmploymentStatus = employment;
              localUpdates.customerEmploymentStatus = employment;
              metadataUpdates.customerEmploymentStatus = employment;
            }
            break;
          }
          case 2: {
            if (formData.budgetWithPartner !== undefined) {
              const withPartner = formData.budgetWithPartner === true;
              payload.jointAccount = withPartner ? "Y" : "N";
              localUpdates.jointAccount = payload.jointAccount;
              metadataUpdates.jointAccount = payload.jointAccount;

              if (withPartner) {
                const partnerName =
                  typeof formData.name === "string" ? formData.name : "";
                if (partnerName) {
                  payload.debtor2Name = partnerName;
                  localUpdates.debtor2Name = partnerName;
                }

                const dobValue = formData.dob;
                let dobString: string | null = null;
                if (
                  dobValue instanceof Date &&
                  !Number.isNaN(dobValue.getTime())
                ) {
                  const year = dobValue.getFullYear();
                  const month = String(dobValue.getMonth() + 1).padStart(
                    2,
                    "0"
                  );
                  const day = String(dobValue.getDate()).padStart(2, "0");
                  dobString = `${year}-${month}-${day}`;
                } else if (typeof dobValue === "string" && dobValue) {
                  const parsed = new Date(dobValue);
                  if (!Number.isNaN(parsed.getTime())) {
                    const year = parsed.getFullYear();
                    const month = String(parsed.getMonth() + 1).padStart(
                      2,
                      "0"
                    );
                    const day = String(parsed.getDate()).padStart(2, "0");
                    dobString = `${year}-${month}-${day}`;
                  }
                }
                if (dobString) {
                  payload.debtor2DateOfBirthStr = dobString;
                  payload.debtor2DateOfBirth = dobString;
                  localUpdates.debtor2DateOfBirthStr = dobString;
                  localUpdates.debtor2DateOfBirth = dobString;
                }

                const partnerEmployment =
                  typeof formData.employmentStatus === "string"
                    ? formData.employmentStatus
                    : "";
                if (partnerEmployment) {
                  payload.partnerEmploymentStatus = partnerEmployment;
                  localUpdates.partnerEmploymentStatus = partnerEmployment;
                  metadataUpdates.partnerEmploymentStatus = partnerEmployment;
                }
              } else {
                localUpdates.debtor2Name = "";
                localUpdates.debtor2DateOfBirthStr = "";
                localUpdates.debtor2DateOfBirth = "";
                localUpdates.partnerEmploymentStatus = "";
                metadataUpdates.partnerEmploymentStatus = "";
              }

              const step3Existing =
                allFormData["income-onboarding-step-3"] || {};
              setFormData("income-onboarding-step-3", {
                ...step3Existing,
                budgetWithPartner: formData.budgetWithPartner,
                jointAccount: payload.jointAccount,
              });
            }
            break;
          }
          case 3: {
            const hasDependents = formData.haveDependents === true;
            if (formData.haveDependents !== undefined) {
              payload.haveChildrenUnder18 = hasDependents ? "Y" : "N";
              localUpdates.haveChildrenUnder18 = payload.haveChildrenUnder18;
              metadataUpdates.haveChildrenUnder18 = payload.haveChildrenUnder18;
            }

            if (hasDependents) {
              const under16Value = formData.under16Dependents;
              const under16Count =
                typeof under16Value === "number"
                  ? under16Value
                  : typeof under16Value === "string"
                    ? parseInt(under16Value, 10)
                    : NaN;
              if (Number.isFinite(under16Count)) {
                payload.dependentChildrenUnder16 = under16Count;
                localUpdates.dependentChildrenUnder16 = under16Count;
                // IMPORTANT: visibility rules read from customerMetadata (merged into initialData)
                metadataUpdates.dependentChildrenUnder16 = under16Count;
              }
              const between16Value = formData.between16to18Dependents;
              const between16Count =
                typeof between16Value === "number"
                  ? between16Value
                  : typeof between16Value === "string"
                    ? parseInt(between16Value, 10)
                    : NaN;
              if (Number.isFinite(between16Count)) {
                payload.dependentChildrenAged16Plus = between16Count;
                localUpdates.dependentChildrenAged16Plus = between16Count;
                // IMPORTANT: keep metadata in sync so dependent screens (school/care) re-evaluate
                metadataUpdates.dependentChildrenAged16Plus = between16Count;
              }
              const adultsValue = formData.otherAdultsOver18;
              if (typeof adultsValue === "number") {
                // adultsOver18IncludingMyself is actually other adults excluding customer and partner
                payload.adultsOver18IncludingMyself = adultsValue;
                localUpdates.adultsOver18IncludingMyself = adultsValue;
              }

              // Defer dependent DOB writes until after confirm-before-clear (if any)
              if (typeof customerId === "number" && typeof userId === "number") {
                const customerIdNum = Number(customerId);
                const normalizedUserId = Number(userId);
                if (Number.isFinite(customerIdNum) && Number.isFinite(normalizedUserId)) {
                  const under16CountForLoop =
                    typeof under16Value === "number" ? under16Value : 0;
                  const between16CountForLoop =
                    typeof between16Value === "number" ? between16Value : 0;

                  persistDependentsDobs = async () => {
                    try {
                      const existingResp =
                        await DependentsService.getDependents(customerIdNum);
                      const existingDependents =
                        existingResp.success && Array.isArray(existingResp.data)
                          ? (existingResp.data as DependentDobRecord[])
                          : [];

                      const ensureDobString = (value: unknown): string | null => {
                        if (!value) return null;
                        if (value instanceof Date && !Number.isNaN(value.getTime())) {
                          const year = value.getFullYear();
                          const month = String(value.getMonth() + 1).padStart(2, "0");
                          const day = String(value.getDate()).padStart(2, "0");
                          return `${year}-${month}-${day}`;
                        }
                        if (typeof value === "string") {
                          const parsed = new Date(value);
                          if (!Number.isNaN(parsed.getTime())) {
                            const year = parsed.getFullYear();
                            const month = String(parsed.getMonth() + 1).padStart(2, "0");
                            const day = String(parsed.getDate()).padStart(2, "0");
                            return `${year}-${month}-${day}`;
                          }
                        }
                        return null;
                      };

                      for (let i = 1; i <= under16CountForLoop; i++) {
                        const dobFieldName = `under16DependentsDOB_${i}`;
                        const dobStr = ensureDobString(formData[dobFieldName]);
                        if (!dobStr) continue;

                        const existing = existingDependents.find(
                          (dep) =>
                            dep.dependantCategory === "age-under16" &&
                            dep.childDependantNo === String(i)
                        );

                        if (existing && existing.id) {
                          await DependentsService.updateDependent({
                            id: existing.id,
                            customerId: existing.customerId,
                            childDependantDob: dobStr,
                            childDependantDobStr: dobStr,
                            childDependantNo: String(i),
                            dependantCategory: "age-under16",
                            lastUpdatedBy: normalizedUserId,
                            lastUpdateDate: new Date().toISOString(),
                          });
                        } else {
                          await DependentsService.createDependent({
                            customerId: customerIdNum,
                            userId: normalizedUserId,
                            childDependantDob: dobStr,
                            childDependantDobStr: dobStr,
                            childDependantNo: String(i),
                            dependantCategory: "age-under16",
                            createdBy: normalizedUserId,
                          });
                        }
                      }

                      const deletableUnder16 = existingDependents.filter(
                        (dep) =>
                          dep.dependantCategory === "age-under16" &&
                          Number(dep.childDependantNo) > under16CountForLoop
                      );
                      for (const dep of deletableUnder16) {
                        if (dep.id) {
                          await DependentsService.deleteDependent(customerIdNum, dep.id);
                        }
                      }

                      for (let i = 1; i <= between16CountForLoop; i++) {
                        const dobFieldName = `between16to18DependentsDOB_${i}`;
                        const dobStr = ensureDobString(formData[dobFieldName]);
                        if (!dobStr) continue;

                        const existing = existingDependents.find(
                          (dep) =>
                            dep.dependantCategory === "age-16to18" &&
                            dep.childDependantNo === String(i)
                        );

                        if (existing && existing.id) {
                          await DependentsService.updateDependent({
                            id: existing.id,
                            customerId: existing.customerId,
                            childDependantDob: dobStr,
                            childDependantDobStr: dobStr,
                            childDependantNo: String(i),
                            dependantCategory: "age-16to18",
                            lastUpdatedBy: normalizedUserId,
                            lastUpdateDate: new Date().toISOString(),
                          });
                        } else {
                          await DependentsService.createDependent({
                            customerId: customerIdNum,
                            userId: normalizedUserId,
                            childDependantDob: dobStr,
                            childDependantDobStr: dobStr,
                            childDependantNo: String(i),
                            dependantCategory: "age-16to18",
                            createdBy: normalizedUserId,
                          });
                        }
                      }

                      const deletable16to18 = existingDependents.filter(
                        (dep) =>
                          dep.dependantCategory === "age-16to18" &&
                          Number(dep.childDependantNo) > between16CountForLoop
                      );
                      for (const dep of deletable16to18) {
                        if (dep.id) {
                          await DependentsService.deleteDependent(customerIdNum, dep.id);
                        }
                      }
                    } catch (error) {
                      console.error(
                        "Failed to update dependents from profile dialog",
                        error
                      );
                    }
                  };

                  shouldRefreshDependents = true;
                }
              }
            } else {
              // Mirror onboarding behaviour: keep profile counters consistent when switching to "No"
              payload.dependentChildrenUnder16 = 0;
              payload.dependentChildrenAged16Plus = 0;
              payload.adultsOver18IncludingMyself = 0;

              localUpdates.dependentChildrenUnder16 = 0;
              localUpdates.dependentChildrenAged16Plus = 0;
              localUpdates.adultsOver18IncludingMyself = 0;

              // IMPORTANT: update metadata so IE visibility updates immediately without refresh
              metadataUpdates.dependentChildrenUnder16 = 0;
              metadataUpdates.dependentChildrenAged16Plus = 0;
            }
            break;
          }
          case 4: {
            if (formData.haveVehicle !== undefined) {
              const hasVehicle = formData.haveVehicle === true;
              payload.haveVehicle = hasVehicle ? "Y" : "N";
              localUpdates.haveVehicle = payload.haveVehicle;

              const countRaw = formData.noOfVehiclesInHouseholds;
              let count: number | null = null;
              if (typeof countRaw === "number") {
                count = countRaw;
              } else if (typeof countRaw === "string") {
                const parsed = parseInt(countRaw, 10);
                if (!Number.isNaN(parsed)) count = parsed;
              }

              if (hasVehicle && count !== null) {
                payload.noOfVehiclesInHouseholds = count;
                localUpdates.noOfVehiclesInHouseholds = count;
              } else if (!hasVehicle) {
                // IMPORTANT:
                // When switching to "No", we must explicitly persist 0 to the backend.
                // Otherwise the previous saved value (e.g. 1) lingers and can trigger red warnings.
                payload.noOfVehiclesInHouseholds = 0;
                localUpdates.noOfVehiclesInHouseholds = 0;
              }
            }
            break;
          }
          case 5: {
            const partnerEmployment =
              typeof formData.partnerEmploymentStatus === "string"
                ? formData.partnerEmploymentStatus
                : "";
            if (partnerEmployment) {
              payload.partnerEmploymentStatus = partnerEmployment;
              localUpdates.partnerEmploymentStatus = partnerEmployment;
              metadataUpdates.partnerEmploymentStatus = partnerEmployment;
            }
            break;
          }
          default:
            break;
        }

        const doSave = async (): Promise<void> => {
          try {
            if (Object.keys(payload).length > 0) {
              if (typeof customerId !== "number" || !Number.isFinite(customerId)) {
                throw new Error("Missing customer id");
              }
              const response = await ProfileService.patchCustomerData(customerId, payload);
              if (!response.success) {
                const message =
                  typeof response.error === "string"
                    ? response.error
                    : response.error?.message || "Failed to update profile details";
                throw new Error(message);
              }
            }

            if (typeof persistDependentsDobs === "function") {
              await persistDependentsDobs();
            }

            if (shouldRefreshDependents && typeof customerId === "number") {
              try {
                const refreshed = await DependentsService.getDependents(customerId);
                if (refreshed.success && Array.isArray(refreshed.data)) {
                  setDependentsDobData(refreshed.data);
                }
              } catch (error) {
                console.error("Failed to refresh dependents after save", error);
              }
            }

            if (typeof window !== "undefined" && Object.keys(localUpdates).length > 0) {
              const currentCustomer = storedCustomer;
              const updatedCustomer = { ...currentCustomer, ...localUpdates };
              try {
                localStorage.setItem("customerData", JSON.stringify(updatedCustomer));
              } catch {
                // ignore storage errors
              }
            }

            if (Object.keys(metadataUpdates).length > 0) {
              setCustomerMetadata((prev) => ({
                ...prev,
                ...metadataUpdates,
              }));
            }

            showAlert({
              severity: "success",
              message: PROFILE_DIALOG_SUCCESS_MESSAGES[key] ?? "Details updated",
              autoHide: true,
              duration: 1500,
            });

            // Profile changes can invalidate existing warning messages. Bump refresh key.
            setWarningsRefreshKey((prev) => prev + 1);
            setProfileDialogState(null);
          } catch (error) {
            console.error("Failed to save profile dialog data", error);
            showAlert({
              severity: "error",
              message: "Failed to save changes. Please try again.",
              autoHide: true,
              duration: 3000,
            });
          } finally {
            setIsProfileDialogSaving(false);
          }
        };

        // Confirm-before-clear gating: if this change will hide valued budget fields,
        // confirm and clear the budget fields first (same behaviour as ProfilePage).
        try {
          const shouldGate = Object.keys(payload).length > 0 && stepIndex !== 4;
          if (
            shouldGate &&
            typeof customerId === "number" &&
            Number.isFinite(customerId) &&
            typeof userId === "number" &&
            Number.isFinite(userId)
          ) {
            const stored =
              storedCustomer && typeof storedCustomer === "object"
                ? (storedCustomer as Record<string, unknown>)
                : {};
            const toNum = (v: unknown): number | undefined => {
              if (typeof v === "number" && Number.isFinite(v)) return v;
              if (typeof v === "string") {
                const n = parseInt(v, 10);
                return Number.isNaN(n) ? undefined : n;
              }
              return undefined;
            };

            const before: ProfileBudgetContext = {};

            const customerEmploymentStatus =
              customerMetadata.customerEmploymentStatus ??
              (typeof stored.customerEmploymentStatus === "string"
                ? stored.customerEmploymentStatus
                : undefined);
            if (typeof customerEmploymentStatus === "string") {
              before.customerEmploymentStatus = customerEmploymentStatus;
            }

            const partnerEmploymentStatus =
              customerMetadata.partnerEmploymentStatus ??
              (typeof stored.partnerEmploymentStatus === "string"
                ? stored.partnerEmploymentStatus
                : undefined);
            if (typeof partnerEmploymentStatus === "string") {
              before.partnerEmploymentStatus = partnerEmploymentStatus;
            }

            const jointAccount =
              customerMetadata.jointAccount ??
              (typeof stored.jointAccount === "string" ||
              typeof stored.jointAccount === "boolean"
                ? stored.jointAccount
                : undefined);
            if (typeof jointAccount === "string" || typeof jointAccount === "boolean") {
              before.jointAccount = jointAccount;
            }

            const accommodationType =
              customerMetadata.accommodationType ??
              (typeof stored.housingTenure === "string"
                ? (stored.housingTenure as string)
                : undefined);
            if (typeof accommodationType === "string") {
              before.accommodationType = accommodationType;
            }

            const haveChildrenUnder18 =
              customerMetadata.haveChildrenUnder18 ??
              (typeof stored.haveChildrenUnder18 === "string"
                ? (stored.haveChildrenUnder18 as string)
                : undefined);
            if (typeof haveChildrenUnder18 === "string") {
              before.haveChildrenUnder18 = haveChildrenUnder18;
            }

            const dependentChildrenUnder16 =
              typeof customerMetadata.dependentChildrenUnder16 === "number"
                ? customerMetadata.dependentChildrenUnder16
                : toNum(stored.dependentChildrenUnder16);
            if (typeof dependentChildrenUnder16 === "number") {
              before.dependentChildrenUnder16 = dependentChildrenUnder16;
            }

            const dependentChildrenAged16Plus =
              typeof customerMetadata.dependentChildrenAged16Plus === "number"
                ? customerMetadata.dependentChildrenAged16Plus
                : toNum(stored.dependentChildrenAged16Plus);
            if (typeof dependentChildrenAged16Plus === "number") {
              before.dependentChildrenAged16Plus = dependentChildrenAged16Plus;
            }

            const customerAge =
              typeof customerMetadata.customerAge === "number"
                ? customerMetadata.customerAge
                : toNum(stored.customerAge);
            if (typeof customerAge === "number") {
              before.customerAge = customerAge;
            }

            const partnerAge =
              typeof customerMetadata.partnerAge === "number"
                ? customerMetadata.partnerAge
                : toNum(stored.partnerAge);
            if (typeof partnerAge === "number") {
              before.partnerAge = partnerAge;
            }

            const afterOverrides: Partial<ProfileBudgetContext> = {};
            if (typeof payload.customerEmploymentStatus === "string") {
              afterOverrides.customerEmploymentStatus = payload.customerEmploymentStatus;
            }
            if (typeof payload.partnerEmploymentStatus === "string") {
              afterOverrides.partnerEmploymentStatus = payload.partnerEmploymentStatus;
            }
            if (typeof payload.jointAccount === "string") {
              afterOverrides.jointAccount = payload.jointAccount;
            }
            if (typeof payload.housingTenure === "string") {
              afterOverrides.accommodationType = payload.housingTenure;
            }
            if (typeof payload.haveChildrenUnder18 === "string") {
              afterOverrides.haveChildrenUnder18 = payload.haveChildrenUnder18;
            }
            if (typeof payload.dependentChildrenUnder16 === "number") {
              afterOverrides.dependentChildrenUnder16 = payload.dependentChildrenUnder16;
            }
            if (typeof payload.dependentChildrenAged16Plus === "number") {
              afterOverrides.dependentChildrenAged16Plus =
                payload.dependentChildrenAged16Plus;
            }

            const nextPartnerDob =
              typeof payload.debtor2DateOfBirthStr === "string"
                ? (payload.debtor2DateOfBirthStr as string)
                : typeof payload.debtor2DateOfBirth === "string"
                  ? (payload.debtor2DateOfBirth as string)
                  : undefined;
            if (typeof nextPartnerDob === "string") {
              try {
                const age = calculateAge(nextPartnerDob);
                if (typeof age === "number" && Number.isFinite(age)) {
                  afterOverrides.partnerAge = age;
                }
              } catch {}
            }

            const after: ProfileBudgetContext = { ...before, ...afterOverrides };

            const snaps: BudgetSnapshots = await (async () => {
              try {
                const [incomeResp, incomeNotesResp, expenseResp, expenseNotesResp] =
                  await Promise.all([
                    BudgetService.getIncomeData(customerId, { skipCache: true }),
                    BudgetService.getIncomeNotes(customerId, { skipCache: true }),
                    BudgetService.getExpenseData(customerId, { skipCache: true }),
                    BudgetService.getExpenseNotes(customerId, { skipCache: true }),
                  ]);

                const income =
                  incomeResp?.success &&
                  Array.isArray(incomeResp.data) &&
                  incomeResp.data[0]
                    ? incomeResp.data[0]
                    : null;
                const incomeNotes =
                  incomeNotesResp?.success &&
                  Array.isArray(incomeNotesResp.data) &&
                  incomeNotesResp.data[0]
                    ? incomeNotesResp.data[0]
                    : null;
                const expenses =
                  expenseResp?.success &&
                  Array.isArray(expenseResp.data) &&
                  expenseResp.data[0]
                    ? expenseResp.data[0]
                    : null;
                const expenseNotes =
                  expenseNotesResp?.success &&
                  Array.isArray(expenseNotesResp.data) &&
                  expenseNotesResp.data[0]
                    ? expenseNotesResp.data[0]
                    : null;

                return { income, incomeNotes, expenses, expenseNotes };
              } catch {
                return { income: null, incomeNotes: null, expenses: null, expenseNotes: null };
              }
            })();

            const plan = computeConfirmBeforeClearPlan({
              before,
              after,
              snaps,
              userId,
            });

            if (plan.needsConfirm) {
              setConfirmMessage(
                CONFIRM_REMOVE_SECTION_MESSAGE
              );
              // Stop "saving" state while waiting for user choice.
              setIsProfileDialogSaving(false);
              setPendingConfirmAction(async () => {
                setIsProfileDialogSaving(true);
                await clearBudgetForPlan(plan);
                await doSave();
                const refreshed = await refreshBudgetStateAfterClear(customerId);
                rehydrateLocalBudgetFormsAfterClear(plan, refreshed);
              });
              setConfirmOpen(true);
              return;
            }
          }
        } catch {
          // If gating fails, fall back to saving
        }

        await doSave();
      } catch (error) {
        console.error("Failed to save profile dialog data", error);
        showAlert({
          severity: "error",
          message: "Failed to save changes. Please try again.",
          autoHide: true,
          duration: 3000,
        });
      } finally {
        setIsProfileDialogSaving(false);
      }
    },
    [
      profileDialogState,
      setFormData,
      readStoredCustomerData,
      resolveCustomerId,
      resolveUserId,
      allFormData,
      setCustomerMetadata,
      setDependentsDobData,
      showAlert,
      setWarningsRefreshKey,
      customerMetadata,
      clearBudgetForPlan,
      refreshBudgetStateAfterClear,
      setPendingConfirmAction,
    ]
  );

  // Helpers
  const toUpperSnake = useCallback(
    (s: string) =>
      s
        ? s
            .replace(/([A-Z])/g, "_$1")
            .toUpperCase()
            .replace(/^_/, "")
        : s,
    []
  );

  const upperSnakeToCamel = useCallback((s: string) => {
    if (!s) return s as any;
    return s
      .toLowerCase()
      .split("_")
      .map((part, idx) =>
        idx === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1)
      )
      .join("");
  }, []);

  const getNested = useCallback((obj: any, path: string | undefined): any => {
    if (!obj || !path) return undefined;
    return path
      .split(".")
      .reduce(
        (acc: any, key: string) =>
          acc && typeof acc === "object" ? acc[key] : undefined,
        obj
      );
  }, []);

  // Compute current API-like values from the current form (for trigger comparisons and value rules)
  const currentApiValues = useMemo(() => {
    const formId = `${selectedOuter}-${selectedInner}-form`;
    const formData = allFormData[formId] || {};

    // Determine current category
    let category: keyof typeof BUDGET_FIELD_MAPPINGS = "salary";
    switch (selectedOuter) {
      case "income":
        if (selectedInner === "salary") category = "salary";
        else if (selectedInner === "benefits") category = "benefits";
        else if (selectedInner === "pension") category = "pension";
        else if (selectedInner === "other-sources") category = "otherSources";
        break;
      case "assets":
        if (selectedInner === "properties") category = "properties";
        else if (selectedInner === "otherAssets") category = "otherAssets";
        break;
      case "fixed-expenses":
        if (selectedInner === "household") category = "household";
        else if (selectedInner === "utilities") category = "utilities";
        else if (selectedInner === "care") category = "care";
        else if (selectedInner === "transport") category = "transport";
        else if (selectedInner === "school") category = "school";
        else if (selectedInner === "pension-insurance")
          category = "pensionInsurance";
        else if (selectedInner === "professionals") category = "professionals";
        else if (selectedInner === "other-sources")
          category = "otherSourcesFixed";
        break;
      case "flexible-expenses":
        if (selectedInner === "leisure") category = "leisure";
        else if (selectedInner === "housekeeping") category = "housekeeping";
        else if (selectedInner === "personal-costs") category = "personalCosts";
        else if (selectedInner === "savings") category = "savings";
        break;
      case "debts":
        if (selectedInner === "debts-administration")
          category = "debtsAdministration";
        else if (selectedInner === "debts") category = "debts";
        break;
    }

    // Determine income/expense
    const incomeAssetCategories = [
      "salary",
      "benefits",
      "pension",
      "otherSources",
      "properties",
      "otherAssets",
      "savings",
    ];
    const isIncomeOrAsset = incomeAssetCategories.includes(category);

    if (isIncomeOrAsset) {
      const mapped = mapSchemaDataToIncome(formData, category, {} as any);
      return { income: mapped, expense: {} as Record<string, any> };
    }
    const mapped = mapSchemaDataToExpense(formData, category, {} as any);
    return { income: {} as Record<string, any>, expense: mapped };
  }, [allFormData, selectedOuter, selectedInner]);

  // Log button actions for analytics
  const logButtonAction = useCallback(
    async (buttonName: string, subSection?: string) => {
      try {
        const customerId = resolveCustomerId();
        const userId = resolveUserId();

        if (!customerId || !userId) return;

        const actionData: ButtonActionLogRequest = {
          buttonName,
          pageName:
            selectedOuter === "income"
              ? "Income"
              : selectedOuter === "assets"
                ? "Assets"
                : selectedOuter === "fixed-expenses"
                  ? "Fixed expenses"
                  : selectedOuter === "flexible-expenses"
                    ? "Flexible expenses"
                    : selectedOuter === "debts"
                      ? "Debts"
                      : "Income",
          subSection: subSection || selectedInner,
          userId,
          customerId: customerId.toString(),
        };

        await BudgetService.logButtonAction(actionData);
      } catch (error) {
        console.warn("Failed to log button action:", error);
      }
    },
    [selectedOuter, selectedInner, resolveCustomerId, resolveUserId]
  );

  // Build triggers context (income + expense; root maxByField for bindings)
  const triggersContext = useMemo(() => {
    const incomeExceeded: Record<string, boolean> = {};
    const incomeMaxByField: Record<string, number> = {};
    const assetsExceeded: Record<string, boolean> = {};
    const assetsMaxByField: Record<string, number> = {};
    const expenseExceeded: Record<string, boolean> = {};
    const expenseMaxByField: Record<string, number> = {};
    const rootMaxByField: Record<string, number> = {};
    const frequencyPathByApiField: Record<string, string> = {};

    const normalizeToMonthly = (
      amount: number | undefined,
      freqRaw: unknown
    ): number | undefined => {
      if (typeof amount !== "number" || !Number.isFinite(amount)) return amount;
      const freq =
        normalizeBudgetFrequency(
          typeof freqRaw === "string" ? freqRaw : String(freqRaw ?? "")
        ) ?? "monthly";
      return toMonthlyBudgetAmount(amount, freq);
    };

    const normalizeTriggerFieldUpper = (raw: unknown): string => {
      const s =
        typeof raw === "string"
          ? raw
          : raw === null || raw === undefined
            ? ""
            : String(raw);
      const trimmed = s.trim();
      if (!trimmed) return "";

      // Normalize "House Rent", "house-rent", "houseRent", "HOUSE_RENT" -> "HOUSE_RENT"
      let out = trimmed
        .replace(/[^a-zA-Z0-9]+/g, "_")
        .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
        .replace(/_+/g, "_")
        .replace(/^_+|_+$/g, "")
        .toUpperCase();

      // Some trigger keys are enumerated (e.g. OTHER_SALARY_WAGES_1). If a number suffix
      // is present without an underscore, insert one: OTHER_SALARY_WAGES1 -> OTHER_SALARY_WAGES_1
      out = out.replace(/([A-Z])(\d+)$/g, "$1_$2");
      return out;
    };

    // Determine partner status for joint caps
    const onboardingStep3 = (allFormData["income-onboarding-step-3"] ||
      {}) as Record<string, any>;
    const withPartnerLive = onboardingStep3?.budgetWithPartner === true;
    const withPartnerSnapshot =
      (profileSnapshot as any)?.budgetWithPartner === true;
    const inc0 = (incomeData?.[0] as any) || {};
    const withPartnerBackend = inc0?.jointAccount === "Y";
    const withPartner = Boolean(
      withPartnerLive || withPartnerSnapshot || withPartnerBackend
    );

    // Build a reverse map for the current category to locate schema paths by API field name
    const formId = `${selectedOuter}-${selectedInner}-form`;
    const formData = allFormData[formId] || {};
    const currentCategory = (() => {
      switch (selectedOuter) {
        case "income":
          if (selectedInner === "salary") return "salary";
          if (selectedInner === "benefits") return "benefits";
          if (selectedInner === "pension") return "pension";
          if (selectedInner === "other-sources") return "otherSources";
          return "salary";
        case "assets":
          if (selectedInner === "properties") return "properties";
          if (selectedInner === "otherAssets") return "otherAssets";
          return "properties";
        case "fixed-expenses":
          if (selectedInner === "household") return "household";
          if (selectedInner === "utilities") return "utilities";
          if (selectedInner === "care") return "care";
          if (selectedInner === "transport") return "transport";
          if (selectedInner === "school") return "school";
          if (selectedInner === "pension-insurance") return "pensionInsurance";
          if (selectedInner === "professionals") return "professionals";
          if (selectedInner === "other-sources") return "otherSourcesFixed";
          return "household";
        case "flexible-expenses":
          if (selectedInner === "leisure") return "leisure";
          if (selectedInner === "housekeeping") return "housekeeping";
          if (selectedInner === "personal-costs") return "personalCosts";
          if (selectedInner === "savings") return "savings";
          return "leisure";
        default:
          return "salary";
      }
    })() as keyof typeof BUDGET_FIELD_MAPPINGS;

    const apiToSchemaPath: Record<string, string> = {};
    // Map trigger keys (UPPER_SNAKE, sometimes with/without underscores) -> API field name
    // This avoids assuming trigger keys can always be converted via upperSnakeToCamel().
    const triggerKeyToApiField: Record<string, string> = {};
    const mapping = BUDGET_FIELD_MAPPINGS[currentCategory] as any;
    if (mapping && typeof mapping === "object") {
      Object.entries(mapping).forEach(([schemaFieldName, def]: any) => {
        if (def && typeof def === "object" && typeof def.amount === "string") {
          apiToSchemaPath[def.amount] = `${schemaFieldName}.amount`;
          if (typeof def.frequency === "string") {
            frequencyPathByApiField[def.amount] = `${schemaFieldName}.frequency`;
          }

          // Build trigger-key aliases from the API field name.
          // Example: otherSalaryWages1 -> OTHER_SALARY_WAGES_1
          const apiField = def.amount as string;
          const key = normalizeTriggerFieldUpper(toUpperSnake(apiField));
          if (key) {
            triggerKeyToApiField[key] = apiField;
            // Also support variants without underscores (e.g., TVLICENCE)
            triggerKeyToApiField[key.replace(/_/g, "")] = apiField;
          }
        }
      });
    }

    try {
      (incomeTriggers as any[]).forEach((t: any) => {
        // Triggers can come back with multiple key formats:
        // - `fieldName` might be an API/backend field (camelCase)
        // - `columnName` might be an UPPER_SNAKE db column (sometimes with suffixes)
        //
        // For warning rules to match reliably, we:
        // 1) resolve the canonical API field name for this trigger (when possible)
        // 2) add aliases for both the raw trigger keys AND the canonical key derived from the API field.
        const rawKeys = [t?.fieldName, t?.columnName]
          .filter((v: unknown) => typeof v === "string" && v.trim().length > 0)
          .map((v: string) => v.trim());
        if (rawKeys.length === 0) return;

        const rawUpperKeys = rawKeys
          .map((k: string) => normalizeTriggerFieldUpper(k))
          .filter(Boolean) as string[];
        if (rawUpperKeys.length === 0) return;

        // Determine the API field name for this trigger in the current category.
        // Prefer:
        // - direct API field match (some trigger endpoints already return camelCase fieldName)
        // - mapping-derived resolution (UPPER_SNAKE -> api field)
        // - snake->camel fallback
        const apiFieldName = (() => {
          const directField = typeof t?.fieldName === "string" ? t.fieldName.trim() : "";
          if (directField && apiToSchemaPath[directField]) {
            return directField;
          }
          for (const upperKey of rawUpperKeys) {
            const hit =
              triggerKeyToApiField[upperKey] ??
              triggerKeyToApiField[upperKey.replace(/_/g, "")];
            if (hit) return hit;
          }
          return upperSnakeToCamel(rawUpperKeys[0] as string);
        })();

        const canonicalUpper =
          typeof apiFieldName === "string" && apiFieldName.length > 0
            ? normalizeTriggerFieldUpper(toUpperSnake(apiFieldName))
            : "";

        // Keep raw keys + canonical key + "no underscore" variants so warning rules
        // can match regardless of minor formatting differences between endpoints.
        const triggerKeyAliases = Array.from(
          new Set(
            [
              ...rawUpperKeys,
              ...rawUpperKeys.map((k) => k.replace(/_/g, "")),
              canonicalUpper,
              canonicalUpper ? canonicalUpper.replace(/_/g, "") : "",
            ].filter(Boolean)
          )
        );

        const incomeType = (
          t?.incomeType ? String(t.incomeType).toLowerCase() : ""
        ) as "income" | "asset" | "expense" | "";
        const group: "income" | "expense" | "assets" =
          incomeType === "expense"
            ? "expense"
            : incomeType === "asset"
              ? "assets"
              : "income";

        // Determine current value from mapped API-like values
        let currentVal =
          group === "expense"
            ? (currentApiValues.expense as any)[apiFieldName]
            : (currentApiValues.income as any)[apiFieldName];
        // Coerce numeric and fallback to schema path if missing
        let numericVal: number | undefined = undefined;
        if (typeof currentVal === "number") {
          numericVal = currentVal;
        } else if (typeof currentVal === "string") {
          const parsed = Number(String(currentVal).replace(/,/g, ""));
          if (Number.isFinite(parsed)) numericVal = parsed;
        }

        if (numericVal === undefined) {
          const path = apiToSchemaPath[apiFieldName];
          if (path) {
            const raw = getNested(formData, path);
            if (typeof raw === "number") {
              numericVal = raw;
            } else if (typeof raw === "string") {
              const parsed = Number(String(raw).replace(/,/g, ""));
              if (Number.isFinite(parsed)) numericVal = parsed;
            } else if (raw && typeof raw === "object" && "amount" in raw) {
              // Handle case where formData has nested structure but path didn't resolve correctly
              const amountValue = (raw as any).amount;
              if (typeof amountValue === "number") {
                numericVal = amountValue;
              } else if (typeof amountValue === "string") {
                const parsed = Number(String(amountValue).replace(/,/g, ""));
                if (Number.isFinite(parsed)) numericVal = parsed;
              }
            }
          }
          // Last resort: try to find the field directly in formData by schema field name
          if (numericVal === undefined && mapping) {
            for (const [schemaFieldName, def] of Object.entries(mapping)) {
              if (
                def &&
                typeof def === "object" &&
                (def as any).amount === apiFieldName
              ) {
                const schemaValue = formData[schemaFieldName];
                if (
                  schemaValue &&
                  typeof schemaValue === "object" &&
                  "amount" in schemaValue
                ) {
                  const amountValue = (schemaValue as any).amount;
                  if (typeof amountValue === "number") {
                    numericVal = amountValue;
                    break;
                  } else if (typeof amountValue === "string") {
                    const parsed = Number(
                      String(amountValue).replace(/,/g, "")
                    );
                    if (Number.isFinite(parsed)) {
                      numericVal = parsed;
                      break;
                    }
                  }
                }
              }
            }
          }
        }

        // Normalize to a monthly basis using the UI frequency when available so caps react to frequency changes
        const freqPath = frequencyPathByApiField[apiFieldName];
        const freqValue = freqPath ? getNested(formData, freqPath) : undefined;
        const monthlyVal = normalizeToMonthly(numericVal, freqValue);
        if (typeof monthlyVal === "number" && Number.isFinite(monthlyVal)) {
          numericVal = monthlyVal;
        }

        // handle TV License condition
        if (triggerKeyAliases.includes("TVLICENCE") || triggerKeyAliases.includes("TV_LICENCE")) {
          try {
            const tv = (formData as any)?.tvLicense;
            const raw = tv?.amount;
            if (typeof raw === "number") {
              numericVal = raw;
            } else if (typeof raw === "string") {
              const parsed = Number(String(raw).replace(/,/g, ""));
              if (Number.isFinite(parsed)) numericVal = parsed;
            }
          } catch {
            // ignore; leave numericVal as-is
          }
        }

        // Choose cap: joint vs single
        const capUpper =
          withPartner && typeof t?.upperLimitJoint === "number"
            ? t.upperLimitJoint
            : t?.upperLimit;

        // Dynamic CHILD_BENEFIT cap based on dependents if available
        let effectiveUpper: number | undefined = capUpper;
        if (triggerKeyAliases.includes("CHILD_BENEFIT")) {
          const eldest =
            typeof t?.eldestChild === "number" ? t.eldestChild : undefined;
          const additional =
            typeof t?.additionalChild === "number"
              ? t.additionalChild
              : undefined;
          const depU16 =
            Number((profileSnapshot as any)?.dependentChildrenUnder16 || 0) ||
            0;
          const dep16P =
            Number(
              (profileSnapshot as any)?.dependentChildrenAged16Plus || 0
            ) || 0;
          const numChildren = depU16 + dep16P;
          if (
            eldest !== undefined &&
            additional !== undefined &&
            numChildren > 0
          ) {
            effectiveUpper = eldest + Math.max(0, numChildren - 1) * additional;
          }
        }

        if (typeof effectiveUpper === "number") {
          triggerKeyAliases.forEach((k) => {
            rootMaxByField[k] = effectiveUpper;
            if (group === "income") incomeMaxByField[k] = effectiveUpper;
            if (group === "assets") assetsMaxByField[k] = effectiveUpper;
            if (group === "expense") expenseMaxByField[k] = effectiveUpper;
          });
        }

        // Compute exceeded (upper or lower bound)
        const belowLower =
          typeof t?.lowerLimit === "number" &&
          typeof numericVal === "number" &&
          Math.round(numericVal * 100) / 100 < Math.round(t.lowerLimit * 100) / 100;
        const aboveUpper =
          typeof effectiveUpper === "number" &&
          typeof numericVal === "number" &&
          Math.round(numericVal * 100) / 100 > Math.round(effectiveUpper * 100) / 100;
        let isExceeded = Boolean(belowLower || aboveUpper);

  

        if (isExceeded) {
          if (group === "income") {
            triggerKeyAliases.forEach((k) => {
              incomeExceeded[k] = true;
            });
          } else if (group === "assets") {
            triggerKeyAliases.forEach((k) => {
              assetsExceeded[k] = true;
            });
          } else if (group === "expense") {
            triggerKeyAliases.forEach((k) => {
              expenseExceeded[k] = true;
            });
          }
        }
      });
    } catch (e) {
      console.warn("Error computing triggers context:", e);
    }

    return {
      income: {
        exceeded: { ...incomeExceeded, ...assetsExceeded },
        maxByField: { ...incomeMaxByField, ...assetsMaxByField },
      },
      expense: { exceeded: expenseExceeded, maxByField: expenseMaxByField },
      maxByField: rootMaxByField,
    };
  }, [
    incomeTriggers,
    currentApiValues,
    toUpperSnake,
    upperSnakeToCamel,
    allFormData,
    profileSnapshot,
    incomeData,
  ]);

  // Evaluate section-level warnings for current section
  useEffect(() => {
    try {
      const templatesMap = Object.fromEntries(
        (promptTemplatesJson as any[]).map((t: any) => [t.id, t])
      );
      const varsMap = Object.fromEntries(
        (variableRegistryJson as any[]).map((v: any) => [v.id, v])
      );
      const ctx = {
        profile: profileSnapshot,
        income: currentApiValues.income,
        expense: currentApiValues.expense,
        assets: {},
        debts: {},
        triggers: triggersContext,
        section: {
          outer: selectedOuter,
          inner: selectedInner,
          hasApplicableQuestions: true,
        },
        liveValues: allFormData[`${selectedOuter}-${selectedInner}-form`] || {},
      } as any;
      const warnings = evaluateWarnings(
        ctx,
        templatesMap,
        varsMap,
        fieldWarningRulesAggregated as any[],
        { max: 5 }
      );
      const sectionOnly = warnings.filter(
        (w) =>
          (w.level ?? "field") === "section"
      );
      setSectionWarnings(sectionOnly);
    } catch (e) {
      console.warn("Failed to evaluate section warnings", e);
      setSectionWarnings([]);
    }
  }, [
    profileSnapshot,
    currentApiValues,
    triggersContext,
    selectedOuter,
    selectedInner,
    allFormData,
    warningsRefreshKey,
  ]);

  const bumpFormInstanceKey = useCallback((index: number) => {
    setFormInstanceKeys((prev) => ({
      ...prev,
      [index]: (prev[index] ?? 0) + 1,
    }));
  }, []);

  const highlightElement = useCallback((element: HTMLElement) => {
    if (!element) return;
    const highlightRoot =
      (element.closest('[data-field-root="true"]') as HTMLElement | null) ??
      element;
    highlightRoot.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });
    try {
      if (typeof (element as any).focus === "function") {
        (element as HTMLInputElement | HTMLTextAreaElement).focus({
          preventScroll: true,
        });
      }
    } catch {}
    highlightRoot.classList.add("warning-highlight");
    setTimeout(() => {
      highlightRoot.classList.remove("warning-highlight");
    }, 2200);
  }, []);

  const findFieldElement = useCallback(
    (fieldPath: string): HTMLElement | null => {
      if (typeof document === "undefined") return null;

      const selectors = [
        `input[name="${fieldPath}"]`,
        `textarea[name="${fieldPath}"]`,
        `select[name="${fieldPath}"]`,
        `[data-field-path="${fieldPath}"]`,
      ];

      for (const selector of selectors) {
        const el = document.querySelector<HTMLElement>(selector);
        if (el) return el;
      }

      return null;
    },
    []
  );

  const focusFieldByPath = useCallback(
    (fieldPath?: string) => {
      if (!fieldPath || typeof window === "undefined") return false;

      window.dispatchEvent(
        new CustomEvent("iehub:focus-field", { detail: { fieldPath } })
      );

      let attempts = 14;
      const attempt = () => {
        const element = findFieldElement(fieldPath);
        if (element) {
          highlightElement(element);
          return;
        }

        attempts -= 1;
        if (attempts > 0) {
          setTimeout(attempt, 120);
        }
      };

      setTimeout(attempt, 90);
      return true;
    },
    [findFieldElement, highlightElement]
  );

  const focusFieldFromAction = useCallback(
    (action: OpenActionSpec) => {
      if (!action || !action.outer) return false;

      const outerKey = action.outer;
      const supportedOuters = [
        "income",
        "assets",
        "fixed-expenses",
        "flexible-expenses",
        "debts",
      ];
      if (!supportedOuters.includes(outerKey)) {
        return false;
      }

      const innerList =
        innerRoutesConfig[outerKey as keyof typeof innerRoutesConfig] || [];
      const fallbackInner = innerList[0]?.key || "";
      const targetInner = action.inner || fallbackInner;

      // Only sync URL params when we are actually changing the in-memory selection.
      // If we’re already on the target outer/inner, updating search params causes a
      // refresh-like rerender (first-click “page refresh” feel) without any benefit.
      const shouldSyncUrlParams =
        outerKey !== selectedOuter || (targetInner && targetInner !== selectedInner);

      if (outerKey !== selectedOuter) {
        setSelectedOuter(outerKey);
      }

      if (targetInner && targetInner !== selectedInner) {
        setSelectedInner(targetInner);
      }

      try {
        const prevOuter = searchParams.get("outer");
        const prevInner = searchParams.get("inner");
        if (
          shouldSyncUrlParams &&
          (prevOuter !== outerKey || prevInner !== targetInner)
        ) {
          const nextParams = new URLSearchParams(searchParams.toString());
          nextParams.set("outer", outerKey);
          if (targetInner) {
            nextParams.set("inner", targetInner);
          }
          setSearchParams(nextParams);
        }
      } catch {}

      if (action.focus) {
        focusFieldByPath(action.focus);
      }

      return true;
    },
    [
      focusFieldByPath,
      innerRoutesConfig,
      searchParams,
      selectedInner,
      selectedOuter,
      setSearchParams,
      setSelectedInner,
      setSelectedOuter,
    ]
  );

  // Token click → open dialog
  const handleWarningDialog = useCallback(
    (action?: OpenActionSpec, _metadata?: { varId?: string }) => {
      if (!action) return false;

      if (action.type === "dialog") {
        const innerKey =
          typeof action.inner === "string" ? action.inner : undefined;

        if (
          action.outer === "profile" &&
          innerKey &&
          innerKey in PROFILE_DIALOG_STEP_MAP
        ) {
          if (openProfileWarningDialog(innerKey as ProfileDialogKey)) {
            return true;
          }
        }

        const targetStep = innerKey
          ? PROFILE_DIALOG_STEP_MAP[innerKey]
          : undefined;
        if (
          typeof targetStep === "number" &&
          targetStep >= 0 &&
          targetStep < incomeOnboardingSchemas.length
        ) {
          setCurrentOnboardingStep(targetStep);
          bumpFormInstanceKey(targetStep);
          setIsOnboardingDialogOpen(true);
          return true;
        }
      }

      if (
        action.outer &&
        [
          "income",
          "assets",
          "fixed-expenses",
          "flexible-expenses",
          "debts",
        ].includes(action.outer)
      ) {
        if (focusFieldFromAction(action)) {
          return true;
        }
      }

      return false;
    },
    [
      openProfileWarningDialog,
      bumpFormInstanceKey,
      setIsOnboardingDialogOpen,
      setCurrentOnboardingStep,
      focusFieldFromAction,
    ]
  );

  const handleSectionTokenClick = useCallback(
    async (token: WarningToken) => {
      try {
        await logButtonAction("Warning Var Click");
      } catch {}
      const spec = (variableRegistryJson as any[]).find(
        (v: any) => v.id === token.varId
      ) as VariableSpec | undefined;

      const action = token.openAction;
      if (handleWarningDialog(action, { varId: token.varId })) {
        return;
      }

      if (!action && spec?.openAction) {
        if (
          handleWarningDialog(spec.openAction as OpenActionSpec, {
            varId: token.varId,
          })
        ) {
          return;
        }
      }

      if (spec) {
        setVarSpec(spec);
        setIsVarDialogOpen(true);
      }
    },
    [handleWarningDialog, logButtonAction]
  );

  // Save handler for dialog
  const handleVarSave = useCallback(
    async (value: any, spec: VariableSpec) => {
      try {
        const customerId = resolveCustomerId();
        const userId = resolveUserId();
        if (!customerId || !userId) {
          showAlert({
            severity: "error",
            message: "Missing customer or user",
            autoHide: true,
            duration: 2500,
          });
          return;
        }
        const serviceRegistry: Record<string, any> = {
          BudgetService,
          OnboardingService,
        };
        const save = spec.saveApi;
        if (!save) {
          showAlert({
            severity: "warning",
            message: "Save not available for this field",
            autoHide: true,
            duration: 2500,
          });
          return;
        }
        const service = serviceRegistry[save.service];
        const method = service?.[save.method];
        if (typeof method !== "function") {
          showAlert({
            severity: "error",
            message: "Save method not found",
            autoHide: true,
            duration: 2500,
          });
          return;
        }

        const normalizeQuickEditValue = (
          raw: unknown,
          valueType: VariableSpec["valueType"]
        ): unknown => {
          if (valueType === "currency" || valueType === "number") {
            const s = typeof raw === "string" ? raw.trim() : String(raw ?? "");
            if (s.length === 0) return 0;
            const cleaned = s.replace(/[^0-9.-]/g, "");
            const n = Number(cleaned);
            if (!Number.isFinite(n)) {
              throw new Error("Please enter a valid number");
            }
            return n;
          }
          if (valueType === "boolean") {
            if (raw === true || raw === false) return raw;
            const s = typeof raw === "string" ? raw.trim().toLowerCase() : "";
            if (["y", "yes", "true", "1"].includes(s)) return true;
            if (["n", "no", "false", "0"].includes(s)) return false;
            throw new Error("Please enter a valid boolean value");
          }
          // Keep string/date as-is; backend coercion happens server-side today.
          return raw;
        };

        const normalizedValue = normalizeQuickEditValue(value, spec.valueType);

        // Build payload by substituting tokens
        const payload: Record<string, any> = {};
        Object.entries(save.payload || {}).forEach(([k, v]) => {
          if (typeof v === "string") {
            payload[k] = v
              .replace(/\$\{value\}/g, String(normalizedValue))
              .replace(/\$\{customerId\}/g, String(customerId))
              .replace(/\$\{userId\}/g, String(userId));
          } else {
            payload[k] = v;
          }
        });

        // Ensure the canonical backend field uses the correct runtime type.
        if (
          spec?.backendField &&
          (spec.valueType === "currency" || spec.valueType === "number")
        ) {
          payload[spec.backendField] = normalizedValue;
        }

        // For PUT endpoints, send the full record (not just the changed field).
        // Variable registry payloads are partial; we merge onto the latest API record.
        const nowIso = new Date().toISOString();
        let finalPayload: Record<string, any> = { ...payload };

        if (save.service === "BudgetService") {
          if (save.method === "updateIncomeData") {
            let base =
              ((latestIncomeRecordRef.current ??
                incomeData?.[0]) as unknown as Record<string, unknown> | undefined) ??
              undefined;
            if (!base || Object.keys(base).length === 0) {
              const fresh = await BudgetService.getIncomeData(customerId, {
                skipCache: true,
              });
              if (fresh?.success && Array.isArray(fresh.data) && fresh.data[0]) {
                base = fresh.data[0] as unknown as Record<string, unknown>;
                latestIncomeRecordRef.current = fresh.data[0] as IncomeDataRecord;
                setIncomeData(fresh.data);
              }
            }
            finalPayload = {
              ...(base || {}),
              ...payload,
              customerId: Number(customerId),
              lastUpdatedBy: Number(userId),
              lastUpdateDate: nowIso,
            };
          } else if (save.method === "updateExpenseData") {
            let base =
              ((latestExpenseRecordRef.current ??
                expenseData?.[0]) as unknown as Record<string, unknown> | undefined) ??
              undefined;
            if (!base || Object.keys(base).length === 0) {
              const fresh = await BudgetService.getExpenseData(customerId, {
                skipCache: true,
              });
              if (fresh?.success && Array.isArray(fresh.data) && fresh.data[0]) {
                base = fresh.data[0] as unknown as Record<string, unknown>;
                latestExpenseRecordRef.current = fresh.data[0] as ExpenseDataRecord;
                setExpenseData(fresh.data);
              }
            }
            finalPayload = {
              ...(base || {}),
              ...payload,
              customerId: Number(customerId),
              lastUpdatedBy: Number(userId),
              lastUpdateDate: nowIso,
            };
          } else if (save.method === "updateIncomeNotes") {
            let base =
              ((latestIncomeNoteRef.current ??
                incomeNotes?.[0]) as unknown as Record<string, unknown> | undefined) ??
              undefined;
            if (!base || Object.keys(base).length === 0) {
              const fresh = await BudgetService.getIncomeNotes(customerId, {
                skipCache: true,
              });
              if (fresh?.success && Array.isArray(fresh.data) && fresh.data[0]) {
                base = fresh.data[0] as unknown as Record<string, unknown>;
                latestIncomeNoteRef.current = fresh.data[0] as IncomeNoteData;
                setIncomeNotes(fresh.data);
              }
            }
            finalPayload = {
              ...(base || {}),
              ...payload,
              customerId: Number(customerId),
              lastUpdatedBy: Number(userId),
              lastUpdateDate: nowIso,
            };
          } else if (save.method === "updateExpenseNotes") {
            let base =
              ((latestExpenseNoteRef.current ??
                expenseNotes?.[0]) as unknown as Record<string, unknown> | undefined) ??
              undefined;
            if (!base || Object.keys(base).length === 0) {
              const fresh = await BudgetService.getExpenseNotes(customerId, {
                skipCache: true,
              });
              if (fresh?.success && Array.isArray(fresh.data) && fresh.data[0]) {
                base = fresh.data[0] as unknown as Record<string, unknown>;
                latestExpenseNoteRef.current = fresh.data[0] as ExpensesNoteData;
                setExpenseNotes(fresh.data);
              }
            }
            finalPayload = {
              ...(base || {}),
              ...payload,
              customerId: Number(customerId),
              lastUpdatedBy: Number(userId),
              lastUpdateDate: nowIso,
            };
          }
        }

        await method(finalPayload, { skipErrorCapture: false });

        // After a QuickEdit save, rehydrate BOTH API baseline state and the local form store.
        // This prevents navigation auto-save from merging stale "live" values back into PUTs.
        try {
          const targetOuter = spec?.outer ?? selectedOuter;
          const targetInner = spec?.inner ?? selectedInner;

          if (targetOuter !== "debts") {
            const targetFormId = `${targetOuter}-${targetInner}-form`;

            // Resolve category for mapping
            let category: keyof typeof BUDGET_FIELD_MAPPINGS = "salary";
            switch (targetOuter) {
              case "income":
                if (targetInner === "salary") category = "salary";
                else if (targetInner === "benefits") category = "benefits";
                else if (targetInner === "pension") category = "pension";
                else if (targetInner === "other-sources") category = "otherSources";
                break;
              case "assets":
                if (targetInner === "properties") category = "properties";
                else if (targetInner === "otherAssets") category = "otherAssets";
                break;
              case "fixed-expenses":
                if (targetInner === "household") category = "household";
                else if (targetInner === "utilities") category = "utilities";
                else if (targetInner === "care") category = "care";
                else if (targetInner === "transport") category = "transport";
                else if (targetInner === "school") category = "school";
                else if (targetInner === "pension-insurance")
                  category = "pensionInsurance";
                else if (targetInner === "professionals") category = "professionals";
                else if (targetInner === "other-sources") category = "otherSourcesFixed";
                break;
              case "flexible-expenses":
                if (targetInner === "leisure") category = "leisure";
                else if (targetInner === "housekeeping") category = "housekeeping";
                else if (targetInner === "personal-costs") category = "personalCosts";
                else if (targetInner === "savings") category = "savings";
                break;
            }

            const incomeAssetCategories = [
              "salary",
              "benefits",
              "pension",
              "otherSources",
              "properties",
              "otherAssets",
              "savings",
            ] as const;
            const isIncomeOrAsset = incomeAssetCategories.includes(
              category as (typeof incomeAssetCategories)[number]
            );

            if (isIncomeOrAsset) {
              const [freshIncome, freshIncomeNotes] = await Promise.all([
                BudgetService.getIncomeData(customerId, { skipCache: true }),
                BudgetService.getIncomeNotes(customerId, { skipCache: true }),
              ]);

              if (freshIncome.success && Array.isArray(freshIncome.data)) {
              latestIncomeRecordRef.current =
                (freshIncome.data[0] as IncomeDataRecord) ?? null;
                setIncomeData(freshIncome.data);
              }
              if (freshIncomeNotes.success && Array.isArray(freshIncomeNotes.data)) {
              latestIncomeNoteRef.current =
                (freshIncomeNotes.data[0] as IncomeNoteData) ?? null;
                setIncomeNotes(freshIncomeNotes.data);
              }

              const updatedIncomeRecord: IncomeDataRecord =
                freshIncome.success &&
                Array.isArray(freshIncome.data) &&
                freshIncome.data[0]
                  ? (freshIncome.data[0] as IncomeDataRecord)
                  : ((incomeData?.[0] as IncomeDataRecord) ||
                    ({} as unknown as IncomeDataRecord));

              const updatedIncomeNotesRecord: IncomeNoteData =
                freshIncomeNotes.success &&
                Array.isArray(freshIncomeNotes.data) &&
                freshIncomeNotes.data[0]
                  ? (freshIncomeNotes.data[0] as IncomeNoteData)
                  : ((incomeNotes?.[0] as IncomeNoteData) ||
                    ({} as unknown as IncomeNoteData));

              const refreshedFormData = mapIncomeDataToSchema(
                updatedIncomeRecord,
                updatedIncomeNotesRecord as unknown as Parameters<
                  typeof mapIncomeDataToSchema
                >[1],
                category
              ) as unknown as Record<string, unknown>;

              setFormData(targetFormId, refreshedFormData);

              if (targetOuter === selectedOuter && targetInner === selectedInner) {
                const schemaFields = getSchemaFieldNames(currentSchema);
                const nextLive: Record<string, unknown> = {};
                schemaFields.forEach((path) => {
                  const v = getNested(refreshedFormData as any, path);
                  if (v !== undefined) {
                    nextLive[path] = v;
                  }
                });
                liveBudgetFormValuesRef.current = nextLive;
                setBudgetFormRehydrateNonce((n) => n + 1);
              }
            } else {
              const [freshExpense, freshExpenseNotes] = await Promise.all([
                BudgetService.getExpenseData(customerId, { skipCache: true }),
                BudgetService.getExpenseNotes(customerId, { skipCache: true }),
              ]);

              if (freshExpense.success && Array.isArray(freshExpense.data)) {
              latestExpenseRecordRef.current =
                (freshExpense.data[0] as ExpenseDataRecord) ?? null;
                setExpenseData(freshExpense.data);
              }
              if (freshExpenseNotes.success && Array.isArray(freshExpenseNotes.data)) {
              latestExpenseNoteRef.current =
                (freshExpenseNotes.data[0] as ExpensesNoteData) ?? null;
                setExpenseNotes(freshExpenseNotes.data);
              }

              const updatedExpenseRecord: ExpenseDataRecord =
                freshExpense.success &&
                Array.isArray(freshExpense.data) &&
                freshExpense.data[0]
                  ? (freshExpense.data[0] as ExpenseDataRecord)
                  : ((expenseData?.[0] as ExpenseDataRecord) ||
                    ({} as unknown as ExpenseDataRecord));

              const updatedExpenseNotesRecord: ExpensesNoteData =
                freshExpenseNotes.success &&
                Array.isArray(freshExpenseNotes.data) &&
                freshExpenseNotes.data[0]
                  ? (freshExpenseNotes.data[0] as ExpensesNoteData)
                  : ((expenseNotes?.[0] as ExpensesNoteData) ||
                    ({} as unknown as ExpensesNoteData));

              const refreshedFormData = mapExpenseDataToSchema(
                updatedExpenseRecord,
                updatedExpenseNotesRecord as unknown as Parameters<
                  typeof mapExpenseDataToSchema
                >[1],
                category
              ) as unknown as Record<string, unknown>;

              setFormData(targetFormId, refreshedFormData);

              if (targetOuter === selectedOuter && targetInner === selectedInner) {
                const schemaFields = getSchemaFieldNames(currentSchema);
                const nextLive: Record<string, unknown> = {};
                schemaFields.forEach((path) => {
                  const v = getNested(refreshedFormData as any, path);
                  if (v !== undefined) {
                    nextLive[path] = v;
                  }
                });
                liveBudgetFormValuesRef.current = nextLive;
                setBudgetFormRehydrateNonce((n) => n + 1);
              }
            }
          }
        } catch {
          // Non-blocking: quick edit succeeded even if refresh/re-hydrate fails
        }
        showAlert({
          severity: "success",
          message: "Saved successfully",
          autoHide: true,
          duration: 2000,
        });
        setIsVarDialogOpen(false);
        setVarSpec(null);
        // Ensure any warnings that reference this value are recomputed.
        setWarningsRefreshKey((prev) => prev + 1);
      } catch (e) {
        console.error("Warning field save failed", e);
        showAlert({
          severity: "error",
          message: "Save failed. Please try again.",
          autoHide: true,
          duration: 3000,
        });
      }
    },
    [
      resolveCustomerId,
      resolveUserId,
      showAlert,
      setWarningsRefreshKey,
      selectedOuter,
      selectedInner,
      incomeData,
      expenseData,
      incomeNotes,
      expenseNotes,
      setIncomeData,
      setExpenseData,
      setIncomeNotes,
      setExpenseNotes,
      setFormData,
      setBudgetFormRehydrateNonce,
      getSchemaFieldNames,
      getNested,
      currentSchema,
    ]
  );

  // Track last saved snapshots (for diagnostics / potential change detection)
  const lastSavedSnapshotRef = useRef<Record<string, string>>({});
  const lastSavedAtRef = useRef<Record<string, number>>({});

  const stableStringify = useCallback((value: unknown): string => {
    const seen = new WeakSet();
    const sort = (v: any): any => {
      if (v === null || typeof v !== "object") return v;
      if (seen.has(v)) return undefined;
      seen.add(v);
      if (Array.isArray(v)) return v.map(sort);
      const out: Record<string, any> = {};
      Object.keys(v)
        .sort()
        .forEach((k) => {
          const vv = (v as any)[k];
          const sv = sort(vv);
          if (sv !== undefined) out[k] = sv;
        });
      return out;
    };
    try {
      return JSON.stringify(sort(value));
    } catch {
      return "";
    }
  }, []);

  const normalizeScalarForDiff = useCallback((v: unknown): unknown => {
    // Treat null/undefined/empty-string as equivalent for "no-change" detection.
    // Empty strings arise when the form injects "" for cleared optional fields
    // (e.g. gasSupplierOther) while the API returns undefined/null.
    if (v === undefined || v === null || v === '') return null;
    if (typeof v === "number") return Number.isFinite(v) ? v : v;
    if (typeof v === "string") {
      // Allow numeric string ↔ number equivalence to avoid spurious PUTs
      const trimmed = v.trim();
      if (trimmed.length > 0 && /^-?\d+(\.\d+)?$/.test(trimmed)) {
        const n = Number(trimmed);
        return Number.isFinite(n) ? n : v;
      }
      return v;
    }
    return v;
  }, []);

  const isValueEqualForDiff = useCallback(
    (a: unknown, b: unknown): boolean => {
      const na = normalizeScalarForDiff(a);
      const nb = normalizeScalarForDiff(b);

      if (typeof na === "number" && typeof nb === "number") {
        return Math.abs(na - nb) < 1e-9;
      }

      // For primitives (including strings), Object.is is fine.
      const aIsObj = na !== null && typeof na === "object";
      const bIsObj = nb !== null && typeof nb === "object";
      if (!aIsObj && !bIsObj) {
        return Object.is(na, nb);
      }

      // For objects/arrays, rely on stable canonical JSON.
      return stableStringify(na) === stableStringify(nb);
    },
    [normalizeScalarForDiff, stableStringify]
  );

  const hasMeaningfulDiff = useCallback(
    (
      nextRecord: Record<string, unknown>,
      baseRecord: Record<string, unknown> | undefined | null,
      ignoreKeys: Set<string> = new Set()
    ): boolean => {
      const base = (baseRecord ?? {}) as Record<string, unknown>;
      for (const key of Object.keys(nextRecord)) {
        if (ignoreKeys.has(key)) continue;
        if (!isValueEqualForDiff((nextRecord as any)[key], (base as any)[key])) {
          return true;
        }
      }
      return false;
    },
    [isValueEqualForDiff]
  );

  const hasNotesDiff = useCallback(
    (
      mappedNotes: Record<string, string>,
      baseNote: Record<string, unknown> | undefined | null
    ): boolean => {
      const base = (baseNote ?? {}) as Record<string, unknown>;
      for (const key of Object.keys(mappedNotes)) {
        const nextVal = mappedNotes[key] ?? "";
        const prevRaw = (base as any)[key];
        const prevVal =
          prevRaw === null || prevRaw === undefined ? "" : String(prevRaw);
        if (prevVal !== nextVal) return true;
      }
      return false;
    },
    []
  );

  // Navigation helpers and URL sync
  const getInnerIndex = useCallback(() => {
    return currentInnerRoutes.findIndex((r) => r.key === selectedInner);
  }, [currentInnerRoutes, selectedInner]);

  const navigateToInner = useCallback(
    async (key: string) => {
      // Skip if already on the same inner route
      if (key === selectedInner) return;
      
      try {
        setIsNavigating(true);
        // Auto-save current section before navigating away
        try {
          // Use ref to access latest saveBudgetData (avoids stale closure)
          if (saveBudgetDataRef.current) {
            await saveBudgetDataRef.current({ source: "auto" });
          }
        } catch {
          // If save fails, stay on current section (error toast already shown)
          setIsNavigating(false);
          return;
        }
        // Batch state updates together
        setSelectedInner(key);
        searchParams.set("outer", selectedOuter);
        searchParams.set("inner", key);
        setSearchParams(searchParams);
        // Scroll to top instantly to avoid conflicting with CSS transitions
        if (typeof window !== "undefined") {
          requestAnimationFrame(() => {
            window.scrollTo({ top: 0, behavior: "instant" });
          });
        }
      } finally {
        // Shorter delay - CSS transitions handle the visual smoothness
        requestAnimationFrame(() => {
          setTimeout(() => setIsNavigating(false), 100);
        });
      }
    },
    [selectedOuter, selectedInner, searchParams, setSearchParams]
  );

  const navigateToOuter = useCallback(
    async (outerKey: string) => {
      // Skip if already on the same outer route
      if (outerKey === selectedOuter) return;
      
      try {
        setIsNavigating(true);
        // Auto-save current section before navigating away
        try {
          // Use ref to access latest saveBudgetData (avoids stale closure)
          if (saveBudgetDataRef.current) {
            await saveBudgetDataRef.current({ source: "auto" });
          }
        } catch {
          // If save fails, stay on current section (error toast already shown)
          setIsNavigating(false);
          return;
        }
        // Batch state updates together
        const firstInner =
          (innerRoutesConfig[outerKey as keyof typeof innerRoutesConfig] || [])[0]
            ?.key || "";
        setSelectedOuter(outerKey);
        setSelectedInner(firstInner);
        searchParams.set("outer", outerKey);
        searchParams.set("inner", firstInner);
        setSearchParams(searchParams);
        // Scroll to top instantly to avoid conflicting with CSS transitions
        if (typeof window !== "undefined") {
          requestAnimationFrame(() => {
            window.scrollTo({ top: 0, behavior: "instant" });
          });
        }
      } finally {
        // Shorter delay - CSS transitions handle the visual smoothness
        requestAnimationFrame(() => {
          setTimeout(() => setIsNavigating(false), 100);
        });
      }
    },
    [
      innerRoutesConfig,
      selectedOuter,
      searchParams,
      setSearchParams,
    ]
  );

  // Handle outer route change
  const handleOuterRouteChange = async (routeKey: string) => {
    // Log the outer navigation action
    await logButtonAction("Outer Navigation");
    await navigateToOuter(routeKey);
  };

  // Menu handlers
  const handleInnerNavMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setInnerNavMenuAnchor(event.currentTarget);
  };

  const handleInnerNavMenuClose = () => {
    setInnerNavMenuAnchor(null);
  };

  const handleInnerNavMenuItemClick = async (routeKey: string) => {
    // Log the inner navigation action
    await logButtonAction("Inner Navigation");

    await navigateToInner(routeKey);
    handleInnerNavMenuClose();
  };

  const handleBackNav = useCallback(async () => {
    const idx = getInnerIndex();
    if (idx > 0) {
      await logButtonAction("Back");
      const prevRoute = currentInnerRoutes[idx - 1];
      if (prevRoute) await navigateToInner(prevRoute.key);
    } else {
      // Leaving the budget page altogether – auto-save before navigating back
      try {
        // Use ref to access latest saveBudgetData (avoids stale closure)
        if (saveBudgetDataRef.current) {
          await saveBudgetDataRef.current({ source: "auto" });
        }
      } catch {
        // If save fails, stay on the page (error toast already shown)
        return;
      }
      navigate(-1);
    }
  }, [
    getInnerIndex,
    logButtonAction,
    navigateToInner,
    currentInnerRoutes,
    navigate,
  ]);

  // Onboarding Dialog Logic
  const totalOnboardingSteps = incomeOnboardingSchemas.length;

  const currentOnboardingSchema: ScreenSchema = useMemo(() => {
    return incomeOnboardingSchemas[currentOnboardingStep] as ScreenSchema;
  }, [currentOnboardingStep]);

  const screenRenderKey = `${currentOnboardingStep}-${formInstanceKeys[currentOnboardingStep] ?? 0}`;

  // Ensure step 3 (Dependents) can compute total household size immediately using
  // the partner answer from step 2, even if step-3 form data hasn't been written yet.
  const onboardingDialogFormData = useMemo((): Record<string, unknown> => {
    const currentKey = `income-onboarding-step-${currentOnboardingStep}`;
    const currentRaw = allFormData[currentKey];
    const current = isPlainObject(currentRaw) ? currentRaw : {};

    if (currentOnboardingStep !== 3) {
      return current;
    }

    const step2Raw = allFormData["income-onboarding-step-2"];
    const step2 = isPlainObject(step2Raw) ? step2Raw : {};

    const budgetWithPartner =
      current.budgetWithPartner !== undefined
        ? current.budgetWithPartner
        : step2.budgetWithPartner;

    const jointAccount =
      typeof current.jointAccount === "string"
        ? current.jointAccount
        : typeof step2.jointAccount === "string"
          ? step2.jointAccount
          : typeof budgetWithPartner === "boolean"
            ? budgetWithPartner
              ? "Y"
              : "N"
            : undefined;

    return {
      ...current,
      ...(budgetWithPartner !== undefined ? { budgetWithPartner } : {}),
      ...(jointAccount !== undefined ? { jointAccount } : {}),
    };
  }, [allFormData, currentOnboardingStep]);

  const formHasNoFields = useMemo(() => {
    try {
      return (
        (currentOnboardingSchema.sections || []).reduce(
          (sum, sec) => sum + (sec.fields || []).length,
          0
        ) === 0
      );
    } catch {
      return false;
    }
  }, [currentOnboardingSchema]);

  const actionsConfig = currentOnboardingSchema.actions;
  const buttonsConfig = actionsConfig?.buttons;

  const computedPrimaryText = useMemo(() => {
    const defaultText =
      currentOnboardingStep === totalOnboardingSteps - 1
        ? "Complete"
        : "Continue";
    return (
      buttonsConfig?.primaryText || actionsConfig?.nextButtonText || defaultText
    );
  }, [
    actionsConfig,
    buttonsConfig,
    currentOnboardingStep,
    totalOnboardingSteps,
  ]);

  const shouldShowSecondary = useMemo(() => {
    return Boolean(
      (buttonsConfig?.showSecondaryWhenNoFields && formHasNoFields) ||
        buttonsConfig?.showSecondary
    );
  }, [buttonsConfig, formHasNoFields]);

  const secondaryButtonText = useMemo(() => {
    return buttonsConfig?.secondaryText || "Skip & continue";
  }, [buttonsConfig]);

  // Persist field stage helper — returns a promise so critical call sites can await it
  const persistFieldStage = useCallback(
    (stageIndex: number): Promise<void> => {
      const customerId = fieldStageCustomerIdRef.current;
      // debug removed

      if (!customerId || stageIndex < 0) {
        // debug removed
        return Promise.resolve();
      }

      // Add offset to continue from main onboarding step count
      const offsetStageIndex = stageIndex + INCOME_ONBOARDING_STEP_OFFSET;
      const stageStr = String(offsetStageIndex);

      if (lastPersistedStageRef.current === stageStr) {
        // debug removed
        return Promise.resolve();
      }

      if (lastPersistedStageRef.current === stageStr) {
        // debug removed
        return Promise.resolve();
      }

      return OnboardingService.upsertCustomerFieldStage(
        {
          customerId,
          fieldStage: stageStr,
          userId: resolveUserId() as number,
        },
        { skipErrorCapture: true }
      )
        .then((response) => {
          // debug removed

          // Response is CustomerFieldStageRecord or null
          if (response && typeof response === "object") {
            fieldStageRecordIdRef.current =
              typeof response.id === "number"
                ? response.id
                : fieldStageRecordIdRef.current;
            lastPersistedStageRef.current = stageStr;
          } else {
            // Even if response is null/undefined, update lastPersisted to prevent duplicate calls
            lastPersistedStageRef.current = stageStr;
          }
        })
        .catch(() => {});
    },
    [resolveUserId]
  );

  // Ensure we have a customer id available before persisting stage
  const ensureFieldStageCustomerId = useCallback(async (): Promise<
    number | null
  > => {
    const existing = fieldStageCustomerIdRef.current;
    if (typeof existing === "number" && existing > 0) {
      return existing;
    }
    try {
      const customerDataStr = localStorage.getItem("customerData");
      if (customerDataStr) {
        const obj = JSON.parse(customerDataStr) as Record<
          string,
          unknown
        > | null;
        const maybeId: unknown = obj
          ? (obj["customerId"] ?? obj["id"])
          : undefined;
        const parsedId = typeof maybeId === "number" ? maybeId : null;
        if (parsedId && parsedId > 0) {
          fieldStageCustomerIdRef.current = parsedId;
          return parsedId;
        }
      }
    } catch {
      // ignore
    }
    const userId = resolveUserId();
    if (typeof userId === "number" && userId > 0) {
      try {
        const resp = await OnboardingService.getCustomerByUserId(userId, {
          skipErrorCapture: true,
        });
        const data = resp?.data as Record<string, unknown> | undefined;
        const maybeId: unknown = data
          ? (data["customerId"] ?? data["id"])
          : undefined;
        const parsedId = typeof maybeId === "number" ? maybeId : null;
        if (parsedId && parsedId > 0) {
          fieldStageCustomerIdRef.current = parsedId;
          try {
            localStorage.setItem("customerData", JSON.stringify(data));
          } catch {
            /* ignore */
          }
          return parsedId;
        }
      } catch {
        // ignore
      }
    }
    return null;
  }, [resolveUserId]);

  const isRecord = (value: unknown): value is Record<string, unknown> => {
    return typeof value === "object" && value !== null && !Array.isArray(value);
  };

  const getStringFromRecord = (
    obj: Record<string, unknown>,
    key: string
  ): string | undefined => {
    const v = obj[key];
    return typeof v === "string" ? v : undefined;
  };

  const coerceToInt = (value: unknown): number | undefined => {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string") {
      const n = parseInt(value, 10);
      return Number.isNaN(n) ? undefined : n;
    }
    return undefined;
  };

  const computeAgeSafe = (dobStr: string | undefined): number | undefined => {
    if (!dobStr) return undefined;
    try {
      const age = calculateAge(dobStr);
      return typeof age === "number" && Number.isFinite(age) ? age : undefined;
    } catch {
      return undefined;
    }
  };

  const fetchBudgetSnapshotsFresh = async (
    customerId: number
  ): Promise<BudgetSnapshots> => {
    try {
      const [incomeResp, incomeNotesResp, expenseResp, expenseNotesResp] =
        await Promise.all([
          BudgetService.getIncomeData(customerId, { skipCache: true }),
          BudgetService.getIncomeNotes(customerId, { skipCache: true }),
          BudgetService.getExpenseData(customerId, { skipCache: true }),
          BudgetService.getExpenseNotes(customerId, { skipCache: true }),
        ]);

      const income =
        incomeResp?.success && Array.isArray(incomeResp.data) && incomeResp.data[0]
          ? incomeResp.data[0]
          : null;
      const incomeNotes =
        incomeNotesResp?.success &&
        Array.isArray(incomeNotesResp.data) &&
        incomeNotesResp.data[0]
          ? incomeNotesResp.data[0]
          : null;
      const expenses =
        expenseResp?.success && Array.isArray(expenseResp.data) && expenseResp.data[0]
          ? expenseResp.data[0]
          : null;
      const expenseNotes =
        expenseNotesResp?.success &&
        Array.isArray(expenseNotesResp.data) &&
        expenseNotesResp.data[0]
          ? expenseNotesResp.data[0]
          : null;

      return { income, incomeNotes, expenses, expenseNotes };
    } catch {
      return { income: null, incomeNotes: null, expenses: null, expenseNotes: null };
    }
  };

  const buildProfileBudgetContext = (
    overrides: Partial<ProfileBudgetContext> = {}
  ): ProfileBudgetContext => {
    const storedUnknown: unknown = readStoredCustomerData();
    const stored = isRecord(storedUnknown) ? storedUnknown : {};

    const storedCustomerEmploymentStatus = getStringFromRecord(
      stored,
      "customerEmploymentStatus"
    );
    const storedPartnerEmploymentStatus = getStringFromRecord(
      stored,
      "partnerEmploymentStatus"
    );
    const storedJointAccount = getStringFromRecord(stored, "jointAccount");
    const storedAccommodationType =
      getStringFromRecord(stored, "housingTenure") ??
      getStringFromRecord(stored, "accommodationType");
    const storedHaveChildrenUnder18 = getStringFromRecord(stored, "haveChildrenUnder18");

    const storedDependentChildrenUnder16 = coerceToInt(stored["dependentChildrenUnder16"]);
    const storedDependentChildrenAged16Plus = coerceToInt(stored["dependentChildrenAged16Plus"]);

    const customerDob =
      getStringFromRecord(stored, "debtor1DateOfBirthStr") ??
      getStringFromRecord(stored, "dateOfBirth") ??
      getStringFromRecord(stored, "debtor1DateOfBirth");
    const partnerDob =
      getStringFromRecord(stored, "debtor2DateOfBirthStr") ??
      getStringFromRecord(stored, "debtor2DateOfBirth") ??
      getStringFromRecord(stored, "partnerDateOfBirth");

    const customerAge =
      typeof customerMetadata.customerAge === "number"
        ? customerMetadata.customerAge
        : computeAgeSafe(customerDob);
    const partnerAge =
      typeof customerMetadata.partnerAge === "number"
        ? customerMetadata.partnerAge
        : computeAgeSafe(partnerDob);

    const ctx: ProfileBudgetContext = {};

    const customerEmploymentStatus =
      customerMetadata.customerEmploymentStatus ?? storedCustomerEmploymentStatus;
    if (typeof customerEmploymentStatus === "string") {
      ctx.customerEmploymentStatus = customerEmploymentStatus;
    }

    const partnerEmploymentStatus =
      customerMetadata.partnerEmploymentStatus ?? storedPartnerEmploymentStatus;
    if (typeof partnerEmploymentStatus === "string") {
      ctx.partnerEmploymentStatus = partnerEmploymentStatus;
    }

    const jointAccount = customerMetadata.jointAccount ?? storedJointAccount;
    if (typeof jointAccount === "string" || typeof jointAccount === "boolean") {
      ctx.jointAccount = jointAccount;
    }

    const accommodationType = customerMetadata.accommodationType ?? storedAccommodationType;
    if (typeof accommodationType === "string") {
      ctx.accommodationType = accommodationType;
    }

    const dependentChildrenUnder16 =
      typeof customerMetadata.dependentChildrenUnder16 === "number"
        ? customerMetadata.dependentChildrenUnder16
        : storedDependentChildrenUnder16;
    if (typeof dependentChildrenUnder16 === "number") {
      ctx.dependentChildrenUnder16 = dependentChildrenUnder16;
    }

    const dependentChildrenAged16Plus =
      typeof customerMetadata.dependentChildrenAged16Plus === "number"
        ? customerMetadata.dependentChildrenAged16Plus
        : storedDependentChildrenAged16Plus;
    if (typeof dependentChildrenAged16Plus === "number") {
      ctx.dependentChildrenAged16Plus = dependentChildrenAged16Plus;
    }

    const haveChildrenUnder18 =
      customerMetadata.haveChildrenUnder18 ?? storedHaveChildrenUnder18;
    if (typeof haveChildrenUnder18 === "string") {
      ctx.haveChildrenUnder18 = haveChildrenUnder18;
    }

    if (typeof customerAge === "number") {
      ctx.customerAge = customerAge;
    }
    if (typeof partnerAge === "number") {
      ctx.partnerAge = partnerAge;
    }

    // Apply overrides last (but do not assign explicit undefined under exactOptionalPropertyTypes)
    if (typeof overrides.customerEmploymentStatus === "string") {
      ctx.customerEmploymentStatus = overrides.customerEmploymentStatus;
    }
    if (typeof overrides.partnerEmploymentStatus === "string") {
      ctx.partnerEmploymentStatus = overrides.partnerEmploymentStatus;
    }
    if (typeof overrides.jointAccount === "string" || typeof overrides.jointAccount === "boolean") {
      ctx.jointAccount = overrides.jointAccount;
    }
    if (typeof overrides.accommodationType === "string") {
      ctx.accommodationType = overrides.accommodationType;
    }
    if (typeof overrides.dependentChildrenUnder16 === "number") {
      ctx.dependentChildrenUnder16 = overrides.dependentChildrenUnder16;
    }
    if (typeof overrides.dependentChildrenAged16Plus === "number") {
      ctx.dependentChildrenAged16Plus = overrides.dependentChildrenAged16Plus;
    }
    if (typeof overrides.haveChildrenUnder18 === "string") {
      ctx.haveChildrenUnder18 = overrides.haveChildrenUnder18;
    }
    if (typeof overrides.customerAge === "number") {
      ctx.customerAge = overrides.customerAge;
    }
    if (typeof overrides.partnerAge === "number") {
      ctx.partnerAge = overrides.partnerAge;
    }

    return ctx;
  };

  const handleOnboardingFormSubmit = async (data: unknown) => {
    const formData = data as Record<string, unknown>;
    const formId = `income-onboarding-step-${currentOnboardingStep}`;

    setFormData(formId, formData);

    const customerIdRaw = resolveCustomerId();
    const customerId =
      typeof customerIdRaw === "number"
        ? customerIdRaw
        : typeof customerIdRaw === "string"
          ? parseInt(customerIdRaw, 10)
          : null;
    const userIdRaw = resolveUserId();
    const userId =
      typeof userIdRaw === "number"
        ? userIdRaw
        : typeof userIdRaw === "string"
          ? parseInt(userIdRaw, 10)
          : null;

    // Map form data to API payload based on current step (do not call APIs here)
    const payload: Record<string, unknown> = {};

    let persistDependentsDobs: null | (() => Promise<void>) = null;

    switch (currentOnboardingStep) {
      case 0: // Accommodation Type
        if (typeof formData.accomodationType === "string" && formData.accomodationType) {
          payload.housingTenure = formData.accomodationType;
        }
        break;

      case 1: // Employment Status
        if (
          typeof formData.customerEmploymentStatus === "string" &&
          formData.customerEmploymentStatus
        ) {
          payload.customerEmploymentStatus = formData.customerEmploymentStatus;
        }
        break;

      case 2: // Budget with Partner
        if (formData.budgetWithPartner !== undefined) {
          payload.jointAccount = formData.budgetWithPartner === true ? "Y" : "N";

          if (formData.budgetWithPartner === true) {
            if (typeof formData.name === "string" && formData.name) payload.debtor2Name = formData.name;
            if (formData.dob) {
              const dobDate =
                formData.dob instanceof Date
                  ? formData.dob
                  : new Date(formData.dob as Date | string);
              if (!Number.isNaN(dobDate.getTime())) {
                const year = dobDate.getFullYear();
                const month = String(dobDate.getMonth() + 1).padStart(2, "0");
                const day = String(dobDate.getDate()).padStart(2, "0");
                const dobString = `${year}-${month}-${day}`;
                payload.debtor2DateOfBirthStr = dobString;
                payload.debtor2DateOfBirth = dobString;
              }
            }
            if (typeof formData.employmentStatus === "string" && formData.employmentStatus) {
              payload.partnerEmploymentStatus = formData.employmentStatus;
            }
          }
        }
        break;

      case 3: // Dependents
        if (formData.haveDependents !== undefined) {
          const hasDependents = formData.haveDependents === true;
          payload.haveChildrenUnder18 = hasDependents ? "Y" : "N";

          if (hasDependents) {
            if (formData.under16Dependents !== undefined)
              payload.dependentChildrenUnder16 = formData.under16Dependents;
            if (formData.between16to18Dependents !== undefined)
              payload.dependentChildrenAged16Plus = formData.between16to18Dependents;
            if (formData.otherAdultsOver18 !== undefined) {
              // adultsOver18IncludingMyself is actually other adults excluding customer and partner
              payload.adultsOver18IncludingMyself = formData.otherAdultsOver18;
            }
          } else {
            // Keep profile counters consistent when switching to "No"
            payload.dependentChildrenUnder16 = 0;
            payload.dependentChildrenAged16Plus = 0;
            payload.adultsOver18IncludingMyself = 0;
          }

          // Defer dependent DOB API updates until after confirmation and profile patch
          if (hasDependents && typeof customerId === "number" && typeof userId === "number") {
            persistDependentsDobs = async () => {
              try {
                const existingDependents = dependentsDobData || [];

                const under16Count =
                  typeof formData.under16Dependents === "number"
                    ? formData.under16Dependents
                    : 0;
                for (let i = 1; i <= under16Count; i += 1) {
                  const dobFieldName = `under16DependentsDOB_${i}`;
                  const dobValue = formData[dobFieldName as keyof typeof formData];
                  if (!dobValue) continue;

                  let dobDate: Date | null = null;
                  if (dobValue instanceof Date) dobDate = dobValue;
                  else if (typeof dobValue === "string" || typeof dobValue === "number")
                    dobDate = new Date(dobValue);

                  if (dobDate instanceof Date && !Number.isNaN(dobDate.getTime())) {
                    const year = dobDate.getFullYear();
                    const month = String(dobDate.getMonth() + 1).padStart(2, "0");
                    const day = String(dobDate.getDate()).padStart(2, "0");
                    const dobStr = `${year}-${month}-${day}`;

                    const existingUnder16 = existingDependents.find(
                      (dep) =>
                        dep.dependantCategory === "age-under16" &&
                        dep.childDependantNo === String(i)
                    );

                    if (existingUnder16 && existingUnder16.id) {
                      await DependentsService.updateDependent({
                        id: existingUnder16.id,
                        customerId: existingUnder16.customerId,
                        childDependantDob: dobStr,
                        childDependantDobStr: dobStr,
                        childDependantNo: String(i),
                        dependantCategory: "age-under16",
                        lastUpdatedBy: Number(userId),
                        lastUpdateDate: new Date().toISOString(),
                      });
                    } else {
                      await DependentsService.createDependent({
                        customerId: Number(customerId),
                        userId: Number(userId),
                        childDependantDob: dobStr,
                        childDependantDobStr: dobStr,
                        childDependantNo: String(i),
                        dependantCategory: "age-under16",
                        createdBy: Number(userId),
                      });
                    }
                  }
                }

                const deletableUnder16 = existingDependents.filter(
                  (dep) =>
                    dep.dependantCategory === "age-under16" &&
                    Number(dep.childDependantNo) > under16Count
                );
                for (const dep of deletableUnder16) {
                  if (dep.id) {
                    await DependentsService.deleteDependent(customerId, dep.id);
                  }
                }

                const between16to18Count =
                  typeof formData.between16to18Dependents === "number"
                    ? formData.between16to18Dependents
                    : 0;
                for (let i = 1; i <= between16to18Count; i += 1) {
                  const dobFieldName = `between16to18DependentsDOB_${i}`;
                  const dobValue = formData[dobFieldName as keyof typeof formData];
                  if (!dobValue) continue;

                  let dobDate: Date | null = null;
                  if (dobValue instanceof Date) dobDate = dobValue;
                  else if (typeof dobValue === "string" || typeof dobValue === "number")
                    dobDate = new Date(dobValue);

                  if (dobDate instanceof Date && !Number.isNaN(dobDate.getTime())) {
                    const year = dobDate.getFullYear();
                    const month = String(dobDate.getMonth() + 1).padStart(2, "0");
                    const day = String(dobDate.getDate()).padStart(2, "0");
                    const dobStr = `${year}-${month}-${day}`;

                    const existing16to18 = existingDependents.find(
                      (dep) =>
                        dep.dependantCategory === "age-16to18" &&
                        dep.childDependantNo === String(i)
                    );

                    if (existing16to18 && existing16to18.id) {
                      await DependentsService.updateDependent({
                        id: existing16to18.id,
                        customerId: existing16to18.customerId,
                        childDependantDob: dobStr,
                        childDependantDobStr: dobStr,
                        childDependantNo: String(i),
                        dependantCategory: "age-16to18",
                        lastUpdatedBy: Number(userId),
                        lastUpdateDate: new Date().toISOString(),
                      });
                    } else {
                      await DependentsService.createDependent({
                        customerId: Number(customerId),
                        userId: Number(userId),
                        childDependantDob: dobStr,
                        childDependantDobStr: dobStr,
                        childDependantNo: String(i),
                        dependantCategory: "age-16to18",
                        createdBy: Number(userId),
                      });
                    }
                  }
                }

                const deletable16to18 = existingDependents.filter(
                  (dep) =>
                    dep.dependantCategory === "age-16to18" &&
                    Number(dep.childDependantNo) > between16to18Count
                );
                for (const dep of deletable16to18) {
                  if (dep.id) {
                    await DependentsService.deleteDependent(customerId, dep.id);
                  }
                }
              } catch (error) {
                console.error("Failed to save dependent DOBs:", error);
              }
            };
          }
        }
        break;

      case 4: // Vehicle Details
        if (formData.haveVehicle !== undefined) {
          payload.haveVehicle = formData.haveVehicle === true ? "Y" : "N";
          if (formData.haveVehicle === true && formData.noOfVehiclesInHouseholds !== undefined) {
            payload.noOfVehiclesInHouseholds = formData.noOfVehiclesInHouseholds;
          } else if (formData.haveVehicle === false) {
            // Persist the counter reset when toggling to "No" so backend/red-warnings don't keep old values.
            payload.noOfVehiclesInHouseholds = 0;
          }
        }
        break;
      default:
        break;
    }

    const doSave = async () => {
      try {
        setLoading("income-onboarding-submit", true);

        if (Object.keys(payload).length > 0) {
          if (typeof customerId === "number" && Number.isFinite(customerId)) {
            const response = await ProfileService.patchCustomerData(customerId, payload);
            if (!response.success) {
              const errorMessage =
                typeof response.error === "string"
                  ? response.error
                  : response.error?.message || "Failed to update customer profile";
              throw new Error(errorMessage);
            }
          }

          const storedCustomer = readStoredCustomerData() || {};
          const updatedCustomer = { ...storedCustomer, ...payload };
          if (typeof window !== "undefined") {
            try {
              localStorage.setItem("customerData", JSON.stringify(updatedCustomer));
            } catch {
              // ignore
            }
          }

          // IMPORTANT:
          // Budget/IE visibility rules read from `customerMetadata` (merged into form initialData).
          // The onboarding PATCH payload uses backend field names (e.g. housingTenure),
          // while our visibility metadata uses UI-friendly names (e.g. accommodationType).
          // If we spread the payload directly, the UI won't react until a full refresh.
          setCustomerMetadata((prev) => {
            const next = { ...prev };

            if (typeof payload.jointAccount === "string") {
              next.jointAccount = payload.jointAccount;
            }
            if (typeof payload.customerEmploymentStatus === "string") {
              next.customerEmploymentStatus = payload.customerEmploymentStatus;
            }
            if (typeof payload.partnerEmploymentStatus === "string") {
              next.partnerEmploymentStatus = payload.partnerEmploymentStatus;
            }
            if (typeof payload.haveChildrenUnder18 === "string") {
              next.haveChildrenUnder18 = payload.haveChildrenUnder18;
            }
            if (typeof payload.housingTenure === "string") {
              // Map backend field -> metadata field used by visibility
              next.accommodationType = payload.housingTenure;
            }
            if (typeof payload.dependentChildrenUnder16 === "number") {
              next.dependentChildrenUnder16 = payload.dependentChildrenUnder16;
            }
            if (typeof payload.dependentChildrenAged16Plus === "number") {
              next.dependentChildrenAged16Plus =
                payload.dependentChildrenAged16Plus;
            }

            // Keep optional derived partner age in sync when onboarding captures DOB.
            const partnerDob =
              typeof payload.debtor2DateOfBirthStr === "string"
                ? payload.debtor2DateOfBirthStr
                : typeof payload.debtor2DateOfBirth === "string"
                  ? payload.debtor2DateOfBirth
                  : undefined;
            if (typeof partnerDob === "string") {
              const age = computeAgeSafe(partnerDob);
              if (typeof age === "number") {
                next.partnerAge = age;
              }
            }

            return next;
          });
          setWarningsRefreshKey((prev) => prev + 1);
        }

        if (typeof persistDependentsDobs === "function") {
          await persistDependentsDobs();
        }

        const progressData = {
          currentStep: currentOnboardingStep,
          completedSteps: currentOnboardingStep,
          formData: formData,
          timestamp: new Date().toISOString(),
        };

        if (typeof window !== "undefined") {
          localStorage.setItem("budget-onboarding-progress", JSON.stringify(progressData));
        }

        await ensureFieldStageCustomerId();

        if (currentOnboardingStep < totalOnboardingSteps - 1) {
          if (currentOnboardingStep === 2) {
            const step3Raw = allFormData["income-onboarding-step-3"];
            const step3FormData = isPlainObject(step3Raw) ? step3Raw : {};
            if (formData.budgetWithPartner !== undefined) {
              setFormData("income-onboarding-step-3", {
                ...step3FormData,
                budgetWithPartner: formData.budgetWithPartner,
                jointAccount: formData.budgetWithPartner === true ? "Y" : "N",
              });
            }
          }
          setCurrentOnboardingStep(currentOnboardingStep + 1);
          bumpFormInstanceKey(currentOnboardingStep + 1);
        } else {
          if (typeof window !== "undefined") {
            localStorage.removeItem("budget-onboarding-progress");
          }

          try {
            await ensureFieldStageCustomerId();
            // User cannot use dashboard until field stage is confirmed (>=105),
            // so wait for the full response (up to 60s failsafe)
            await Promise.race([
              persistFieldStage(totalOnboardingSteps),
              new Promise<void>((resolve) => setTimeout(resolve, 120000)),
            ]);
          } catch {}

          setIsOnboardingDialogOpen(false);
          searchParams.delete("onboardingPending");
          searchParams.delete("step");
          setSearchParams(searchParams);
          showAlert({
            severity: "success",
            message: "Onboarding completed successfully!",
            autoHide: true,
            duration: 2000,
          });
          try {
            if (typeof window !== "undefined") {
              setTimeout(() => window.location.reload(), 0);
            }
          } catch {}
        }
      } catch (error) {
        console.error("Failed to save income onboarding data:", error);
        showAlert({
          severity: "error",
          message: "Failed to save progress. Please try again.",
          autoHide: true,
          duration: 3000,
        });
      } finally {
        setLoading("income-onboarding-submit", false);
      }
    };

    // Confirm-before-clear gating: clear budget first, then patch profile
    try {
      if (typeof customerId === "number" && Number.isFinite(customerId) && typeof userId === "number") {
        const before = buildProfileBudgetContext();

        const afterOverrides: Partial<ProfileBudgetContext> = {};
        const nextEmployment = payload["customerEmploymentStatus"];
        if (typeof nextEmployment === "string") afterOverrides.customerEmploymentStatus = nextEmployment;
        const nextPartnerEmployment = payload["partnerEmploymentStatus"];
        if (typeof nextPartnerEmployment === "string")
          afterOverrides.partnerEmploymentStatus = nextPartnerEmployment;
        const nextJoint = payload["jointAccount"];
        if (typeof nextJoint === "string") afterOverrides.jointAccount = nextJoint;
        const nextAccommodation = payload["housingTenure"];
        if (typeof nextAccommodation === "string") afterOverrides.accommodationType = nextAccommodation;
        const nextHaveChildren = payload["haveChildrenUnder18"];
        if (typeof nextHaveChildren === "string") afterOverrides.haveChildrenUnder18 = nextHaveChildren;
        const nextUnder16 = payload["dependentChildrenUnder16"];
        if (typeof nextUnder16 === "number") afterOverrides.dependentChildrenUnder16 = nextUnder16;
        const next16Plus = payload["dependentChildrenAged16Plus"];
        if (typeof next16Plus === "number") afterOverrides.dependentChildrenAged16Plus = next16Plus;

        const dobStrCandidate = payload["debtor2DateOfBirthStr"];
        const dobCandidate = payload["debtor2DateOfBirth"];
        const nextPartnerDob =
          typeof dobStrCandidate === "string"
            ? dobStrCandidate
            : typeof dobCandidate === "string"
              ? dobCandidate
              : undefined;
        if (typeof nextPartnerDob === "string") {
          const nextAge = computeAgeSafe(nextPartnerDob);
          if (typeof nextAge === "number") {
            afterOverrides.partnerAge = nextAge;
          }
        }

        const after: ProfileBudgetContext = { ...before, ...afterOverrides };

        const snaps = await fetchBudgetSnapshotsFresh(customerId);
        const plan = computeConfirmBeforeClearPlan({
          before,
          after,
          snaps,
          userId,
        });

        if (plan.needsConfirm) {
          setConfirmMessage(
            CONFIRM_REMOVE_SECTION_MESSAGE
          );
          setPendingConfirmAction(async () => {
            await clearBudgetForPlan(plan);
            await doSave();
            const refreshed = await refreshBudgetStateAfterClear(customerId);
            rehydrateLocalBudgetFormsAfterClear(plan, refreshed);
          });
          setConfirmOpen(true);
          return;
        }
      }
    } catch {
      // If gating fails, fall back to saving
    }

    await doSave();
  };

  const handleOnboardingValuesChange = (vals: unknown) => {
    const record = vals as Record<string, unknown>;
    liveStepValuesRef.current = record;
  };

  // Memoize schema field names for current schema to prevent recalculation
  const currentSchemaFields = useMemo(
    () => getSchemaFieldNames(currentSchema),
    [currentSchema, getSchemaFieldNames]
  );

  // Transform dot-notation fields to nested objects
  // Converts: { "parent.child": value } → { parent: { child: value } }
  const transformDotNotationToNested = useCallback(
    (data: Record<string, unknown>): Record<string, unknown> => {
      const result: Record<string, unknown> = {};

      Object.entries(data).forEach(([key, value]) => {
        if (!key.includes(".")) {
          if (
            result[key] &&
            isPlainObject(result[key]) &&
            isPlainObject(value)
          ) {
            result[key] = {
              ...(result[key] as Record<string, unknown>),
              ...(value as Record<string, unknown>),
            };
          } else {
            result[key] = value;
          }
          return;
        }

        const segments = key.split(".").filter(Boolean);
        if (segments.length === 0) {
          return;
        }

        let cursor: Record<string, unknown> = result;

        segments.forEach((segment, index) => {
          const isLeaf = index === segments.length - 1;

          if (isLeaf) {
            cursor[segment] = value;
            return;
          }

          const existing = cursor[segment];
          if (existing && isPlainObject(existing)) {
            cursor = existing as Record<string, unknown>;
          } else {
            const next: Record<string, unknown> = {};
            cursor[segment] = next;
            cursor = next;
          }
        });
      });

      return result;
    },
    []
  );

  const mergeNestedFormValues = useCallback(
    (
      base: Record<string, unknown>,
      updates: Record<string, unknown>
    ): Record<string, unknown> => {
      const result: Record<string, unknown> = { ...base };

      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined) {
          delete result[key];
          return;
        }

        if (isPlainObject(value)) {
          const existing = result[key];
          if (isPlainObject(existing)) {
            result[key] = mergeNestedFormValues(
              existing as Record<string, unknown>,
              value as Record<string, unknown>
            );
          } else {
            result[key] = mergeNestedFormValues(
              {},
              value as Record<string, unknown>
            );
          }
        } else {
          result[key] = value;
        }
      });

      return result;
    },
    []
  );

  /**
   * Build a "live values" snapshot (dot-path keys) from nested Zustand form data.
   *
   * Why: auto-save merges `liveBudgetFormValuesRef` over stored form data. If we
   * update the store programmatically (e.g. QuickEdit/redwarning save or post-save
   * refresh) but leave `liveBudgetFormValuesRef` stale, navigation can accidentally
   * PUT the old value back.
   */
  const buildLiveDotValuesFromNested = useCallback(
    (nestedData: Record<string, unknown>): Record<string, unknown> => {
      const out: Record<string, unknown> = {};
      currentSchemaFields.forEach((path) => {
        const v = getNested(nestedData as any, path);
        if (v !== undefined) {
          out[path] = v;
        }
      });
      return out;
    },
    [currentSchemaFields, getNested]
  );

  /**
   * Clear/remove values for question-groups that are currently hidden by `visibleWhen`.
   *
   * Why: when a field becomes hidden (e.g. jobSeekerAllowance after employment status changes),
   * react-hook-form stops emitting it, but the old value can remain in our cached form store.
   * On navigation, auto-save can then PUT that stale value unless we prune it here.
   *
   * Behavior:
   * - If the API currently has a non-zero value for that field, we set `{ amount: 0 }` to clear it
   *   without wiping mode/frequency (avoids spurious PUTs due to mode=null).
   * - If the API does NOT have a value but the local form cache does (unsaved), we delete it
   *   so it won't be accidentally saved.
   */
  const pruneHiddenQuestionGroups = useCallback(
    (
      schema: any,
      category: keyof typeof BUDGET_FIELD_MAPPINGS,
      formData: Record<string, unknown>,
      baseApiRecord: Record<string, unknown>
    ): { data: Record<string, unknown>; changed: boolean } => {
      const sections = Array.isArray(schema?.sections) ? schema.sections : [];
      const mapping = (BUDGET_FIELD_MAPPINGS as any)?.[category] as
        | Record<string, any>
        | undefined;
      if (!sections.length || !mapping) {
        return { data: formData, changed: false };
      }

      const ctx: Record<string, unknown> = {
        ...formData,
        ...customerMetadata,
        profile: profileSnapshot,
      };

      const next: Record<string, unknown> = { ...(formData || {}) };
      let changed = false;

      const visitField = (field: any) => {
        if (!field || typeof field !== "object") return;
        const fieldName = typeof field.fieldName === "string" ? field.fieldName : "";
        if (!fieldName) return;

        // Only prune question-groups (amount/frequency/notes) that map to API amounts
        if (field.dataType !== "question-group") return;
        if (field.addToPayload === false) return;

        let visible = true;
        if (field.isHidden) {
          visible = false;
        } else if (field.visibleWhen !== undefined) {
          try {
            visible = evaluateVisibilityRule(field.visibleWhen, ctx);
          } catch {
            visible = true; // fail-open (matches Screen/SpecialScreen behavior)
          }
        }
        if (visible) return;

        const m = mapping[fieldName];
        const apiAmountField: string | undefined =
          m && typeof m === "object" ? (m.amount as string | undefined) : undefined;

        const apiValueRaw = apiAmountField
          ? (baseApiRecord as any)?.[apiAmountField]
          : undefined;
        const apiHasMeaningful =
          apiValueRaw !== undefined &&
          apiValueRaw !== null &&
          Number(apiValueRaw) !== 0;

        const local = (next as any)[fieldName];
        const localAmtRaw =
          local && typeof local === "object" ? (local as any).amount : undefined;
        const localHasMeaningful =
          localAmtRaw !== undefined &&
          localAmtRaw !== null &&
          String(localAmtRaw).trim() !== "";

        if (apiHasMeaningful) {
          // Clear backend amount but keep mode untouched by only sending amount.
          (next as any)[fieldName] = { amount: 0 };
          changed = true;
          return;
        }

        if (localHasMeaningful) {
          // Only local cache had it (unsaved). Remove so it can't be persisted.
          delete (next as any)[fieldName];
          changed = true;
        }
      };

      sections.forEach((section: any) => {
        if (!Array.isArray(section?.fields)) return;
        section.fields.forEach(visitField);
      });

      return { data: next, changed };
    },
    [customerMetadata, profileSnapshot]
  );

  // Handle budget form values change - persist to Zustand in real-time
  // CRITICAL: Filter out metadata fields that are only needed for visibility evaluation
  const handleBudgetFormValuesChange = useCallback(
    (vals: unknown) => {
      const record = vals as Record<string, unknown>;
      liveBudgetFormValuesRef.current = record;

      // Filter to only include fields defined in current schema
      // This prevents metadata contamination (accommodationType, customerAge, etc.)
      const filteredData: Record<string, unknown> = {};
      Object.entries(record).forEach(([key, value]) => {
        if (currentSchemaFields.has(key)) {
          filteredData[key] = value;
        }
      });

      if (Object.keys(filteredData).length === 0) {
        return;
      }

      // CRITICAL: Transform dot-notation to nested objects BEFORE saving to Zustand
      // This ensures uniform data structure in Zustand store
      const transformedData = transformDotNotationToNested(filteredData);

      // Persist transformed and filtered data to Zustand for navigation persistence
      const formId = `${selectedOuter}-${selectedInner}-form`;
      const existingData = allFormData[formId] ?? {};
      const mergedData = mergeNestedFormValues(existingData, transformedData);
      setFormData(formId, mergedData);
      // No auto-save on change; saving occurs only when the user continues/saves.
    },
    [
      selectedOuter,
      selectedInner,
      setFormData,
      currentSchemaFields,
      transformDotNotationToNested,
      allFormData,
      mergeNestedFormValues,
    ]
  );

  const handleOnboardingBack = () => {
    if (currentOnboardingStep > 0) {
      const newStep = currentOnboardingStep - 1;
      setCurrentOnboardingStep(newStep);
      bumpFormInstanceKey(newStep);
      // URL will be updated by the useEffect watching currentOnboardingStep
    }
  };

  const handleOnboardingSkip = () => {
    if (currentOnboardingStep < totalOnboardingSteps - 1) {
      const newStep = currentOnboardingStep + 1;
      setCurrentOnboardingStep(newStep);
      bumpFormInstanceKey(newStep);
      // URL will be updated by the useEffect watching currentOnboardingStep
    } else {
      setIsOnboardingDialogOpen(false);
      searchParams.delete("onboardingPending");
      searchParams.delete("step");
      setSearchParams(searchParams);
    }
  };

  const handleOnboardingClose = () => {
    // Always close and go back to dashboard per requirement
    setIsOnboardingDialogOpen(false);
    searchParams.delete("onboardingPending");
    searchParams.delete("step");
    setSearchParams(searchParams);
    navigate("/dashboard");
  };

  const handleHelpOpen = () => {
    setIsHelpDialogOpen(true);
  };

  const handleHelpClose = () => {
    setIsHelpDialogOpen(false);
  };

  // Update dialog state when URL parameter changes
  useEffect(() => {
    // Prevent multiple initializations
    if (hasInitializedStepRef.current) {
      return;
    }

    // Fetch customer data and pre-fill forms
    const fetchAndPreFillData = async () => {
      setLoading("income-onboarding-prefill", true);
      const userId = resolveUserId();
      if (userId) {
        try {
          const response = await ProfileService.getCustomerData(userId, {
            skipCache: true,
            skipErrorCapture: true,
          });
          if (response.success && response.data) {
            const customerData = (
              Array.isArray(response.data) ? response.data[0] : response.data
            ) as any;

            // Calculate customer age from DOB string
            const customerAge = calculateAge(
              customerData.debtor1DateOfBirthStr
            );
            const partnerAge = calculateAge(customerData.debtor2DateOfBirthStr);
            // Store customer metadata for visibility conditions
            setCustomerMetadata({
              jointAccount: customerData.jointAccount,
              customerEmploymentStatus: customerData.customerEmploymentStatus,
              partnerEmploymentStatus: customerData.partnerEmploymentStatus,
              haveChildrenUnder18: customerData.haveChildrenUnder18,
              accommodationType: customerData.housingTenure,
              customerAge: customerAge ?? 0,
              partnerAge: partnerAge ?? 0,
              dependentChildrenUnder16: customerData.dependentChildrenUnder16,
              dependentChildrenAged16Plus:
                customerData.dependentChildrenAged16Plus,
            });

            // Reverse map API data to form fields for each step

            // Step 0: Accommodation Type
            const step0Data: Record<string, unknown> = {};
            if (customerData.housingTenure) {
              step0Data.accomodationType = customerData.housingTenure;
            }
            setFormData("income-onboarding-step-0", step0Data);

            // Step 1: Employment Status
            const step1Data: Record<string, unknown> = {};
            if (customerData.customerEmploymentStatus) {
              step1Data.customerEmploymentStatus =
                customerData.customerEmploymentStatus;
            }
            setFormData("income-onboarding-step-1", step1Data);

            // Step 2: Budget with Partner
            const step2Data: Record<string, unknown> = {};
            if (customerData.jointAccount) {
              step2Data.budgetWithPartner = customerData.jointAccount === "Y";
              if (customerData.jointAccount === "Y") {
                if (customerData.debtor2Name)
                  step2Data.name = customerData.debtor2Name;
                if (
                  customerData.debtor2DateOfBirthStr ||
                  customerData.debtor2DateOfBirth
                ) {
                  const dateStr =
                    customerData.debtor2DateOfBirthStr ||
                    customerData.debtor2DateOfBirth;
                  step2Data.dob = new Date(dateStr);
                }
                if (customerData.partnerEmploymentStatus)
                  step2Data.employmentStatus =
                    customerData.partnerEmploymentStatus;
              }
            }
            setFormData("income-onboarding-step-2", step2Data);

            // Step 3: Dependents
            const step3Data: Record<string, unknown> = {};
            // Pass budgetWithPartner from step 2 for total household calculation
            if (customerData.jointAccount) {
              step3Data.budgetWithPartner = customerData.jointAccount === "Y";
              step3Data.jointAccount = customerData.jointAccount;
            }
            
            // Only pre-fill haveDependents if the user has previously answered this question
            const hasExistingDependentsAnswer = customerData.haveChildrenUnder18 !== undefined && customerData.haveChildrenUnder18 !== null;
            
            if (hasExistingDependentsAnswer) {
              // adultsOver18IncludingMyself is actually other adults excluding customer and partner
              const otherAdultsOver18 = typeof customerData.adultsOver18IncludingMyself === "number"
                ? customerData.adultsOver18IncludingMyself
                : 0;
              
              // haveDependents is true if there are children under 18 OR other adults
              const hasChildren = customerData.haveChildrenUnder18 === "Y";
              const hasOtherAdults = otherAdultsOver18 > 0;
              const haveDependentsValue = hasChildren || hasOtherAdults;
              
              step3Data.haveDependents = haveDependentsValue;
              if (haveDependentsValue) {
                step3Data.under16Dependents = customerData.dependentChildrenUnder16 ?? 0;
                step3Data.between16to18Dependents = customerData.dependentChildrenAged16Plus ?? 0;
                step3Data.otherAdultsOver18 = otherAdultsOver18;

              // Fetch dependent DOBs from the API
              try {
                const dependentsResponse =
                  await DependentsService.getDependents(
                    customerData.customerId
                  );
                if (dependentsResponse.success && dependentsResponse.data) {
                  setDependentsDobData(dependentsResponse.data);

                  // Map DOBs to form fields based on category - with numbered fields
                  const under16Dependents = dependentsResponse.data
                    .filter((dep) => dep.dependantCategory === "age-under16")
                    .sort(
                      (a, b) =>
                        Number(a.childDependantNo) -
                        Number(b.childDependantNo)
                    );

                  const between16to18Dependents = dependentsResponse.data
                    .filter((dep) => dep.dependantCategory === "age-16to18")
                    .sort(
                      (a, b) =>
                        Number(a.childDependantNo) -
                        Number(b.childDependantNo)
                    );

                  // Set numbered DOB fields for under16 dependents
                  under16Dependents.forEach((dep) => {
                    const dobStr = dep.childDependantDobStr;
                    if (dobStr && typeof dobStr === "string") {
                      const fieldName = `under16DependentsDOB_${dep.childDependantNo}`;
                      step3Data[fieldName] = new Date(dobStr);
                    }
                  });

                  // Set numbered DOB fields for 16-18 dependents
                  between16to18Dependents.forEach((dep) => {
                    const dobStr = dep.childDependantDobStr;
                    if (dobStr && typeof dobStr === "string") {
                      const fieldName = `between16to18DependentsDOB_${dep.childDependantNo}`;
                      step3Data[fieldName] = new Date(dobStr);
                    }
                  });
                }
              } catch (error) {
                console.error("Failed to fetch dependent DOBs:", error);
              }
              }
            }
            setFormData("income-onboarding-step-3", step3Data);

            // Step 4: Vehicle Details
            const step4Data: Record<string, unknown> = {};
            if (customerData.haveVehicle) {
              step4Data.haveVehicle = customerData.haveVehicle === "Y";
              if (
                customerData.haveVehicle === "Y" &&
                customerData.noOfVehiclesInHouseholds !== undefined
              ) {
                step4Data.noOfVehiclesInHouseholds =
                  customerData.noOfVehiclesInHouseholds;
              }
            }
            setFormData("income-onboarding-step-4", step4Data);

            // Note: Partner Employment Status (step 5) is NOT part of income onboarding dialog
            // It's handled via profile warning dialogs instead

            // Bump all form instance keys to force re-render with pre-filled data
            for (let i = 0; i < totalOnboardingSteps; i++) {
              bumpFormInstanceKey(i);
            }
          }
        } catch (error) {
          console.error("Failed to fetch and pre-fill customer data:", error);
        }
      }

      // Capture original step param on first load
      if (!hasInitializedStepRef.current) {
        originalStepParamRef.current = searchParams.get("step");
        hasInitializedStepRef.current = true;
      }

      // Parse step from URL - ignored when API stage is not available

      // Continue

      // Fetch field stage from API to check last saved step AND set up refs
      let apiStep: number | null = null;
      try {
        // Use the helper to get customerId reliably
        const effectiveCustomerId = resolveCustomerId();

        if (effectiveCustomerId) {
          // Set up the customerId ref for future persistence
          fieldStageCustomerIdRef.current = Number(effectiveCustomerId);

          const stageResp = await OnboardingService.getCustomerFieldStage(
            Number(effectiveCustomerId),
            { skipErrorCapture: true }
          );

          console.log("stageResp", stageResp);

          if (stageResp?.fieldStage) {
            const parsed = parseInt(stageResp.fieldStage, 10);
            const base = INCOME_ONBOARDING_STEP_OFFSET; // 100
            // Treat values <= base as step 0; otherwise derive step by subtracting base
            if (!Number.isNaN(parsed)) {
              apiStep = parsed <= base ? 0 : parsed - base;
              // Set up refs for persistence
              fieldStageRecordIdRef.current =
                typeof stageResp.id === "number" ? stageResp.id : null;
              lastPersistedStageRef.current = String(parsed);
            }
          }

          // Mark as initialized so persistence can work
          fieldStageInitializedRef.current = true;
        }
      } catch (error) {
        console.error(
          "Failed to fetch field stage for step restoration:",
          error
        );
      }

      // Prefer API step over URL; clamp to valid range [0..total-1]
      const clampStep = (n: number) =>
        Math.max(0, Math.min(n, totalOnboardingSteps - 1));
      // If API has no stage, always start at step 0 (ignore any URL-provided step)
      const initialStep =
        apiStep !== null && !isNaN(apiStep) && apiStep >= 0
          ? clampStep(apiStep)
          : 0;

      // Determine completed from API stage (sentinel is offset + total steps)
      const completedByApi =
        apiStep !== null && !isNaN(apiStep)
          ? apiStep >= totalOnboardingSteps
          : false;

      // Open dialog only when incomplete (ignore URL forcing when already completed)
      const shouldOpen = !completedByApi;
      setIsOnboardingDialogOpen(shouldOpen);

      if (shouldOpen) {
        setCurrentOnboardingStep(initialStep);
        // Only sync step to URL if it was present originally to avoid background rerenders
        if (originalStepParamRef.current !== null) {
          setSearchParams(
            (prevParams) => {
              const newParams = new URLSearchParams(prevParams);
              newParams.set("step", String(initialStep));
              return newParams;
            },
            { replace: true }
          );
        }
      } else {
        // Strip onboarding params when not needed
        setSearchParams(
          (prevParams) => {
            const newParams = new URLSearchParams(prevParams);
            newParams.delete("onboardingPending");
            newParams.delete("step");
            return newParams;
          },
          { replace: true }
        );
      }

      setLoading("income-onboarding-prefill", false);
    };

    fetchAndPreFillData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboardingPending]); // Only run on mount and when onboardingPending changes

  // Persist field stage whenever currentOnboardingStep changes
  useEffect(() => {
    if (
      fieldStageCustomerIdRef.current &&
      fieldStageInitializedRef.current &&
      isOnboardingDialogOpen
    ) {
      persistFieldStage(currentOnboardingStep);
    }
  }, [currentOnboardingStep, isOnboardingDialogOpen, persistFieldStage]);

  // Sync step to URL whenever it changes (but not during initial load)
  useEffect(() => {
    // Only sync after initialization is complete to avoid race conditions
    // and only when a step param was originally present. This prevents
    // background rerenders while the dialog is open.
    if (
      isOnboardingDialogOpen &&
      hasInitializedStepRef.current &&
      originalStepParamRef.current !== null
    ) {
      setSearchParams(
        (prevParams) => {
          const newParams = new URLSearchParams(prevParams);
          newParams.set("step", String(currentOnboardingStep));
          return newParams;
        },
        { replace: true }
      );
    }
  }, [currentOnboardingStep, isOnboardingDialogOpen, setSearchParams]);

  // Sync outer/inner selection from URL search params
  useEffect(() => {
    const qpOuter = searchParams.get("outer");
    const qpInner = searchParams.get("inner");
    if (!qpOuter && !qpInner) return;

    // Validate outer
    const outerKey = qpOuter || selectedOuter;
    const innerList =
      innerRoutesConfig[outerKey as keyof typeof innerRoutesConfig] || [];

    // Validate inner
    const isInnerValid = qpInner
      ? innerList.some((r) => r.key === qpInner)
      : false;
    const nextInner = isInnerValid
      ? (qpInner as string)
      : innerList[0]?.key || "";

    if (qpOuter && qpOuter !== selectedOuter) {
      setSelectedOuter(qpOuter);
    }
    if (nextInner && nextInner !== selectedInner) {
      setSelectedInner(nextInner);
    }
  }, [searchParams, innerRoutesConfig, selectedOuter, selectedInner]);

  useEffect(() => {
    const enteringDebtsList =
      selectedOuter === "debts" && selectedInner === "debts";

    if (enteringDebtsList && !isBudgetDataLoading) {
      void refreshDebtsData({ withSpinner: true });
    }

    lastDebtsSelectionRef.current = {
      outer: selectedOuter,
      inner: selectedInner,
    };
  }, [selectedOuter, selectedInner, refreshDebtsData, isBudgetDataLoading]);

  // Fetch budget data on component mount
  useEffect(() => {
    const fetchBudgetData = async () => {
      setIsBudgetDataLoading(true);
      setDebtsLoading(true);
      setAccountTypesLoading(true);
      try {
        // Resolve customerId via multiple sources: query, local snapshot, onboarding lookup
        let resolvedCustomerId: string | number | null = resolveCustomerId();

        // 3) Derive from logged-in user via onboarding service
        if (!resolvedCustomerId) {
          try {
            const userId = resolveUserId();
            if (userId) {
              const customerResp = await OnboardingService.getCustomerByUserId(
                Number(userId),
                { skipErrorCapture: true }
              );
              const got = customerResp?.data;
              const derivedId = got?.customerId || got?.id;
              if (derivedId) {
                resolvedCustomerId = derivedId;
                try {
                  localStorage.setItem("customerData", JSON.stringify(got));
                } catch {}
              }
            }
          } catch {}
        }

        if (!resolvedCustomerId) {
          showAlert({
            severity: "warning",
            message:
              "Missing customerId. Add ?customerId= in URL or ensure you are logged in.",
            autoHide: true,
            duration: 5000,
          });
          return;
        }

        // Fetch all budget data in parallel
        const [
          incomeResponse,
          expenseResponse,
          incomeNotesResponse,
          expenseNotesResponse,
          guidanceNotesResponse,
          triggersResponse,
          debtsResponse,
          accountTypesResponse,
        ] = await Promise.all([
          BudgetService.getIncomeData(resolvedCustomerId),
          BudgetService.getExpenseData(resolvedCustomerId),
          BudgetService.getIncomeNotes(resolvedCustomerId),
          BudgetService.getExpenseNotes(resolvedCustomerId),
          BudgetService.getGuidanceNotes(),
          BudgetService.getIncomeTriggers(),
          DebtsService.getDebts(resolvedCustomerId),
          DebtCompaniesService.fetchAllAccountTypes({
            skipErrorCapture: false,
          }),
        ]);

        if (incomeResponse.success && incomeResponse.data) {
          setIncomeData(incomeResponse.data);
        }

        if (expenseResponse.success && expenseResponse.data) {
          setExpenseData(expenseResponse.data);
        }

        if (incomeNotesResponse.success && incomeNotesResponse.data) {
          setIncomeNotes(incomeNotesResponse.data);
        }

        // Seed cross-section values into the per-page form store so warning rules that
        // compare across sections (e.g. Housing Benefit vs Rent) can work even when
        // the user jumps directly into fixed expenses without visiting benefits first.
        try {
          const incomeRecord =
            incomeResponse.success &&
            Array.isArray(incomeResponse.data) &&
            incomeResponse.data[0]
              ? incomeResponse.data[0]
              : null;
          const incomeNotesRecord =
            incomeNotesResponse.success &&
            Array.isArray(incomeNotesResponse.data) &&
            incomeNotesResponse.data[0]
              ? incomeNotesResponse.data[0]
              : {};
          if (incomeRecord) {
            const mappedBenefits = mapIncomeDataToSchema(
              incomeRecord as any,
              incomeNotesRecord as any,
              "benefits"
            ) as any;
            const hb = mappedBenefits?.housingBenefitLocalAllowance;
            if (hb) {
              setFormData("__budget_cross_section__", {
                housingBenefitLocalAllowance: hb,
                housingBenefitLocalAllowanceMode:
                  (incomeRecord as any)?.housingBenefitLocalAllowanceMode ??
                  (mappedBenefits as any)?.housingBenefitLocalAllowanceMode,
              });
            }
          }
        } catch {
          // non-blocking
        }

        if (expenseNotesResponse.success && expenseNotesResponse.data) {
          setExpenseNotes(expenseNotesResponse.data);
        }

        if (guidanceNotesResponse.success && guidanceNotesResponse.data) {
          setGuidanceNotes(guidanceNotesResponse.data);
        }

        if (triggersResponse.success && triggersResponse.data) {
          setIncomeTriggers(triggersResponse.data);
        }

        if (debtsResponse.success && Array.isArray(debtsResponse.data)) {
          // Enrich debts with payment offer data from separate API
          const enrichedDebts: CustomerDebtRecord[] = await (async () => {
            const items = debtsResponse.data as CustomerDebtRecord[];
            const tasks = items.map(async (d) => {
              if (
                d &&
                typeof d.id === "number" &&
                d.id > 0 &&
                d.accountNumber &&
                (d.paymentOfferAmount == null || d.paymentOfferAmount === 0)
              ) {
                try {
                  const resp = await DebtsService.getPaymentOffer(
                    resolvedCustomerId,
                    d.id,
                    d.accountNumber,
                    { skipErrorCapture: true }
                  );
                  const payload = (resp?.data as any) ?? null;
                  // Take the last (most recent) payment offer when multiple exist
                  const record =
                    Array.isArray(payload) && payload.length > 0
                      ? (payload[payload.length - 1] as any)
                      : payload;

                  if (record) {
                    // Debug: surface the mapped payment-offer data to the console
                    // so we can verify the amount/day flowing from API → debts → UI.
                    // eslint-disable-next-line no-console
                    console.log("[Debts] Enriched payment offer (initial load)", {
                      customerId: resolvedCustomerId,
                      debtId: d.id,
                    });

                    return {
                      ...d,
                      paymentOfferAmount: (record.paymentOfferAmount ??
                        d.paymentOfferAmount ??
                        null) as number | null,
                        paymentOfferMode: (record.paymentPreferredFrequency ??
                          record.paymentOfferMode ??
                        d.paymentOfferMode ??
                        null) as any,
                      paymentOfferId: record.id ?? null,
                    } as CustomerDebtRecord;
                  }
                } catch (e) {
                  // Debug: log failures so we can see if any debt fails enrichment
                  // eslint-disable-next-line no-console
                  console.warn("[Debts] Failed to enrich payment offer (initial load)", {
                    customerId: resolvedCustomerId,
                    debtId: d.id,
                    error: e,
                  });
                }
              }
              return d;
            });
            return await Promise.all(tasks);
          })();
          setDebtData(enrichedDebts);
        } else {
          setDebtData([]);
        }

        if (
          accountTypesResponse.success &&
          Array.isArray(accountTypesResponse.data)
        ) {
          setDebtAccountTypes(accountTypesResponse.data);
        } else {
          setDebtAccountTypes([]);
        }
      } catch (error) {
        console.error("Failed to fetch budget data:", error);
        showAlert({
          severity: "error",
          message:
            "Failed to load budget data. Some features may not work correctly.",
          autoHide: true,
          duration: 5000,
        });
      } finally {
        setIsBudgetDataLoading(false);
        setDebtsLoading(false);
        setAccountTypesLoading(false);
      }
    };

    fetchBudgetData();
  }, [showAlert, resolveCustomerId, resolveUserId]);

  useEffect(() => {
    if (lendersLoaded) {
      return;
    }

    const loadLenders = async (): Promise<void> => {
      try {
        setLoading("lenders-load", true);
        const response = await DebtCompaniesService.fetchCompanies({
          skipErrorCapture: false,
        });
        if (response.success && Array.isArray(response.data)) {
          setLenders(response.data);
          setLendersLoaded(true);
        } else {
          setLenders([]);
          setLendersLoaded(true);
        }
      } catch (error) {
        console.error("Failed to load lenders:", error);
        setLenders([]);
        setLendersLoaded(true);
      } finally {
        setLoading("lenders-load", false);
      }
    };

    void loadLenders();
  }, [lendersLoaded, setLoading]);

  // Load energy companies for dynamic supplier selection in utilities
  // NI-specific: Load different suppliers based on postcode region
  useEffect(() => {
    if (energyCompaniesLoaded) {
      return;
    }
    // Wait for NI detection to complete before loading energy companies
    if (niLoading) {
      return;
    }

    const loadEnergyCompanies = async (): Promise<void> => {
      try {
        setLoading("energy-companies-load", true);
        const response = await BudgetService.fetchEnergyCompanies({
          skipErrorCapture: false,
          isNorthernIreland: isNorthernIreland,
        });
        if (response.success && Array.isArray(response.data)) {
          setEnergyCompanies(response.data);
          setEnergyCompaniesLoaded(true);
        } else {
          setEnergyCompanies([]);
          setEnergyCompaniesLoaded(true);
        }
      } catch (error) {
        console.error("Failed to load energy companies:", error);
        setEnergyCompanies([]);
        setEnergyCompaniesLoaded(true);
      } finally {
        setLoading("energy-companies-load", false);
      }
    };

    void loadEnergyCompanies();
  }, [energyCompaniesLoaded, setLoading, niLoading, isNorthernIreland]);

  // Load broadband suppliers for dynamic supplier selection in leisure
  useEffect(() => {
    if (broadbandSuppliersLoaded) {
      return;
    }

    const loadBroadbandSuppliers = async (): Promise<void> => {
      try {
        setLoading("broadband-suppliers-load", true);
        const response = await BudgetService.fetchBroadbandSuppliers({
          skipErrorCapture: false,
        });
        if (response.success && Array.isArray(response.data)) {
          setBroadbandSuppliers(response.data);
          setBroadbandSuppliersLoaded(true);
        } else {
          setBroadbandSuppliers([]);
          setBroadbandSuppliersLoaded(true);
        }
      } catch (error) {
        console.error("Failed to load broadband suppliers:", error);
        setBroadbandSuppliers([]);
        setBroadbandSuppliersLoaded(true);
      } finally {
        setLoading("broadband-suppliers-load", false);
      }
    };

    void loadBroadbandSuppliers();
  }, [broadbandSuppliersLoaded, setLoading]);

  // Load landline suppliers for dynamic supplier selection in leisure
  useEffect(() => {
    if (landlineSuppliersLoaded) {
      return;
    }

    const loadLandlineSuppliers = async (): Promise<void> => {
      try {
        setLoading("landline-suppliers-load", true);
        const response = await BudgetService.fetchLandlineSuppliers({
          skipErrorCapture: false,
        });
        if (response.success && Array.isArray(response.data)) {
          setLandlineSuppliers(response.data);
          setLandlineSuppliersLoaded(true);
        } else {
          setLandlineSuppliers([]);
          setLandlineSuppliersLoaded(true);
        }
      } catch (error) {
        console.error("Failed to load landline suppliers:", error);
        setLandlineSuppliers([]);
        setLandlineSuppliersLoaded(true);
      } finally {
        setLoading("landline-suppliers-load", false);
      }
    };

    void loadLandlineSuppliers();
  }, [landlineSuppliersLoaded, setLoading]);

  // Load mobile suppliers for dynamic supplier selection in leisure
  useEffect(() => {
    if (mobileSuppliersLoaded) {
      return;
    }

    const loadMobileSuppliers = async (): Promise<void> => {
      try {
        setLoading("mobile-suppliers-load", true);
        const response = await BudgetService.fetchMobileSuppliers({
          skipErrorCapture: false,
        });
        if (response.success && Array.isArray(response.data)) {
          setMobileSuppliers(response.data);
          setMobileSuppliersLoaded(true);
        } else {
          setMobileSuppliers([]);
          setMobileSuppliersLoaded(true);
        }
      } catch (error) {
        console.error("Failed to load mobile suppliers:", error);
        setMobileSuppliers([]);
        setMobileSuppliersLoaded(true);
      } finally {
        setLoading("mobile-suppliers-load", false);
      }
    };

    void loadMobileSuppliers();
  }, [mobileSuppliersLoaded, setLoading]);

  // Memoize initial form data to prevent recreation on every render
  // This is critical - recreation causes form state issues
  const initialFormData = useMemo(() => {
    if (!currentSchema) return {};

    // Always consider previously saved form data for this section,
    // but do NOT return early – we still need to merge metadata for visibility
    const formId = `${selectedOuter}-${selectedInner}-form`;
    const savedFormData = allFormData[formId];

    // Track which top-level fields have arrears injected from Debts API so we can
    // make those arrears values win over stale cached form data. This fixes the
    // "need to refresh twice" bug where old arrears from Zustand override the
    // freshly mapped arrears from debts.
    const arrearsFieldNames = new Set<string>();

    if (selectedOuter === "debts") {
      if (selectedInner === "debts") {
        const defaults: DebtsSchemaState = {
          haveDebts: mappedDebts.haveDebts,
          creditCards: mappedDebts.creditCards.map((entry) => ({ ...entry })),
          loans: mappedDebts.loans.map((entry) => ({ ...entry })),
          overdrafts: mappedDebts.overdrafts.map((entry) => ({ ...entry })),
          otherDebts: mappedDebts.otherDebts.map((entry) => ({ ...entry })),
          arrears: mappedDebts.arrears.map((entry) => ({ ...entry })),
          allDebtsAdded:
            typeof mappedDebts.allDebtsAdded === "boolean"
              ? mappedDebts.allDebtsAdded
              : debtsAllAddedFlag,
        };

        /**
         * Debts screen initial data
         *
         * - `mappedDebts` (from Debts API) is still the canonical base snapshot.
         * - However, within the current session, any create/update/delete done via
         *   `DebtCategory` is written into the Debts form slice in Zustand.
         *
         * Previously we only used `mappedDebts` here, which meant that newly created
         * debts did not appear in the UI until a full page refresh re-fetched the
         * Debts API. To fix that:
         *
         * - Start from `mappedDebts` as `defaults`.
         * - Overlay any arrays and flags that exist in the saved Debts form slice
         *   (`savedFormData`). This makes the UI immediately reflect what the user
         *   just saved in the dialog, while preserving API-enriched fields on
         *   initial load.
         */
        let merged: DebtsSchemaState = { ...defaults };

        if (savedFormData && typeof savedFormData === "object") {
          const saved = savedFormData as Partial<DebtsSchemaState>;

          merged = {
            ...merged,
            // If a category array exists in the Debts form slice, use it – this
            // contains the latest entries from the DebtCategory dialog (including
            // newly created debts) so they show up without a full refresh.
            creditCards: Array.isArray(saved.creditCards)
              ? (saved.creditCards as DebtsSchemaState["creditCards"])
              : merged.creditCards,
            loans: Array.isArray(saved.loans)
              ? (saved.loans as DebtsSchemaState["loans"])
              : merged.loans,
            overdrafts: Array.isArray(saved.overdrafts)
              ? (saved.overdrafts as DebtsSchemaState["overdrafts"])
              : merged.overdrafts,
            otherDebts: Array.isArray(saved.otherDebts)
              ? (saved.otherDebts as DebtsSchemaState["otherDebts"])
              : merged.otherDebts,
            arrears: Array.isArray(saved.arrears)
              ? (saved.arrears as DebtsSchemaState["arrears"])
              : merged.arrears,
            haveDebts:
              typeof saved.haveDebts === "boolean"
                ? saved.haveDebts
                : merged.haveDebts,
            allDebtsAdded: Boolean(
              typeof saved.allDebtsAdded === "boolean"
                ? saved.allDebtsAdded
                : merged.allDebtsAdded
            ),
          };
        }

        return {
          ...(merged as unknown as Record<string, unknown>),
          ...customerMetadata,
        };
      }
    }

    // Determine category from current selection
    let category: keyof typeof BUDGET_FIELD_MAPPINGS = "salary";

    switch (selectedOuter) {
      case "income":
        if (selectedInner === "salary") category = "salary";
        else if (selectedInner === "benefits") category = "benefits";
        else if (selectedInner === "pension") category = "pension";
        else if (selectedInner === "other-sources") category = "otherSources";
        // else if (selectedInner === 'benefits2') category = 'benefits2'; // Schema not found
        else category = "salary"; // Default fallback
        break;
      case "assets":
        if (selectedInner === "properties") category = "properties";
        else if (selectedInner === "otherAssets") category = "otherAssets";
        else category = "properties"; // Default fallback
        break;
      case "fixed-expenses":
        if (selectedInner === "household") category = "household";
        else if (selectedInner === "utilities") category = "utilities";
        else if (selectedInner === "care") category = "care";
        else if (selectedInner === "transport") category = "transport";
        else if (selectedInner === "school") category = "school";
        else if (selectedInner === "pension-insurance")
          category = "pensionInsurance";
        else if (selectedInner === "professionals") category = "professionals";
        else if (selectedInner === "other-sources")
          category = "otherSourcesFixed";
        else category = "household"; // Default fallback
        break;
      case "flexible-expenses":
        if (selectedInner === "leisure") category = "leisure";
        else if (selectedInner === "housekeeping") category = "housekeeping";
        else if (selectedInner === "personal-costs") category = "personalCosts";
        else if (selectedInner === "savings") category = "savings";
        else category = "leisure"; // Default fallback
        break;
      case "debts":
        if (selectedInner === "debts-administration")
          category = "debtsAdministration";
        else if (selectedInner === "debts") category = "debts";
        else category = "debtsAdministration"; // Default fallback
        break;
      default:
        category = "salary";
    }

    // Determine if this is an income/asset or expense category
    // Exception: savings category uses income API despite being under flexible-expenses
    const incomeAssetCategories = [
      "salary",
      "benefits",
      "pension",
      "otherSources",
      "properties",
      "otherAssets",
      "savings",
    ];
    const isIncomeOrAsset = incomeAssetCategories.includes(category);

    // Map data based on category type
    let mappedData: Record<string, any> = {};

    if (isIncomeOrAsset) {
      // Income and Assets use income data
      const incomeRecord = incomeData[0];
      const incomeNotesRecord = incomeNotes[0] || {};
      if (incomeRecord) {
        mappedData = mapIncomeDataToSchema(
          incomeRecord,
          incomeNotesRecord,
          category
        );
      }
    } else {
      // Expenses use expense data
      const expenseRecord = expenseData[0];
      const expenseNotesRecord = expenseNotes[0] || {};
      if (expenseRecord) {
        mappedData = mapExpenseDataToSchema(
          expenseRecord,
          expenseNotesRecord,
          category
        );

        // Normalise legacy supplier data for utilities:
        // If a concrete supplier is selected (not "Other" / "I can't find my company"),
        // always clear the corresponding "*SupplierOther" field so the manual input
        // does not appear on load due to a stale backend value.
        if (category === "utilities") {
          const gas = mappedData.gas as
            | {
                gasSupplier?: string;
                gasSupplierOther?: string;
              }
            | undefined;
          if (
            gas &&
            typeof gas.gasSupplier === "string" &&
            gas.gasSupplier !== "Other" &&
            gas.gasSupplier !== "I can't find my company"
          ) {
            gas.gasSupplierOther = "";
          }

          const electricity = mappedData.electricity as
            | {
                electricitySupplier?: string;
                electricitySupplierOther?: string;
              }
            | undefined;
          if (
            electricity &&
            typeof electricity.electricitySupplier === "string" &&
            electricity.electricitySupplier !== "Other" &&
            electricity.electricitySupplier !== "I can't find my company"
          ) {
            electricity.electricitySupplierOther = "";
          }
        }

        // Load arrears from existing debtData and merge into expense form data
        // Only load arrears for expense categories that support arrears
        const expenseCategoriesWithArrears = ["household", "utilities"];
        if (
          expenseCategoriesWithArrears.includes(category) &&
          Array.isArray(debtData) &&
          debtData.length > 0
        ) {
          const arrearsData = mapArrearsDebtsToExpenseSchema(
            debtData,
            category
          );
          // Merge arrears data into mappedData
          Object.keys(arrearsData).forEach((fieldName) => {
            arrearsFieldNames.add(fieldName);
            if (arrearsData[fieldName] && arrearsData[fieldName].arrears) {
              if (!mappedData[fieldName]) {
                mappedData[fieldName] = {};
              }
              mappedData[fieldName].arrears = arrearsData[fieldName].arrears;
              // Set toggle based on presence of arrears
              mappedData[fieldName].inArrears =
                Array.isArray(arrearsData[fieldName].arrears) &&
                arrearsData[fieldName].arrears.length > 0;
            }
          });
        }
      }
    }

    // Build dynamic meta (always include numeric balance for cross-screen logic; add formatted values for Savings help text)
    const dynamicMeta: Record<string, unknown> = {};
    if (calculatedTotals) {
      const num = (v: unknown): number => {
        const n = typeof v === "string" ? parseFloat(v as string) : Number(v);
        return Number.isFinite(n) ? (n as number) : 0;
      };
      // Always expose numeric balance for components like Debts (payment-offer gating)
      dynamicMeta.balanceAmount = num(calculatedTotals.balanceAmount);
    }
    if (
      selectedOuter === "flexible-expenses" &&
      selectedInner === "savings" &&
      calculatedTotals
    ) {
      const toNumber = (v: unknown): number => {
        const n = typeof v === "string" ? parseFloat(v as string) : Number(v);
        return Number.isFinite(n) ? (n as number) : 0;
      };
      const format = (n: number): string =>
        n.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      const fixed = toNumber(calculatedTotals.totalOutgoingsFixed);
      const flexible = toNumber(calculatedTotals.totalOutgoingsFlexible);
      const bal = toNumber(calculatedTotals.balanceAmount);
      dynamicMeta.balanceAmount = format(bal);
      dynamicMeta.totalOutgoings = format(fixed + flexible);
    }

    // Add guidance notes for fields that have them
    if (guidanceNotes.length > 0) {
      // Get all field names from the current schema that might have guidance notes
      const schemaFields =
        currentSchema.sections?.flatMap((section: any) =>
          section.fields?.map((field: any) => field.fieldName)
        ) || [];

      // For nested benefit/pension fields, we need to map them to API field names
      const guidanceNotesData: Record<string, any> = {};

      schemaFields.forEach((fieldName: string) => {
        // Try to find guidance notes for this field
        // For nested fields like childBenefit, universalCredit, etc., we need to map to API field names
        let apiFieldName = fieldName;

        // Handle nested benefit/pension/asset field mappings
        if (
          category === "benefits" ||
          category === "pension" ||
          category === "properties" ||
          category === "otherAssets"
        ) {
          // Map nested field names to API field names
          const nestedFieldMappings: Record<string, string> = {
            // Benefits mappings
            childBenefit: "childBenefit",
            workingTaxCredit: "workingTaxCredit",
            childTaxCredit: "childTaxCredit",
            carerAllowance: "carerAllowance",
            housingBenefitLocalAllowance: "housingBenefitLocalAllowance",
            councilTaxReduction: "councilTaxReduction",
            universalCredit: "universalCredit",
            empsupportallowancestatutory: "empSupportAllowanceStatutory",
            disabilityAllowance: "dlaPipAttendanceAllowance",
            studentLoansAndGrants: "studentLoansAndGrants",
            otherBenefits: "otherBenefits",
            // Pension mappings
            statePension: "statePension",
            privatePension: "privatePension",
            pensionCredit: "pensionCredit",
            pensionOthers: "pensionOthers",
            // Asset mappings
            propertiesTotalValue: "totalValueProperties",
            propertiesMortgageOutstanding: "mortgageOutstanding",
            propertiesSecuredLoanOutstanding: "securedLoanOutstanding",
            valueOfVehicles: "valueOfVehicles",
            assetSavings: "assetSavings",
            otherAssets: "otherAssets",
          };

          if (fieldName in nestedFieldMappings) {
            apiFieldName = nestedFieldMappings[fieldName] || fieldName;
          }
        }

        const guidanceNote = getGuidanceNotesForField(
          apiFieldName,
          guidanceNotes
        );
        if (guidanceNote) {
          guidanceNotesData[`${fieldName}_guidance`] = guidanceNote;
        }
      });

      // Add trigger validation for fields that have triggers
      if (incomeTriggers.length > 0) {
        const triggerValidationData: Record<string, any> = {};

        schemaFields.forEach((fieldName: string) => {
          // Get the current value for this field from mapped data or form data
          let fieldValue = mappedData[fieldName];

          // Handle nested field values (like childBenefit.amount)
          if (
            typeof fieldValue === "object" &&
            fieldValue !== null &&
            "amount" in fieldValue
          ) {
            fieldValue = fieldValue.amount;
          }

          if (typeof fieldValue === "number") {
            // Try to find trigger for this field
            let apiFieldName = fieldName;

            // Handle nested benefit/pension/asset field mappings for triggers
            if (
              category === "benefits" ||
              category === "pension" ||
              category === "properties" ||
              category === "otherAssets"
            ) {
              const nestedFieldMappings: Record<string, string> = {
                // Benefits mappings
                childBenefit: "childBenefit",
                workingTaxCredit: "workingTaxCredit",
                childTaxCredit: "childTaxCredit",
                carerAllowance: "carerAllowance",
                housingBenefitLocalAllowance: "housingBenefitLocalAllowance",
                councilTaxReduction: "councilTaxReduction",
                universalCredit: "universalCredit",
                empsupportallowancestatutory: "empSupportAllowanceStatutory",
                disabilityAllowance: "dlaPipAttendanceAllowance",
                studentLoansAndGrants: "studentLoansAndGrants",
                otherBenefits: "otherBenefits",
                // Pension mappings
                statePension: "statePension",
                privatePension: "privatePension",
                pensionCredit: "pensionCredit",
                pensionOthers: "pensionOthers",
                // Asset mappings
                propertiesTotalValue: "totalValueProperties",
                propertiesMortgageOutstanding: "mortgageOutstanding",
                propertiesSecuredLoanOutstanding: "securedLoanOutstanding",
                valueOfVehicles: "valueOfVehicles",
                assetSavings: "assetSavings",
                otherAssets: "otherAssets",
              };

              if (fieldName in nestedFieldMappings) {
                apiFieldName = nestedFieldMappings[fieldName] || fieldName;
              }
            }

            const triggerResult = checkFieldTrigger(
              fieldValue,
              apiFieldName,
              incomeTriggers
            );
            if (triggerResult.exceeded) {
              triggerValidationData[`${fieldName}_trigger`] = {
                exceeded: true,
                message: triggerResult.message,
                trigger: triggerResult.trigger,
              };
            }
          }
        });

        // Start with mapped data + guidance + triggers + metadata
        let result: Record<string, unknown> = {
          ...mappedData,
          ...guidanceNotesData,
          ...triggerValidationData,
          ...customerMetadata,
        };

        // Overlay any locally cached form data so unsaved edits survive navigation
        if (savedFormData && Object.keys(savedFormData).length > 0) {
          result = {
            ...result,
            ...savedFormData,
          };
        }

        // For arrears-backed fields, ensure arrears + inArrears from the latest
        // Debts mapping win over any stale cached values.
        if (arrearsFieldNames.size > 0) {
          arrearsFieldNames.forEach((fieldName) => {
            const mappedField = mappedData[fieldName];
            const resultField = result[fieldName];

            if (isPlainObject(mappedField) && isPlainObject(resultField)) {
              const { arrears, inArrears } =
                mappedField as Record<string, unknown>;
              const resultObj = resultField as Record<string, unknown>;

              result[fieldName] = {
                ...resultObj,
                ...(arrears !== undefined ? { arrears } : {}),
                ...(inArrears !== undefined ? { inArrears } : {}),
              };
            } else if (mappedField !== undefined) {
              result[fieldName] = mappedField;
            }
          });
        }

        // Apply dynamicMeta LAST so help text placeholders are always current
        result = {
          ...result,
          ...dynamicMeta,
        };

        return result;
      }

      // No triggers – use mapped data + guidance + metadata + dynamic meta
      let result: Record<string, unknown> = {
        ...mappedData,
        ...guidanceNotesData,
        ...customerMetadata,
      };

      // Overlay cached form data
      if (savedFormData && Object.keys(savedFormData).length > 0) {
        result = {
          ...result,
          ...savedFormData,
        };
      }

      // Apply dynamicMeta LAST so help text placeholders are always current
      // (cached form data should not override calculated totals)
      result = {
        ...result,
        ...dynamicMeta,
      };

      // Ensure arrears from Debts mapping win
      if (arrearsFieldNames.size > 0) {
        arrearsFieldNames.forEach((fieldName) => {
          const mappedField = mappedData[fieldName];
          const resultField = result[fieldName];

          if (isPlainObject(mappedField) && isPlainObject(resultField)) {
            const { arrears, inArrears } =
              mappedField as Record<string, unknown>;
            const resultObj = resultField as Record<string, unknown>;

            result[fieldName] = {
              ...resultObj,
              ...(arrears !== undefined ? { arrears } : {}),
              ...(inArrears !== undefined ? { inArrears } : {}),
            };
          } else if (mappedField !== undefined) {
            result[fieldName] = mappedField;
          }
        });
      }

      return result;
    }

    // No guidance notes – mapped data + metadata
    let result: Record<string, unknown> = {
      ...mappedData,
      ...customerMetadata,
    };

    // Overlay cached form data
    if (savedFormData && Object.keys(savedFormData).length > 0) {
      result = {
        ...result,
        ...savedFormData,
      };
    }

    // Ensure arrears from Debts mapping win
    if (arrearsFieldNames.size > 0) {
      arrearsFieldNames.forEach((fieldName) => {
        const mappedField = mappedData[fieldName];
        const resultField = result[fieldName];

        if (isPlainObject(mappedField) && isPlainObject(resultField)) {
          const { arrears, inArrears } =
            mappedField as Record<string, unknown>;
          const resultObj = resultField as Record<string, unknown>;

          result[fieldName] = {
            ...resultObj,
            ...(arrears !== undefined ? { arrears } : {}),
            ...(inArrears !== undefined ? { inArrears } : {}),
          };
        } else if (mappedField !== undefined) {
          result[fieldName] = mappedField;
        }
      });
    }

    // Apply dynamicMeta LAST so help text placeholders are always current
    result = {
      ...result,
      ...dynamicMeta,
    };

    return result;
  }, [
    incomeData,
    expenseData,
    incomeNotes,
    expenseNotes,
    calculatedTotals,
    selectedOuter,
    selectedInner,
    currentSchema,
    guidanceNotes,
    incomeTriggers,
    allFormData,
    customerMetadata,
    debtData,
    mappedDebts,
    debtsAllAddedFlag,
  ]);

  // TODO: Implement field-level guidance and validation info when backend endpoints are ready
  // const getFieldMetadata = useCallback((schemaFieldName: string, apiFieldName?: string) => {
  //   const metadata: {
  //     guidanceNote?: string | null;
  //     triggerInfo?: ReturnType<typeof checkFieldTriggerForValue>;
  //   } = {};
  //   // Get guidance note if API field name is provided
  //   if (apiFieldName) {
  //     metadata.guidanceNote = getGuidanceNoteForField(apiFieldName);
  //   }
  //   return metadata;
  // }, []);

  // const getGuidanceNoteForField = useCallback((apiFieldName: string): string | null => {
  //   return null; // Placeholder until backend is ready
  // }, []);

  // const checkFieldTriggerForValue = useCallback((fieldValue: number, apiFieldName: string) => {
  //   return null; // Placeholder until backend is ready
  // }, []);

  // Collect all form data for PUT submission
  const collectAllFormData = useCallback(async () => {
    const collectedFormData: Record<string, unknown> = {};

    // Generate all possible form IDs based on the routing configuration
    const allPossibleFormIds: string[] = [];

    // Iterate through all outer routes and their inner routes to generate form IDs
    Object.entries(innerRoutesConfig).forEach(([outerKey, innerRoutes]) => {
      innerRoutes.forEach((innerRoute) => {
        const formId = `${outerKey}-${innerRoute.key}-form`;
        allPossibleFormIds.push(formId);
      });
    });

    // Collect data from all form IDs that have data
    allPossibleFormIds.forEach((formId) => {
      const formData = allFormData[formId];
      if (formData) {
        collectedFormData[formId] = formData;
      }
    });

    return collectedFormData;
  }, [allFormData, innerRoutesConfig]);

  // Log button actions for analytics
  // (moved earlier above to avoid use-before-declare warnings)

  // Save all budget data
  const saveBudgetData = useCallback(
    async (opts?: { source?: "auto" | "manual" }) => {
      const source = opts?.source ?? "manual";
      try {
        setLoading("budget-save", true);

        // Resolve customer ID
        const customerId = resolveCustomerId();

        const userId = resolveUserId();

        if (!customerId) {
          throw new Error("Customer ID not available");
        }

        if (!userId) {
          throw new Error("User ID not available");
        }

        // Collect all form data from the form store
        // Zustand already has the latest data from onValuesChange (transformed & filtered)
        const allCollectedFormData = await collectAllFormData();

        // Determine current category
        let category: keyof typeof BUDGET_FIELD_MAPPINGS = "salary";

        switch (selectedOuter) {
          case "income":
            if (selectedInner === "salary") category = "salary";
            else if (selectedInner === "benefits") category = "benefits";
            else if (selectedInner === "pension") category = "pension";
            else if (selectedInner === "other-sources")
              category = "otherSources";
            break;
          case "assets":
            if (selectedInner === "properties") category = "properties";
            else if (selectedInner === "otherAssets") category = "otherAssets";
            break;
          case "fixed-expenses":
            if (selectedInner === "household") category = "household";
            else if (selectedInner === "utilities") category = "utilities";
            else if (selectedInner === "care") category = "care";
            else if (selectedInner === "transport") category = "transport";
            else if (selectedInner === "school") category = "school";
            else if (selectedInner === "pension-insurance")
              category = "pensionInsurance";
            else if (selectedInner === "professionals")
              category = "professionals";
            else if (selectedInner === "other-sources")
              category = "otherSourcesFixed";
            break;
          case "flexible-expenses":
            if (selectedInner === "leisure") category = "leisure";
            else if (selectedInner === "housekeeping")
              category = "housekeeping";
            else if (selectedInner === "personal-costs")
              category = "personalCosts";
            else if (selectedInner === "savings") category = "savings";
            break;
          case "debts":
            if (selectedInner === "debts-administration")
              category = "debtsAdministration";
            else if (selectedInner === "debts") category = "debts";
            break;
        }

        // Get current form data for this category using the unique form ID
        const currentFormId = `${selectedOuter}-${selectedInner}-form`;
        const storedFormData: Record<string, unknown> =
          (allCollectedFormData[currentFormId] as Record<string, unknown>) ||
          ({} as Record<string, unknown>);

        // IMPORTANT:
        // - For JSON‑driven screens (all non‑debts sections), React Hook Form keeps the
        //   latest values in its internal state.
        // - In some fast navigation cases, the Zustand store can lag behind by one
        //   change, so relying *only* on storedFormData may result in sending the
        //   previous value (e.g. 40 instead of 50) when auto‑saving on section change.
        //
        // To make auto‑save behave like the Continue button:
        // - Always merge the latest live values snapshot from react‑hook‑form
        //   (liveBudgetFormValuesRef) into the stored form data.
        // - liveBudgetFormValuesRef contains flat "dot notation" keys, so we transform
        //   them to the nested structure and then deep‑merge.
        //
        // This ensures that both manual saves and auto‑saves use exactly the same
        // latest values, fixing the "old value on section change" bug for assets
        // (e.g. 1‑properties.json) and other categories.
        let effectiveFormData: Record<string, unknown> = storedFormData;
        if (
          Object.keys(liveBudgetFormValuesRef.current).length > 0 &&
          // Debts screen uses a custom, non‑JSON renderer – keep its data path isolated
          !(selectedOuter === "debts" && selectedInner === "debts")
        ) {
          // Only merge live values that belong to the current schema.
          // This prevents stale values from other sections (or previously-visible fields)
          // from leaking into the payload when a field/section is currently hidden.
          const liveFiltered: Record<string, unknown> = {};
          Object.entries(liveBudgetFormValuesRef.current).forEach(([key, val]) => {
            if (typeof key === "string" && currentSchemaFields.has(key)) {
              liveFiltered[key] = val;
            }
          });

          if (Object.keys(liveFiltered).length > 0) {
          const transformedLive = transformDotNotationToNested(
            liveFiltered
          );
          effectiveFormData = mergeNestedFormValues(
            storedFormData,
            transformedLive
          );
          }
        }

        // Never mutate `storedFormData` directly (Zustand state). Always work on a copy.
        let currentFormData: Record<string, unknown> = {
          ...(effectiveFormData || {}),
        };

        // If some salary fields are hidden (e.g. customer is Unemployed), those fields won't
        // be present/updated in react-hook-form, but old values can still linger in the
        // stored form cache (Zustand) and get re-sent on auto-save.
        //
        // To match legacy behavior, we explicitly clear hidden Salary question-groups
        // before mapping to the Income PUT payload so they can never "spring back".
        if (category === "salary") {
          const customerStatus =
            customerMetadata.customerEmploymentStatus ??
            (profileSnapshot as any)?.customerEmploymentStatus ??
            readStoredCustomerData()?.customerEmploymentStatus;
          const partnerStatus =
            customerMetadata.partnerEmploymentStatus ??
            (profileSnapshot as any)?.partnerEmploymentStatus ??
            readStoredCustomerData()?.partnerEmploymentStatus;
          const jointAccount =
            customerMetadata.jointAccount ??
            (profileSnapshot as any)?.jointAccount ??
            readStoredCustomerData()?.jointAccount;

          const isCustomerUnemployed =
            typeof customerStatus === "string" && customerStatus === "Unemployed";
          const hasNoPartner =
            typeof jointAccount === "string" &&
            (jointAccount === "N" || jointAccount === "n");
          const isPartnerUnemployed =
            typeof partnerStatus === "string" && partnerStatus === "Unemployed";

          // takeHomeSalary hidden when customerEmploymentStatus === 'Unemployed'
          if (isCustomerUnemployed) {
            // Keep modes untouched (avoid spurious PUTs); only force amount to 0.
            (currentFormData as any).takeHomeSalary = { amount: 0 };
          }

          // partnerTakeHomeSalary hidden when jointAccount === 'N' OR partnerEmploymentStatus === 'Unemployed'
          if (hasNoPartner || isPartnerUnemployed) {
            // Keep modes untouched (avoid spurious PUTs); only force amount to 0.
            (currentFormData as any).partnerTakeHomeSalary = { amount: 0 };
          }

          // otherSalarySource hidden when:
          // customerEmploymentStatus in the "non-working" list AND (no partner OR partnerEmploymentStatus in same list)
          const nonWorkingStatuses = new Set<string>([
            "Unemployed",
            "Retired",
            "Carer",
            "Student",
            "Other",
            "Not working due to illness/disability",
            "Not working due to illness/disability",
          ]);
          const isCustomerNonWorking =
            typeof customerStatus === "string" && nonWorkingStatuses.has(customerStatus);
          const isPartnerNonWorking =
            typeof partnerStatus === "string" && nonWorkingStatuses.has(partnerStatus);

          if (isCustomerNonWorking && (hasNoPartner || isPartnerNonWorking)) {
            // Keep modes untouched (avoid spurious PUTs); only force amount to 0.
            (currentFormData as any).otherSalarySource = { amount: 0 };
          }
        }

        // Handle Debts screen separately: dialog Save already persists individual debts.
        // On Continue/Save here we should:
        // - NEVER create/update/delete debts again (to avoid double PUT/POST)
        // - ONLY update metadata (allDebtsAdded flag) and refresh local snapshots.
        if (selectedOuter === "debts" && selectedInner === "debts") {
          // For the Debts main screen we only want to *snapshot* on auto-save,
          // not call any APIs. Dialog "Save" buttons in DebtCategory already
          // persist changes to the Debts API.
          if (source === "auto") {
            const currentFormIdForDebts = `${selectedOuter}-${selectedInner}-form`;
            try {
              lastSavedSnapshotRef.current[currentFormIdForDebts] =
                stableStringify(allFormData[currentFormIdForDebts] || {});
              lastSavedAtRef.current[currentFormIdForDebts] = Date.now();
            } catch {
              // ignore snapshot errors on auto-save
            }
            return;
          }

          const normalizeBoolean = (value: unknown): boolean | undefined => {
            if (typeof value === "boolean") {
              return value;
            }
            if (typeof value === "string") {
              const normalized = value.trim().toLowerCase();
              if (["true", "y", "yes", "1"].includes(normalized)) {
                return true;
              }
              if (["false", "n", "no", "0"].includes(normalized)) {
                return false;
              }
            }
            return undefined;
          };

          const debtsFormData = currentFormData as Partial<
            DebtsSchemaState & Record<string, unknown>
          >;

          const resolvedAllDebtsAdded = normalizeBoolean(
            debtsFormData?.allDebtsAdded
          );

          // Only update expense API and refresh debts on manual save (this path),
          // not auto-save. Debts themselves are already saved via dialog.
            if (
              typeof resolvedAllDebtsAdded === "boolean" &&
              resolvedAllDebtsAdded !== debtsAllAddedFlag
            ) {
              const baseExpenseRecord = expenseData[0];
              if (baseExpenseRecord) {
                const expenseUpdatePayload: ExpenseUpdateRequest = {
                  ...baseExpenseRecord,
                  field4TxtFld: resolvedAllDebtsAdded ? "Y" : "N",
                  lastUpdatedBy:
                    Number(userId) || baseExpenseRecord.lastUpdatedBy,
                  lastUpdateDate: new Date().toISOString(),
                };

                try {
                  await BudgetService.updateExpenseData(expenseUpdatePayload);

                  setExpenseData((prev) => {
                    if (!Array.isArray(prev) || prev.length === 0) {
                      return prev;
                    }
                    const [first, ...rest] = prev;
                    if (!first) {
                      return prev;
                    }
                    const resolvedLastUpdateDate: string =
                    expenseUpdatePayload.lastUpdateDate ?? first.lastUpdateDate;
                    const updatedFirst: ExpenseDataRecord = {
                      ...first,
                      field4TxtFld:
                        expenseUpdatePayload.field4TxtFld ?? first.field4TxtFld,
                      lastUpdatedBy: expenseUpdatePayload.lastUpdatedBy,
                      lastUpdateDate: resolvedLastUpdateDate,
                    };
                    return [updatedFirst, ...rest];
                  });
                } catch (expenseError) {
                console.error("💰 Error updating expense record:", expenseError);
                  throw expenseError;
                }
              }
            }

          // Light refresh of debts to keep local state in sync with what the
          // dialogs have already saved. This is read-only and does not create/update.
          try {
            const refreshedDebts = await DebtsService.getDebts(customerId, {
              skipErrorCapture: true,
            });
            if (refreshedDebts.success && Array.isArray(refreshedDebts.data)) {
              const enrichedDebts: CustomerDebtRecord[] = await (async () => {
                const items = refreshedDebts.data as CustomerDebtRecord[];
                const tasks = items.map(async (d) => {
                  if (
                    d &&
                    typeof d.id === "number" &&
                    d.id > 0 &&
                    d.accountNumber &&
                    (d.paymentOfferAmount == null || d.paymentOfferAmount === 0)
                  ) {
                    try {
                      const resp = await DebtsService.getPaymentOffer(
                        Number(customerId),
                        d.id,
                        d.accountNumber,
                        { skipErrorCapture: true }
                      );
                      const payload = (resp?.data as any) ?? null;
                      // Take the last (most recent) payment offer when multiple exist
                      const record =
                        Array.isArray(payload) && payload.length > 0
                          ? (payload[payload.length - 1] as any)
                          : payload;
                      if (record) {
                        // Debug: surface the mapped payment-offer data to the console
                        // so we can verify the amount/day flowing from API → debts → UI.
                        // eslint-disable-next-line no-console
                        console.log("[Debts] Enriched payment offer (post-save refresh)", {
                          customerId,
                          debtId: d.id,
                        });

                        return {
                          ...d,
                          paymentOfferAmount: (record.paymentOfferAmount ??
                            d.paymentOfferAmount ??
                            null) as number | null,
                          paymentOfferMode: (record.paymentPreferredFrequency ??
                            record.paymentOfferMode ??
                            d.paymentOfferMode ??
                            null) as any,
                          paymentOfferId: record.id ?? null,
                        } as CustomerDebtRecord;
                      }
                    } catch (e) {
                      // Debug: log failures so we can see if any debt fails enrichment
                      // eslint-disable-next-line no-console
                      console.warn("[Debts] Failed to enrich payment offer (post-save refresh)", {
                        customerId,
                        debtId: d.id,
                        error: e,
                      });
                    }
                  }
                  return d;
                });
                return await Promise.all(tasks);
              })();

              setDebtData(enrichedDebts);

              // Also refresh the Debts form slice from API-mapped schema so the
              // list reflects whatever the dialog has already persisted.
              const idToCode: Record<number, string> = {};
              const labelToCode: Record<string, string> = {};
              for (const rec of debtAccountTypes) {
                if (typeof rec?.id === "number" && rec.code) {
                  idToCode[rec.id] = rec.code;
                }
                if (rec?.label && rec.code) {
                  labelToCode[rec.label.trim().toLowerCase()] = rec.code;
                }
              }

              const refreshedSchema = mapDebtApiToSchema(enrichedDebts, {
                includeRaw: true,
                allDebtsAdded:
                  typeof resolvedAllDebtsAdded === "boolean"
                    ? resolvedAllDebtsAdded
                    : debtsAllAddedFlag,
                accountTypeIdToCodeMap: idToCode,
                accountTypeLabelToCodeMap: labelToCode,
              });

              liveBudgetFormValuesRef.current =
                refreshedSchema as unknown as Record<string, unknown>;
              setFormData(
                currentFormId,
                refreshedSchema as unknown as Record<string, unknown>
              );
            }
          } catch (refreshError) {
            console.error("💰 Error refreshing debts after save:", refreshError);
          }

          // Update baseline snapshot after successful metadata save
          try {
            lastSavedSnapshotRef.current[currentFormId] =
              stableStringify(currentFormData);
            lastSavedAtRef.current[currentFormId] = Date.now();
          } catch {}

          showAlert({
            severity: "success",
            message: "Debts saved successfully!",
            autoHide: true,
            duration: 3000,
          });
          await logButtonAction("Save Budget Data");
          return;
        }

        // Determine if this is income/asset or expense category
        // Exception: savings category uses income API despite being under flexible-expenses
        const incomeAssetCategories = [
          "salary",
          "benefits",
          "pension",
          "otherSources",
          "properties",
          "otherAssets",
          "savings",
        ];
        const isIncomeOrAsset = incomeAssetCategories.includes(category);

        // Update based on category type
        if (isIncomeOrAsset) {
          // INCOME & ASSETS UPDATE
          const baseIncomeRecord =
            latestIncomeRecordRef.current ?? incomeData[0];
          if (!baseIncomeRecord) {
            console.error("⚠️ No base income data available. Skipping save.");
            // For manual saves, surface a user-facing error; for auto-saves, fail silently. let's not show error for any
            // if (source !== "auto") {
            //   showAlert({
            //     severity: "error",
            //     message:
            //       "Unable to save: No existing income data found. Please contact support.",
            //     autoHide: true,
            //     duration: 5000,
            //   });
            // }
            return;
          }

          // Prune hidden question-groups (e.g. JSA) so cached hidden values can't be saved on leave.
          // This also clears backend amounts only when they were previously non-zero, avoiding
          // unnecessary PUTs for null/default fields.
          const prunedIncome = pruneHiddenQuestionGroups(
            currentSchema,
            category,
            currentFormData,
            baseIncomeRecord as unknown as Record<string, unknown>
          );
          if (prunedIncome.changed) {
            currentFormData = prunedIncome.data;
            setFormData(currentFormId, currentFormData);
            liveBudgetFormValuesRef.current =
              buildLiveDotValuesFromNested(currentFormData);
          }

          // --- UU Evidence & Document Pre-Save Cleanup ---
          // BEFORE saving, check if any UU benefit fields have amount = 0 while
          // a date (evidence period) and/or document still exist. If so, prompt
          // the user:
          //   "Remove" → delete evidence + document, then proceed to save.
          //   "Keep"   → revert form to previous saved state, abort save & navigation.
          if (category === "benefits" || category === "pension") {
            try {
              const cleanupRecords = await detectCleanupNeeded(
                customerId,
                currentFormData,
                category
              );

              if (cleanupRecords.length > 0) {
                const confirmed = await openConfirmDialog({
                  title: "Remove benefit data",
                  message: buildCleanupConfirmMessage(cleanupRecords),
                  confirmText: "Remove",
                  cancelText: "Keep",
                });

                if (confirmed) {
                  // Delete evidence dates and documents
                  const failures = await deleteCleanupRecords(
                    cleanupRecords,
                    customerId
                  );
                  if (failures.length > 0) {
                    console.warn(
                      "[uuCleanup] Some records could not be deleted:",
                      failures
                    );
                    showAlert({
                      severity: "error",
                      message:
                        "Some benefit data could not be removed. Please try again.",
                      autoHide: true,
                      duration: 4000,
                    });
                  }
                  // Proceed to save the cleared amounts
                } else {
                  // User chose "Keep" — do NOT revert the store or liveRef.
                  // The form input still shows 0 and we can't update react-hook-form
                  // from here. By leaving the store as-is (amount = 0), subsequent
                  // navigation attempts will trigger the same prompt again, keeping
                  // the user on this page until they either:
                  //   a) click "Remove" (deletes date + document, saves 0), or
                  //   b) manually re-enter a non-zero amount.
                  const keepSignal = new Error("__UU_EVIDENCE_KEEP__");
                  keepSignal.name = "UuEvidenceKeepSignal";
                  throw keepSignal;
                }
              }
            } catch (cleanupError) {
              // Re-throw the "Keep" signal so navigation is aborted
              if (
                cleanupError instanceof Error &&
                cleanupError.name === "UuEvidenceKeepSignal"
              ) {
                throw cleanupError;
              }
              // Other errors are non-critical; don't block the save flow
              console.warn(
                "[uuCleanup] Pre-save cleanup check failed:",
                cleanupError
              );
            }
          }

          // Map form data to income API format
          const mappedIncomeData = mapSchemaDataToIncome(
            currentFormData,
            category,
            baseIncomeRecord
          );
          // Extract notes for income notes API
          const mappedIncomeNotes = mapSchemaDataToIncomeNotes(
            currentFormData,
            category
          );

          const incomeDataNeedsUpdate = hasMeaningfulDiff(
            mappedIncomeData as unknown as Record<string, unknown>,
            baseIncomeRecord as unknown as Record<string, unknown>,
            new Set(["lastUpdatedBy", "lastUpdateDate"])
          );

          const baseIncomeNote = (latestIncomeNoteRef.current ??
            incomeNotes?.[0]) as
            | (IncomeNoteData & Record<string, unknown>)
            | undefined;
          const incomeNotesNeedsUpdate =
            Object.keys(mappedIncomeNotes).length > 0 &&
            Array.isArray(incomeNotes) &&
            incomeNotes.length > 0 &&
            Boolean(baseIncomeNote) &&
            hasNotesDiff(
              mappedIncomeNotes as Record<string, string>,
              baseIncomeNote as unknown as Record<string, unknown>
            );

          let didUpdateIncomeData = false;
          let didUpdateIncomeNotes = false;

          if (incomeDataNeedsUpdate) {
            // Prepare income update payload
            const incomeUpdatePayload = {
              ...baseIncomeRecord,
              ...mappedIncomeData,
              customerId: Number(customerId),
              lastUpdatedBy: Number(userId),
              lastUpdateDate: new Date().toISOString(),
            } as IncomeUpdateRequest;

            // Update income data
            await BudgetService.updateIncomeData(incomeUpdatePayload);
            didUpdateIncomeData = true;

            setIncomeData((prev) => {
              const nextRecord = {
                ...baseIncomeRecord,
                ...mappedIncomeData,
                customerId: Number(customerId),
                lastUpdatedBy: Number(userId),
                lastUpdateDate: incomeUpdatePayload.lastUpdateDate,
              } as IncomeDataRecord;

              if (!Array.isArray(prev) || prev.length === 0) {
                return [nextRecord];
              }

              const [, ...rest] = prev;
              return [nextRecord, ...rest];
            });
          }

          // Update income notes if there are any
          if (incomeNotesNeedsUpdate && baseIncomeNote) {
            const incomeNotesPayload: IncomeNoteUpdateRequest = {
              ...(baseIncomeNote as IncomeNoteData),
              ...mappedIncomeNotes,
              id: (baseIncomeNote as any).id,
              customerId: Number(customerId),
              lastUpdatedBy: Number(userId),
              lastUpdateDate: new Date().toISOString(),
            };

            await BudgetService.updateIncomeNotes(incomeNotesPayload);
            didUpdateIncomeNotes = true;

            setIncomeNotes((prev) => {
              const updated: IncomeNoteData = {
                ...(baseIncomeNote as IncomeNoteData),
                ...mappedIncomeNotes,
                id: (baseIncomeNote as any).id,
                customerId: Number(customerId),
                lastUpdatedBy:
                  typeof userId === "number"
                    ? String(userId)
                    : (baseIncomeNote as any).lastUpdatedBy,
                lastUpdateDate: incomeNotesPayload.lastUpdateDate,
              };

              if (!Array.isArray(prev) || prev.length === 0) {
                return [updated];
              }

              const [, ...rest] = prev;
              return [updated, ...rest];
            });
          }

          // After successful save, refresh local form store with the latest values
          if (didUpdateIncomeData || didUpdateIncomeNotes) {
            try {
            const [freshIncome, freshIncomeNotes] = await Promise.all([
              BudgetService.getIncomeData(customerId),
              BudgetService.getIncomeNotes(customerId),
            ]);

            const updatedIncomeRecord: IncomeDataRecord =
              freshIncome.success &&
              Array.isArray(freshIncome.data) &&
              freshIncome.data[0]
                ? (freshIncome.data[0] as IncomeDataRecord)
                : ({
                    ...(baseIncomeRecord as IncomeDataRecord),
                    ...mappedIncomeData,
                  } as IncomeDataRecord);

            const updatedIncomeNotesRecord: IncomeNoteData =
              freshIncomeNotes.success &&
              Array.isArray(freshIncomeNotes.data) &&
              freshIncomeNotes.data[0]
                ? (freshIncomeNotes.data[0] as IncomeNoteData)
                : (incomeNotes[0] as IncomeNoteData) ||
                  ({} as unknown as IncomeNoteData);

            if (freshIncome.success && Array.isArray(freshIncome.data)) {
              setIncomeData(freshIncome.data);
            }
            if (
              freshIncomeNotes.success &&
              Array.isArray(freshIncomeNotes.data)
            ) {
              setIncomeNotes(freshIncomeNotes.data);
            }

            const refreshedFormData = mapIncomeDataToSchema(
              updatedIncomeRecord,
              updatedIncomeNotesRecord as unknown as Parameters<
                typeof mapIncomeDataToSchema
              >[1],
              category
            ) as unknown as Record<string, unknown>;
            setFormData(currentFormId, refreshedFormData);
            liveBudgetFormValuesRef.current =
              buildLiveDotValuesFromNested(refreshedFormData);
            } catch {
            // Fallback: sync store from locally computed values when refresh fails
            const fallbackFormData = mapIncomeDataToSchema(
              {
                ...(baseIncomeRecord as IncomeDataRecord),
                ...mappedIncomeData,
              } as IncomeDataRecord,
              ((incomeNotes[0] as IncomeNoteData) ||
                ({} as unknown as IncomeNoteData)) as unknown as Parameters<
                typeof mapIncomeDataToSchema
              >[1],
              category
            ) as unknown as Record<string, unknown>;
            setFormData(currentFormId, fallbackFormData);
            liveBudgetFormValuesRef.current =
              buildLiveDotValuesFromNested(fallbackFormData);
            }
          }

          // (UU evidence + document cleanup is handled BEFORE the save — see above)
        } else {
          // EXPENSES UPDATE
          const baseExpenseRecord =
            latestExpenseRecordRef.current ?? expenseData[0];
          if (!baseExpenseRecord) {
            console.error("⚠️ No base expense data available. Skipping save.");
            // if (source !== "auto") {
            //   showAlert({
            //     severity: "error",
            //     message:
            //       "Unable to save: No existing expense data found. Please contact support.",
            //     autoHide: true,
            //     duration: 5000,
            //   });
            // }
            return;
          }

          // Prune hidden question-groups so cached hidden values can't be saved on leave.
          const prunedExpense = pruneHiddenQuestionGroups(
            currentSchema,
            category,
            currentFormData,
            baseExpenseRecord as unknown as Record<string, unknown>
          );
          if (prunedExpense.changed) {
            currentFormData = prunedExpense.data;
            setFormData(currentFormId, currentFormData);
            liveBudgetFormValuesRef.current =
              buildLiveDotValuesFromNested(currentFormData);
          }

          // Map form data to expense API format
          const mappedExpenseData = mapSchemaDataToExpense(
            currentFormData,
            category,
            baseExpenseRecord
          );

          // Extract notes for expense notes API
          const mappedExpenseNotes = mapSchemaDataToExpenseNotes(
            currentFormData,
            category
          );

          const expenseDataNeedsUpdate = hasMeaningfulDiff(
            mappedExpenseData as unknown as Record<string, unknown>,
            baseExpenseRecord as unknown as Record<string, unknown>,
            new Set(["lastUpdatedBy", "lastUpdateDate"])
          );

          const baseExpenseNote = (latestExpenseNoteRef.current ??
            expenseNotes?.[0]) as
            | (ExpensesNoteData & Record<string, unknown>)
            | undefined;
          const expenseNotesNeedsUpdate =
            Object.keys(mappedExpenseNotes).length > 0 &&
            Array.isArray(expenseNotes) &&
            expenseNotes.length > 0 &&
            Boolean(baseExpenseNote) &&
            hasNotesDiff(
              mappedExpenseNotes as Record<string, string>,
              baseExpenseNote as unknown as Record<string, unknown>
            );

          let didUpdateExpenseData = false;
          let didUpdateExpenseNotes = false;

          if (expenseDataNeedsUpdate) {
            // Prepare expense update payload
            const expenseUpdatePayload = {
              ...baseExpenseRecord,
              ...mappedExpenseData,
              customerId: Number(customerId),
              lastUpdatedBy: Number(userId),
              lastUpdateDate: new Date().toISOString(),
            } as ExpenseUpdateRequest;

            // Update expense data
            await BudgetService.updateExpenseData(expenseUpdatePayload);
            didUpdateExpenseData = true;

            setExpenseData((prev) => {
              const nextRecord = {
                ...baseExpenseRecord,
                ...mappedExpenseData,
                customerId: Number(customerId),
                lastUpdatedBy: Number(userId),
                lastUpdateDate: expenseUpdatePayload.lastUpdateDate,
              } as ExpenseDataRecord;

              if (!Array.isArray(prev) || prev.length === 0) {
                return [nextRecord];
              }

              const [, ...rest] = prev;
              return [nextRecord, ...rest];
            });
          }

          // Update expense notes if there are any
          if (expenseNotesNeedsUpdate && baseExpenseNote) {
            const expenseNotesPayload = {
              ...(baseExpenseNote as ExpensesNoteData),
              ...mappedExpenseNotes,
              id: (baseExpenseNote as any).id,
              customerId: Number(customerId),
              lastUpdatedBy: Number(userId),
              lastUpdateDate: new Date().toISOString(),
            };

            await BudgetService.updateExpenseNotes(expenseNotesPayload);
            didUpdateExpenseNotes = true;

            setExpenseNotes((prev) => {
              const updated = {
                ...(baseExpenseNote as ExpensesNoteData),
                ...mappedExpenseNotes,
                id: (baseExpenseNote as any).id,
                customerId: Number(customerId),
                lastUpdateDate: expenseNotesPayload.lastUpdateDate,
              } as ExpensesNoteData;

              if (!Array.isArray(prev) || prev.length === 0) {
                return [updated];
              }

              const [, ...rest] = prev;
              return [updated, ...rest];
            });
          }

          // Arrears persistence now happens only from the arrears dialog (Debts screen).
          // Do not persist arrears from Update Budget saves to avoid unintended overwrites
          // when navigating between sections.
          // After successful save, refresh local form store with the latest values
          if (didUpdateExpenseData || didUpdateExpenseNotes) {
            try {
            const [freshExpense, freshExpenseNotes, freshDebts] =
              await Promise.all([
                BudgetService.getExpenseData(customerId),
                BudgetService.getExpenseNotes(customerId),
                DebtsService.getDebts(customerId), // Also refresh debts to include arrears
              ]);

            const updatedExpenseRecord: ExpenseDataRecord =
              freshExpense.success &&
              Array.isArray(freshExpense.data) &&
              freshExpense.data[0]
                ? (freshExpense.data[0] as ExpenseDataRecord)
                : ({
                    ...(baseExpenseRecord as ExpenseDataRecord),
                    ...mappedExpenseData,
                  } as ExpenseDataRecord);

            const updatedExpenseNotesRecord: ExpensesNoteData =
              freshExpenseNotes.success &&
              Array.isArray(freshExpenseNotes.data) &&
              freshExpenseNotes.data[0]
                ? (freshExpenseNotes.data[0] as ExpensesNoteData)
                : (expenseNotes[0] as ExpensesNoteData) ||
                  ({} as unknown as ExpensesNoteData);

            if (freshExpense.success && Array.isArray(freshExpense.data)) {
              setExpenseData(freshExpense.data);
            }
            if (
              freshExpenseNotes.success &&
              Array.isArray(freshExpenseNotes.data)
            ) {
              setExpenseNotes(freshExpenseNotes.data);
            }
            if (freshDebts.success && Array.isArray(freshDebts.data)) {
              setDebtData(freshDebts.data);
            }

            let refreshedFormData = mapExpenseDataToSchema(
              updatedExpenseRecord,
              updatedExpenseNotesRecord as unknown as Parameters<
                typeof mapExpenseDataToSchema
              >[1],
              category
            ) as unknown as Record<string, unknown>;

            // Merge arrears from debts into refreshed form data
            if (
              freshDebts.success &&
              Array.isArray(freshDebts.data) &&
              freshDebts.data.length > 0
            ) {
              const expenseCategoriesWithArrears = ["household", "utilities"];
              if (expenseCategoriesWithArrears.includes(category)) {
                const arrearsData = mapArrearsDebtsToExpenseSchema(
                  freshDebts.data,
                  category
                );
                Object.keys(arrearsData).forEach((fieldName) => {
                  if (
                    arrearsData[fieldName] &&
                    arrearsData[fieldName].arrears
                  ) {
                    if (!refreshedFormData[fieldName]) {
                      refreshedFormData[fieldName] = {};
                    }
                    const fieldData = refreshedFormData[fieldName] as Record<
                      string,
                      unknown
                    >;
                    fieldData.arrears = arrearsData[fieldName].arrears;
                    fieldData.inArrears =
                      Array.isArray(arrearsData[fieldName].arrears) &&
                      arrearsData[fieldName].arrears.length > 0;
                  }
                });
              }
            }

            setFormData(currentFormId, refreshedFormData);
            liveBudgetFormValuesRef.current =
              buildLiveDotValuesFromNested(refreshedFormData);
            } catch {
            // Fallback: sync store from locally computed values when refresh fails
            const fallbackFormData = mapExpenseDataToSchema(
              {
                ...(baseExpenseRecord as ExpenseDataRecord),
                ...mappedExpenseData,
              } as ExpenseDataRecord,
              ((expenseNotes[0] as ExpensesNoteData) ||
                ({} as unknown as ExpensesNoteData)) as unknown as Parameters<
                typeof mapExpenseDataToSchema
              >[1],
              category
            ) as unknown as Record<string, unknown>;
            setFormData(currentFormId, fallbackFormData);
            liveBudgetFormValuesRef.current =
              buildLiveDotValuesFromNested(fallbackFormData);
            }
          }
        }

        // Refresh calculated totals after saving income/expenses so Savings help text placeholders
        // (balanceAmount/totalOutgoings) reflect the latest backend totals when navigating back.
        // Skip on Debts pages to avoid unnecessary calls (Debts screens don’t use these totals).
        if (selectedOuter !== "debts") {
          await fetchCalculatedTotals({ customerId, withSpinner: false });
        }

        // Update baseline snapshot after successful auto/manual save
        try {
          lastSavedSnapshotRef.current[currentFormId] =
            stableStringify(currentFormData);
          lastSavedAtRef.current[currentFormId] = Date.now();
        } catch {}

        if (source !== "auto") {
          // showAlert({
          //   severity: "success",
          //   message: "Budget data saved successfully!",
          //   autoHide: true,
          //   duration: 3000,
          // });
          // Log the save action
          await logButtonAction("Save Budget Data");
        }
      } catch (error) {
        // UU Evidence "Keep" signal — user chose to keep the benefit start date
        // and stay on the current section. Re-throw silently (no error toast)
        // so the navigation handler aborts navigation.
        if (
          error instanceof Error &&
          error.name === "UuEvidenceKeepSignal"
        ) {
          throw error;
        }

        console.error("❌ Failed to save budget data:", error);
        console.error("❌ Error type:", typeof error);
        console.error(
          "❌ Error stack:",
          error instanceof Error ? error.stack : "No stack trace"
        );
        console.error("❌ Current category:", { selectedOuter, selectedInner });
        console.error("❌ All form data at time of error:", allFormData);
        console.error("❌ Current form ID:", currentFormId);

        const isSessionExpiredError =
          error instanceof Error && error.name === "SessionExpiredError";

        // Avoid showing a duplicate "Your session has expired" toast here.
        // The global unauthorized handler in AppShell listens for session expiry
        // and shows a single red error message plus redirects to /auth.
        if (!isSessionExpiredError) {
          showAlert({
            severity: "error",
            message:
              error instanceof Error
                ? error.message
                : "Failed to save budget data. Please try again.",
            autoHide: true,
            duration: 5000,
          });
        }

        // For manual saves, propagate the error so the caller can block navigation.
        // For auto-saves (triggered on navigation), do NOT re-throw – navigation should continue.
        if (source !== "auto") {
          throw error;
        }
      } finally {
        setLoading("budget-save", false);
      }
    },
    [
      incomeData,
      expenseData,
      incomeNotes,
      expenseNotes,
      selectedOuter,
      selectedInner,
      showAlert,
      hasMeaningfulDiff,
      hasNotesDiff,
      logButtonAction,
      collectAllFormData,
      setLoading,
      resolveCustomerId,
      resolveUserId,
      fetchCalculatedTotals,
    ]
  );

  // Keep the saveBudgetData ref updated so navigation callbacks always use the latest version
  useEffect(() => {
    saveBudgetDataRef.current = saveBudgetData;
  }, [saveBudgetData]);

  // Handle global "leave budget and navigate" requests (e.g. from Header or BottomTabBar navigation)
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handler = (event: Event) => {
      const custom = event as CustomEvent<{ targetPath?: string }>;
      const targetPath = custom.detail?.targetPath;
      if (!targetPath) return;

      void (async () => {
        setIsNavigating(true);
        try {
          await saveBudgetData({ source: "auto" });
          navigate(targetPath);
        } catch {
          // Save errors are already surfaced via showAlert in saveBudgetData.
          // Do not navigate away if save fails.
        } finally {
          // Small delay for smoother UX and to ensure route transition starts
          setTimeout(() => setIsNavigating(false), 300);
        }
      })();
    };

    window.addEventListener("iehub:budget-before-leave", handler as EventListener);
    return () => {
      window.removeEventListener(
        "iehub:budget-before-leave",
        handler as EventListener
      );
    };
  }, [navigate, saveBudgetData, setIsNavigating]);

  // Generate current form ID based on selection
  const currentFormId = useMemo(() => {
    return `${selectedOuter}-${selectedInner}-form`;
  }, [selectedOuter, selectedInner]);

  // Initialize baseline snapshot after data load to avoid first auto-save firing on mount
  useEffect(() => {
    if (isBudgetDataLoading) return;
    const formId = `${selectedOuter}-${selectedInner}-form`;
    if (lastSavedSnapshotRef.current[formId] === undefined) {
      try {
        lastSavedSnapshotRef.current[formId] = stableStringify(
          allFormData[formId] || {}
        );
        lastSavedAtRef.current[formId] = Date.now();
      } catch {}
    }
  }, [
    isBudgetDataLoading,
    selectedOuter,
    selectedInner,
    allFormData,
    stableStringify,
  ]);

  // Helper to check if there's a next outer route
  const getNextOuterRoute = useCallback(() => {
    const currentOuterIndex = outerRoutes.findIndex(
      (route) => route.key === selectedOuter
    );
    if (currentOuterIndex >= 0 && currentOuterIndex < outerRoutes.length - 1) {
      return outerRoutes[currentOuterIndex + 1];
    }
    return null;
  }, [selectedOuter]);

  // Next button label based on position
  const innerIndex = getInnerIndex();
  const hasNextInner =
    innerIndex >= 0 && innerIndex < currentInnerRoutes.length - 1;
  const nextOuterRoute = getNextOuterRoute();
  const hasNext = hasNextInner || nextOuterRoute !== null;
  const nextButtonLabel = hasNext ? "Continue" : "Continue";

  // Disable Save Changes on debts page until confirmation checkbox is checked.
  // If the checkbox is checked (whether from API or user interaction),
  // Continue must always be enabled.
  const nextButtonDisabled = useMemo(() => {
    const isDebtsFinalPage =
      selectedOuter === "debts" && selectedInner === "debts";

    if (!isDebtsFinalPage) {
      return false;
    }

    // Prefer current form values when they actually contain `allDebtsAdded`.
    // On first load, the Debts form slice may be an empty {} even though the
    // API has a value, so we must explicitly fall back to mappedDebts /
    // debtsAllAddedFlag when the field is missing.
    const storedValues = allFormData[
      currentFormId
    ] as Record<string, unknown> | undefined;

    const hasStoredAllDebtsAdded =
      storedValues !== undefined &&
      storedValues !== null &&
      Object.prototype.hasOwnProperty.call(storedValues, "allDebtsAdded");

    const currentValues: Record<string, unknown> = hasStoredAllDebtsAdded
      ? storedValues!
      : typeof mappedDebts?.allDebtsAdded !== "undefined"
        ? ({ allDebtsAdded: mappedDebts.allDebtsAdded } as Record<
            string,
            unknown
          >)
        : ({ allDebtsAdded: debtsAllAddedFlag } as Record<string, unknown>);

    const normalizeBoolean = (value: unknown): boolean => {
      if (value === true) return true;
      if (typeof value === "string") {
        const v = value.trim().toLowerCase();
        return v === "true" || v === "y" || v === "yes" || v === "1";
      }
      return false;
    };

    const allDebtsAddedValue = (currentValues as any).allDebtsAdded;
    const isChecked = normalizeBoolean(allDebtsAddedValue);

    // Button is disabled only when checkbox is not checked.
    return !isChecked;
  }, [selectedOuter, selectedInner, allFormData, currentFormId, mappedDebts, debtsAllAddedFlag]);

  // Handle form submission
  const handleFormSubmit = useCallback(
    async (data: unknown) => {
      try {
        // CRITICAL FIX: If data is empty, use the live ref values as fallback
        let formData = data as Record<string, unknown>;

        // If form data is empty or has no meaningful values, use live ref values
        const hasData =
          formData &&
          Object.keys(formData).length > 0 &&
          Object.values(formData).some(
            (val) => val !== undefined && val !== null && val !== ""
          );

        if (
          !hasData &&
          Object.keys(liveBudgetFormValuesRef.current).length > 0
        ) {
          console.warn(
            "⚠️ Form submitted with empty data, using live ref values as fallback"
          );
          formData = liveBudgetFormValuesRef.current;
        }

        // Store with unique form ID based on current selection
        const formId = `${selectedOuter}-${selectedInner}-form`;

        // Only keep schema-defined fields to avoid metadata pollution
        const submissionFiltered: Record<string, unknown> = {};
        Object.entries(formData).forEach(([key, value]) => {
          if (typeof key === "string" && currentSchemaFields.has(key)) {
            submissionFiltered[key] = value;
          }
        });

        const transformedFormData =
          transformDotNotationToNested(submissionFiltered);
        const existingData = allFormData[formId] ?? {};
        const mergedFormData = mergeNestedFormValues(
          existingData,
          transformedFormData
        );

        // Update Zustand state with merged data
        setFormData(formId, mergedFormData);

        // Persist changes to backend when the user clicks Continue/Save
        await saveBudgetData();

        // Only navigate if save was successful
        // Determine if we're on the absolute last page of the flow
        const lastOuterKey = outerRoutes[outerRoutes.length - 1]?.key;
        const lastOuterInnerRoutes =
          innerRoutesConfig[lastOuterKey as keyof typeof innerRoutesConfig] ||
          [];
        const lastInnerKey =
          lastOuterInnerRoutes[lastOuterInnerRoutes.length - 1]?.key;
        const isOnAbsoluteLastPage =
          selectedOuter === lastOuterKey && selectedInner === lastInnerKey;

        if (isOnAbsoluteLastPage) {
          // Last page - navigate to budget summary after save
          navigate("/budget?showSummary=true");
          return;
        }

        // Recompute navigation state AFTER save to avoid stale closures (non-last pages)
        const currentInnerIndex = currentInnerRoutes.findIndex(
          (r) => r.key === selectedInner
        );
        const currentHasNextInner =
          currentInnerIndex >= 0 &&
          currentInnerIndex < currentInnerRoutes.length - 1;
        const currentOuterIndex = outerRoutes.findIndex(
          (route) => route.key === selectedOuter
        );
        const currentNextOuterRoute =
          currentOuterIndex >= 0 && currentOuterIndex < outerRoutes.length - 1
            ? outerRoutes[currentOuterIndex + 1]
            : null;
        const currentHasNext =
          currentHasNextInner || currentNextOuterRoute !== null;

        if (currentHasNext) {
          await logButtonAction("Continue");
          if (currentHasNextInner) {
            const nextRoute = currentInnerRoutes[currentInnerIndex + 1];
            if (nextRoute) {
              await navigateToInner(nextRoute.key);
            }
          } else if (currentNextOuterRoute) {
            await navigateToOuter(currentNextOuterRoute.key);
          }
        } else {
          // Fallback: navigate to budget summary after save
          navigate("/budget?showSummary=true");
        }
      } catch (error) {
        console.error("❌ Form submission failed:", error);
        // Error is already shown by saveBudgetData's error handler
        // Don't navigate if save failed
        throw error; // Re-throw to let react-hook-form know submission failed
      }
    },
    [
      saveBudgetData,
      setFormData,
      selectedOuter,
      selectedInner,
      currentInnerRoutes,
      logButtonAction,
      navigateToInner,
      navigateToOuter,
      transformDotNotationToNested,
      currentSchemaFields,
      navigate,
      allFormData,
      outerRoutes,
    ]
  );

  const renderBudgetForm = useCallback(() => {
    // Custom non-JSON render for Debts second screen
    if (selectedOuter === "debts" && selectedInner === "debts") {
      // IMPORTANT:
      // - On mount we clear all form slices in Zustand by setting them to `{}`.
      //   For JSON-driven sections this is fine because `BudgetFormRenderer`
      //   rehydrates from `initialFormData`.
      // - For the Debts screen we read directly from the stored slice here.
      //   If we treat an empty `{}` as real data, we never fall back to the
      //   mapped API state (`initialFormData`), so existing debts never show.
      //
      // Only honour the stored slice when it actually contains data; otherwise,
      // fall back to `initialFormData` (built from `mapDebtApiToSchema`).
      const storedFormRaw =
        (allFormData[currentFormId] as unknown as
          | DebtsSchemaState
          | Record<string, unknown>
          | undefined);
      const hasStoredDebts =
        storedFormRaw !== undefined &&
        storedFormRaw !== null &&
        Object.keys(storedFormRaw as Record<string, unknown>).length > 0;

      // Prefer freshly mapped debts (which include latest payment-offer enrichment)
      // and only carry forward the `allDebtsAdded` flag from any cached slice.
      const baseMapped: DebtsSchemaState =
        (initialFormData as unknown as DebtsSchemaState) ||
        EMPTY_DEBTS_SCHEMA_STATE;

      let formValue: DebtsSchemaState = { ...baseMapped };
      if (
        hasStoredDebts &&
        typeof (storedFormRaw as any)?.allDebtsAdded === "boolean"
      ) {
        formValue = {
          ...formValue,
          allDebtsAdded: (storedFormRaw as any).allDebtsAdded as boolean,
        };
      }

      const safeArray = (arr: unknown): any[] =>
        Array.isArray(arr) ? arr : [];
      const availableBudget =
        typeof calculatedTotals?.balanceAmount === "number"
          ? Number(calculatedTotals.balanceAmount)
          : undefined;
      const hasAnyDebts =
        safeArray(formValue?.creditCards).length +
          safeArray(formValue?.loans).length +
          safeArray(formValue?.overdrafts).length +
          safeArray(formValue?.otherDebts).length +
          safeArray(formValue?.arrears).length >
        0;

      // Collect existing creditor/company names and full debt list across ALL debt sections so that
      // each DebtCategory can:
      // - show a soft confirmation when the user selects a company that already exists anywhere
      // - enforce a hard duplicate rule on Save for same company + same reference number
      const collectDebtsMeta = (
        state: DebtsSchemaState
      ): { existingCreditorNames: string[]; allExistingDebts: DebtFormEntry[] } => {
        const names: string[] = [];
        const allDebts: DebtFormEntry[] = [];

        const pushFrom = (entries: unknown) => {
          if (!Array.isArray(entries)) return;
          (entries as DebtFormEntry[]).forEach((entry) => {
            allDebts.push(entry);
            const rawName =
              typeof entry?.creditorName === "string"
                ? entry.creditorName.trim()
                : "";
            if (rawName) {
              names.push(rawName);
            }
          });
        };

        pushFrom(state.creditCards);
        pushFrom(state.loans);
        pushFrom(state.overdrafts);
        pushFrom(state.otherDebts);
        pushFrom(state.arrears);

        return { existingCreditorNames: names, allExistingDebts: allDebts };
      };

      const { existingCreditorNames, allExistingDebts } = collectDebtsMeta(formValue);

      const updateCategory = (key: keyof DebtsSchemaState) => (next: any[]) => {
        const prev: DebtsSchemaState = formValue;
        const nextValue: DebtsSchemaState = {
          ...prev,
          [key]: next,
        } as DebtsSchemaState;
        // keep haveDebts in sync
        const count =
          safeArray(nextValue.creditCards).length +
          safeArray(nextValue.loans).length +
          safeArray(nextValue.overdrafts).length +
          safeArray(nextValue.otherDebts).length +
          safeArray(nextValue.arrears).length;
        nextValue.haveDebts = count > 0;
        setFormData(
          currentFormId,
          nextValue as unknown as Record<string, unknown>
        );
      };

      const handleAllDebtsAddedChange = (checked: boolean) => {
        setFormData(currentFormId, {
          ...formValue,
          allDebtsAdded: checked,
        } as unknown as Record<string, unknown>);
      };

      return (
        <form
          id={currentFormId}
          onSubmit={(e) => {
            e.preventDefault();
            void handleFormSubmit(
              allFormData[currentFormId] ||
                liveBudgetFormValuesRef.current ||
                {}
            );
          }}
        >
          <Stack spacing={2.5}>
            <DebtCategory
              label="Arrears"
              category={"arrears" as any}
              value={safeArray(formValue?.arrears)}
              onChange={updateCategory("arrears")}
              existingCreditorNames={existingCreditorNames}
              allExistingDebts={allExistingDebts}
              lenders={lenders}
              accountTypes={debtAccountTypes}
              loading={debtsLoading || accountTypesLoading || !lendersLoaded}
              hideAccountType={false}
              forceShowAccountType
              density="compact"
              energyCompanies={energyCompanies}
              {...(typeof availableBudget === "number"
                ? { availableBudget }
                : {})}
            />
            <DebtCategory
              label="Credit card"
              category={"creditCards" as any}
              value={safeArray(formValue?.creditCards)}
              onChange={updateCategory("creditCards")}
              existingCreditorNames={existingCreditorNames}
              allExistingDebts={allExistingDebts}
              lenders={lenders}
              accountTypes={debtAccountTypes}
              loading={debtsLoading || accountTypesLoading || !lendersLoaded}
              hideAccountType={false}
              density="compact"
              {...(typeof availableBudget === "number"
                ? { availableBudget }
                : {})}
            />
            <DebtCategory
              label="Loans"
              category={"loans" as any}
              value={safeArray(formValue?.loans)}
              onChange={updateCategory("loans")}
              existingCreditorNames={existingCreditorNames}
              allExistingDebts={allExistingDebts}
              lenders={lenders}
              accountTypes={debtAccountTypes}
              loading={debtsLoading || accountTypesLoading || !lendersLoaded}
              hideAccountType={false}
              forceShowAccountType
              density="compact"
              {...(typeof availableBudget === "number"
                ? { availableBudget }
                : {})}
            />
            <DebtCategory
              label="Overdraft"
              category={"overdrafts" as any}
              value={safeArray(formValue?.overdrafts)}
              onChange={updateCategory("overdrafts")}
              existingCreditorNames={existingCreditorNames}
              allExistingDebts={allExistingDebts}
              lenders={lenders}
              accountTypes={debtAccountTypes}
              loading={debtsLoading || accountTypesLoading || !lendersLoaded}
              hideAccountType={false}
              density="compact"
              {...(typeof availableBudget === "number"
                ? { availableBudget }
                : {})}
            />
            <DebtCategory
              label="Others"
              category={"otherDebts" as any}
              value={safeArray(formValue?.otherDebts)}
              onChange={updateCategory("otherDebts")}
              existingCreditorNames={existingCreditorNames}
              allExistingDebts={allExistingDebts}
              lenders={lenders}
              accountTypes={debtAccountTypes}
              loading={debtsLoading || accountTypesLoading || !lendersLoaded}
              hideAccountType={false}
              forceShowAccountType
              density="compact"
              {...(typeof availableBudget === "number"
                ? { availableBudget }
                : {})}
            />
            <Box sx={{ mt: 1 }}>
              <Paper 
              elevation={0}
              sx={{
                backgroundColor: 'white',
                borderRadius: 1.5,
                border: '1px solid #155F54',
                overflow: 'hidden',
                p: 1.5,
              }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean((formValue as any)?.allDebtsAdded)}
                    onChange={(e) =>
                      handleAllDebtsAddedChange(e.target.checked)
                    }
                  />
                }
                label={
                  hasAnyDebts
                    ? "I have added all the companies I owe money to"
                    : "I do not have any companies I owe money to"
                }
              />
              </Paper>
            </Box>
          </Stack>
        </form>
      );
    }

    // For savings page, also wait for calculatedTotals to load so help text placeholders are populated
    // Also wait for NI detection to complete so labels are correct from the start
    const isDataLoading =
      isBudgetDataLoading ||
      niLoading ||
      (selectedOuter === "flexible-expenses" &&
        selectedInner === "savings" &&
        !calculatedTotals);

    return (
      <BudgetFormRenderer
        isBudgetDataLoading={isDataLoading}
        currentSchema={currentSchema}
        currentFormId={currentFormId}
        initialFormData={initialFormData}
        renderNonce={budgetFormRehydrateNonce}
        selectedInner={selectedInner}
        handleFormSubmit={handleFormSubmit}
        handleBudgetFormValuesChange={handleBudgetFormValuesChange}
        handleWarningDialog={handleWarningDialog}
        isNorthernIreland={isNorthernIreland}
      />
    );
  }, [
    selectedOuter,
    selectedInner,
    allFormData,
    currentFormId,
    initialFormData,
    calculatedTotals,
    lenders,
    debtAccountTypes,
    setFormData,
    isBudgetDataLoading,
    niLoading,
    currentSchema,
    handleFormSubmit,
    handleBudgetFormValuesChange,
    handleWarningDialog,
    isNorthernIreland,
  ]);

  return (
    <CustomerMetadataContext.Provider value={customerMetadata}>
      <WarningsRefreshContext.Provider value={warningsRefreshKey}>
        <TriggersProvider value={triggersContext}>
          <>
        {/* Confirmation dialog for destructive budget clears caused by profile changes */}
        <Dialog
          open={confirmOpen}
          onClose={() => {
            setConfirmOpen(false);
            setConfirmPendingAction(null);
            setConfirmMessage(
              CONFIRM_REMOVE_SECTION_MESSAGE
            );
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700, color: "#003d34" }}>
            Confirm change
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1">{confirmMessage}</Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => {
                setConfirmOpen(false);
                setConfirmPendingAction(null);
                setConfirmMessage(
                  CONFIRM_REMOVE_SECTION_MESSAGE
                );
              }}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              No
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                const fn = confirmPendingAction;
                setConfirmOpen(false);
                setConfirmPendingAction(null);
                setConfirmMessage(
                  CONFIRM_REMOVE_SECTION_MESSAGE
                );
                if (typeof fn === "function") {
                  await fn();
                }
              }}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Yes
            </Button>
          </DialogActions>
        </Dialog>

        {profileDialogState ? (
          <ProfileFormDialog
            open
            title={profileDialogState.title}
            schema={profileDialogState.schema}
            initialData={profileDialogState.initialData}
            formId={`profile-warning-${profileDialogState.key}`}
            onSubmit={handleProfileDialogSubmit}
            onClose={() => setProfileDialogState(null)}
            isSaving={isProfileDialogSaving}
            onWarningAction={handleWarningDialog}
          />
        ) : null}

        {/* Mobile View */}
        <MobileLayout
          outerRoutes={outerRoutes}
          selectedOuter={selectedOuter}
          currentInnerRoutes={currentInnerRoutes}
          selectedInner={selectedInner}
          innerNavMenuAnchor={innerNavMenuAnchor}
          sectionWarnings={sectionWarnings}
          nextButtonLabel={nextButtonLabel}
          currentFormId={currentFormId}
          nextButtonLoading={Boolean(loading?.["budget-save"])}
          nextButtonDisabled={nextButtonDisabled}
          isNavigating={isNavigating}
          onOuterRouteChange={handleOuterRouteChange}
          onInnerNavMenuOpen={handleInnerNavMenuOpen}
          onInnerNavMenuClose={handleInnerNavMenuClose}
          onInnerNavMenuItemClick={handleInnerNavMenuItemClick}
          onNavigateToInner={navigateToInner}
          onSectionTokenClick={handleSectionTokenClick}
          onBackNav={handleBackNav}
          renderBudgetForm={renderBudgetForm}
          isNorthernIreland={isNorthernIreland}
        />

        {/* Desktop View */}
        <DesktopLayout
          outerRoutes={outerRoutes}
          selectedOuter={selectedOuter}
          currentInnerRoutes={currentInnerRoutes}
          selectedInner={selectedInner}
          sectionWarnings={sectionWarnings}
          nextButtonLabel={nextButtonLabel}
          currentFormId={currentFormId}
          nextButtonLoading={Boolean(loading?.["budget-save"])}
          nextButtonDisabled={nextButtonDisabled}
          isNavigating={isNavigating}
          onOuterRouteChange={handleOuterRouteChange}
          onNavigateToInner={navigateToInner}
          onSectionTokenClick={handleSectionTokenClick}
          onBackNav={handleBackNav}
          renderBudgetForm={renderBudgetForm}
          isNorthernIreland={isNorthernIreland}
        />

        {/* Onboarding Dialog */}
        <OnboardingDialog
          isOpen={isOnboardingDialogOpen}
          currentStep={currentOnboardingStep}
          totalSteps={totalOnboardingSteps}
          currentSchema={currentOnboardingSchema}
          screenRenderKey={screenRenderKey}
          formData={onboardingDialogFormData}
          isLoading={
            Boolean(loading?.["income-onboarding-submit"]) ||
            Boolean(loading?.["income-onboarding-prefill"])
          }
          computedPrimaryText={computedPrimaryText}
          shouldShowSecondary={shouldShowSecondary}
          secondaryButtonText={secondaryButtonText}
          onClose={handleOnboardingClose}
          onHelpOpen={handleHelpOpen}
          onSubmit={handleOnboardingFormSubmit}
          onValuesChange={handleOnboardingValuesChange}
          onBack={handleOnboardingBack}
          onSkip={handleOnboardingSkip}
          onWarningAction={handleWarningDialog}
        />

        {/* Help Dialog - Opens on top of onboarding dialog */}
        <HelpDialog
          open={isHelpDialogOpen}
          onClose={handleHelpClose}
          zIndex={1302}
        />
        {/* Quick Edit Dialog for Warning tokens */}
        <QuickEditDialog
          open={isVarDialogOpen}
          variable={varSpec as any}
          onClose={() => {
            setIsVarDialogOpen(false);
            setVarSpec(null);
          }}
          onSave={handleVarSave}
        />
          </>
        </TriggersProvider>
      </WarningsRefreshContext.Provider>
    </CustomerMetadataContext.Provider>
  );
};

const UpdateBudgetPage: React.FC = () => {
 

  return <UpdateBudgetPageInner />;
};

export default UpdateBudgetPage;
